import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Task, TaskTimePreferences, SavedSession } from '../types';
import CompactMetronome, { CompactMetronomeRef } from './CompactMetronome';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc, 
  addDoc, 
  updateDoc,
  setDoc,
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { loadAllUserBadges } from '../utils/badgeCalculator';
import DailyPracticeGoalCard from './DailyPracticeGoalCard';
import NotificationSystem from './NotificationSystem';
import { User } from '../types';

interface FeedEvent {
  id: string;
  type: 'heart_sent' | 'nudge_sent' | 'daily_goal_reached' | 'weekly_goal_reached' | 'badge_earned';
  fromUserId: string;
  fromUserNickname: string;
  toUserId?: string; // For heart/nudge events
  toUserNickname?: string; // For heart/nudge events
  badgeName?: string; // For badge events
  timestamp: any;
  hearts: string[]; // Users who sent hearts to this event
  nudges: string[]; // Users who sent nudges to this event
}

interface Lesson {
  id: string;
  lessonId: string;
  studentId: string;
  createdAt: any;
  tasks: Task[];
}

interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
}

interface TaskWithDuration extends Task {
  plannedMinutes: number;
  actualMinutes: number;
}

const PracticeTasks: React.FC = () => {
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [latestLesson, setLatestLesson] = useState<Lesson | null>(null);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [customTasks, setCustomTasks] = useState<Task[]>([]);
  const [taskCounts, setTaskCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Teacher access state
  const [isTeacherMode, setIsTeacherMode] = useState(false);
  const [viewingStudentId, setViewingStudentId] = useState<string | null>(null);
  const [viewingStudent, setViewingStudent] = useState<{ name: string; nickname: string; instrument: string; birthdate?: string; practiceSessionDuration?: number; practiceSessionsPerWeek?: number } | null>(null);
  
  // Carousel state for Free Practice
  const [carouselItems, setCarouselItems] = useState<Array<{ content: string; id: string }>>([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  
  // Theme state for Free Practice
  const [selectedTheme, setSelectedTheme] = useState(() => {
    // Load theme from localStorage or default to 'classic'
    return localStorage.getItem('freePracticeTheme') || 'classic';
  });

  // Practice category timers
  const [categoryTimers, setCategoryTimers] = useState({
    scales: { isActive: false, elapsed: 0 },
    exercises: { isActive: false, elapsed: 0 },
    studies: { isActive: false, elapsed: 0 },
    repertoire: { isActive: false, elapsed: 0 },
    ensemble: { isActive: false, elapsed: 0 }
  });

  // Celebration state
  const [showGoalCelebration, setShowGoalCelebration] = useState(false);
  const [goalReached, setGoalReached] = useState(false);
  
  // Next practice session state
  const [showNextSessionModal, setShowNextSessionModal] = useState(false);
  const [nextSessionDate, setNextSessionDate] = useState('');
  const [nextSessionTime, setNextSessionTime] = useState('');
  const [nextSessionGoal, setNextSessionGoal] = useState(30);
  const [isEditingSession, setIsEditingSession] = useState(false);
  const [lastPracticeTime, setLastPracticeTime] = useState('');
  const [showNextSessionPrompt, setShowNextSessionPrompt] = useState(false);
  const [todaySessionDetails, setTodaySessionDetails] = useState<{
    date: string;
    time: string;
    goal: number;
  } | null>(null);
  const [isEditingTodayTime, setIsEditingTodayTime] = useState(false);
  const [editingTodayTime, setEditingTodayTime] = useState('');
  const [isEditingTodayGoal, setIsEditingTodayGoal] = useState(false);
  const [editingTodayGoal, setEditingTodayGoal] = useState(30);
  const [isEditingTodayDate, setIsEditingTodayDate] = useState(false);
  const [editingTodayDate, setEditingTodayDate] = useState('');
  
  // Theme configurations
  const themes = {
    classic: {
      name: 'Classic',
      background: 'from-indigo-600 via-purple-600 to-pink-600',
      progressColors: ['#10B981', '#3B82F6', '#8B5CF6'],
      cardBg: 'bg-white bg-opacity-20'
    },
    sunset: {
      name: 'Sunset',
      background: 'from-orange-400 via-red-500 to-purple-600',
      progressColors: ['#F97316', '#EF4444', '#9333EA'],
      cardBg: 'bg-white bg-opacity-20'
    },
    ocean: {
      name: 'Ocean',
      background: 'from-blue-400 via-cyan-500 to-teal-600',
      progressColors: ['#3B82F6', '#06B6D4', '#14B8A6'],
      cardBg: 'bg-white bg-opacity-20'
    },
    forest: {
      name: 'Forest',
      background: 'from-green-400 via-emerald-500 to-green-700',
      progressColors: ['#22C55E', '#10B981', '#047857'],
      cardBg: 'bg-white bg-opacity-20'
    }
  };
  
  // Handle theme change
  const handleThemeChange = (themeKey: string) => {
    setSelectedTheme(themeKey);
    localStorage.setItem('freePracticeTheme', themeKey);
  };

  // Handle next practice session
  const handleSetNextSession = () => {
    // Load existing session data if available, otherwise use defaults
    if (nextSessionDate && nextSessionTime) {
      // Editing existing session - use current values
      setNextSessionGoal(nextSessionGoal);
      setIsEditingSession(true);
      console.log('Editing existing session with date:', nextSessionDate);
    } else {
      // Creating new session - use defaults
      setIsEditingSession(false);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      // Use local date formatting instead of UTC
      const todayString = today.getFullYear() + '-' + 
        String(today.getMonth() + 1).padStart(2, '0') + '-' + 
        String(today.getDate()).padStart(2, '0');
      const tomorrowDateString = tomorrow.getFullYear() + '-' + 
        String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + 
        String(tomorrow.getDate()).padStart(2, '0');
      
      console.log('Setting new session with tomorrow date:', tomorrowDateString);
      console.log('Today is:', todayString);
      
      setNextSessionDate(tomorrowDateString);
      setNextSessionTime('09:00');
      setNextSessionGoal(currentUser?.practiceSessionDuration || 30);
    }
    setShowNextSessionModal(true);
  };

  const handleSaveNextSession = async () => {
    if (!currentUser || !nextSessionDate || !nextSessionTime) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // If the session is for today, set it as today's session instead of next session
      if (nextSessionDate === today) {
        setTodaySessionDetails({
          date: nextSessionDate,
          time: nextSessionTime,
          goal: nextSessionGoal
        });
        
        // Save today's session to database and clear next session data
        await updateDoc(doc(db, 'users', currentUser.uid), {
          todayPracticeSession: {
            date: nextSessionDate,
            time: nextSessionTime,
            goal: nextSessionGoal,
            scheduledAt: serverTimestamp()
          },
          nextPracticeSession: null
        });
        
        // Reset next session state
        setNextSessionDate('');
        setNextSessionTime('');
        setNextSessionGoal(30);
        
        console.log('Set session as today\'s session');
      } else {
        // Session is for a future date, save as next session
        await updateDoc(doc(db, 'users', currentUser.uid), {
          nextPracticeSession: {
            date: nextSessionDate,
            time: nextSessionTime,
            goal: nextSessionGoal,
            scheduledAt: serverTimestamp()
          }
        });
        
        console.log('Set session as next session');
      }
      
      setShowNextSessionModal(false);
      
      // Remember the practice time for next time
      setLastPracticeTime(nextSessionTime);
      
      // Only navigate to dashboard if not editing (i.e., creating new session)
      if (!isEditingSession) {
        navigate('/dashboard');
      }
      
      // Reset editing flag
      setIsEditingSession(false);
      
    } catch (error) {
      console.error('Error saving next practice session:', error);
    }
  };

  // Load today's session details
  const loadTodaySessionDetails = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const today = new Date().toISOString().split('T')[0];
        
        // First check if there's already a today's session saved
        const todaySession = userData.todayPracticeSession;
        if (todaySession && todaySession.date === today) {
          setTodaySessionDetails({
            date: todaySession.date,
            time: todaySession.time,
            goal: todaySession.goal
          });
          console.log('Loaded existing today\'s session');
        }
        
        // Always check for next session data (for future dates)
        const nextSession = userData.nextPracticeSession;
        if (nextSession) {
          // If the next session is for today and we don't already have a today's session, transfer it
          if (nextSession.date === today && !todaySession) {
            // Set today's session details from the next session
            setTodaySessionDetails({
              date: nextSession.date,
              time: nextSession.time,
              goal: nextSession.goal
            });
            
            // Save today's session to database and clear next session
            await updateDoc(doc(db, 'users', currentUser.uid), {
              todayPracticeSession: {
                date: nextSession.date,
                time: nextSession.time,
                goal: nextSession.goal,
                scheduledAt: serverTimestamp()
              },
              nextPracticeSession: null
            });
            
            // Reset next session state
            setNextSessionDate('');
            setNextSessionTime('');
            setNextSessionGoal(30);
            
            console.log('Transferred next session to today\'s session');
          } else if (nextSession.date !== today) {
            // Next session is for a future date, keep it as next session
            setNextSessionDate(nextSession.date);
            setNextSessionTime(nextSession.time);
            setNextSessionGoal(nextSession.goal);
            console.log('Loaded next session for future date');
          }
        }
      }
    } catch (error) {
      console.error('Error loading today session details:', error);
    }
  };

  // Handle Back to Dashboard navigation
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Load today's session details on component mount and check for daily transitions
  useEffect(() => {
    const performDailyCheck = async () => {
      const today = new Date().toISOString().split('T')[0];
      const lastCheckKey = `lastDailyCheck_${currentUser?.uid}`;
      const lastCheck = localStorage.getItem(lastCheckKey);
      
      // Only perform daily check if we haven't checked today yet
      if (lastCheck !== today) {
        console.log('Performing daily session check for:', today);
        
        // If there was a previous day's session, clear it
        if (lastCheck && lastCheck !== today && currentUser) {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            todayPracticeSession: null
          });
          console.log('Cleared previous day\'s session');
        }
        
        await loadTodaySessionDetails();
        localStorage.setItem(lastCheckKey, today);
      } else {
        // Still load session details but don't perform full daily check
        await loadTodaySessionDetails();
      }
    };
    
    if (currentUser) {
      performDailyCheck();
    }
  }, [currentUser]);

  // Ensure date input shows tomorrow when modal opens for new sessions
  useEffect(() => {
    if (showNextSessionModal) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      // Use local date formatting instead of UTC
      const todayString = today.getFullYear() + '-' + 
        String(today.getMonth() + 1).padStart(2, '0') + '-' + 
        String(today.getDate()).padStart(2, '0');
      const tomorrowDateString = tomorrow.getFullYear() + '-' + 
        String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + 
        String(tomorrow.getDate()).padStart(2, '0');
      
      console.log('Modal opened - ensuring tomorrow date is set:', tomorrowDateString);
      console.log('Today is:', todayString);
      console.log('Current nextSessionDate state:', nextSessionDate);
      
      // Force set the date even if it's already set
      setNextSessionDate(tomorrowDateString);
      
      // Use setTimeout to ensure the DOM is updated
      setTimeout(() => {
        const dateInput = document.querySelector('input[type="date"]') as HTMLInputElement;
        if (dateInput) {
          console.log('Forcing date input value to:', tomorrowDateString);
          dateInput.value = tomorrowDateString;
          dateInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 100);
    }
  }, [showNextSessionModal]);


  // Handle editing today's session time
  const handleEditTodayTime = () => {
    if (todaySessionDetails?.time) {
      setEditingTodayTime(todaySessionDetails.time);
    } else {
      // Set to current time + 1 hour as default
      const now = new Date();
      now.setHours(now.getHours() + 1);
      setEditingTodayTime(now.toTimeString().slice(0, 5));
    }
    setIsEditingTodayTime(true);
  };

  // Handle editing today's session goal
  const handleEditTodayGoal = () => {
    const currentGoal = todaySessionDetails?.goal || plannedTotalMinutes || currentUser?.practiceSessionDuration || 30;
    setEditingTodayGoal(currentGoal);
    setIsEditingTodayGoal(true);
  };

  // Handle editing today's session date
  const handleEditTodayDate = () => {
    if (todaySessionDetails?.date) {
      setEditingTodayDate(todaySessionDetails.date);
    } else {
      // Set to tomorrow's date as default
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setEditingTodayDate(tomorrow.toISOString().split('T')[0]);
    }
    setIsEditingTodayDate(true);
  };

  // Save today's session time
  const handleSaveTodayTime = async () => {
    if (!currentUser || !editingTodayTime) {
      console.log('Missing currentUser or editingTodayTime:', { currentUser: !!currentUser, editingTodayTime });
      return;
    }

    console.log('Saving today session time:', editingTodayTime);

    try {
      // First get the current user data to preserve existing nextPracticeSession
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      
      const today = new Date().toISOString().split('T')[0];
      
      // Update the nextPracticeSession with the new time, preserving other fields
      const updatedNextSession = {
        ...userData?.nextPracticeSession,
        date: editingTodayDate || today, // Use edited date or fallback to today
        time: editingTodayTime,
        goal: editingTodayGoal || userData?.nextPracticeSession?.goal || currentUser?.practiceSessionDuration || 30
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        nextPracticeSession: updatedNextSession
      });

      console.log('Successfully updated Firestore');

      // Update local state
      setTodaySessionDetails({
        date: editingTodayDate || today,
        time: editingTodayTime,
        goal: updatedNextSession.goal
      });

      setIsEditingTodayTime(false);
      console.log('Successfully updated local state');
    } catch (error) {
      console.error('Error updating today session time:', error);
    }
  };

  // Cancel editing today's session time
  const handleCancelEditTodayTime = () => {
    setIsEditingTodayTime(false);
    setEditingTodayTime('');
  };

  // Save today's session goal
  const handleSaveTodayGoal = async () => {
    if (!currentUser || editingTodayGoal <= 0) {
      console.log('Missing currentUser or invalid goal:', { currentUser: !!currentUser, editingTodayGoal });
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Update the today's session with the new goal
      await updateDoc(doc(db, 'users', currentUser.uid), {
        'todayPracticeSession.goal': editingTodayGoal
      });

      // Update local state
      setTodaySessionDetails(prev => prev ? {
        ...prev,
        goal: editingTodayGoal
      } : {
        date: editingTodayDate || today,
        time: editingTodayTime || '',
        goal: editingTodayGoal
      });

      // Also update plannedTotalMinutes to reflect the new goal
      setPlannedTotalMinutes(editingTodayGoal);

      setIsEditingTodayGoal(false);
      console.log('Successfully updated today\'s session goal');
    } catch (error) {
      console.error('Error updating today session goal:', error);
    }
  };

  const handleCancelEditTodayGoal = () => {
    setIsEditingTodayGoal(false);
    setEditingTodayGoal(30);
  };

  // Save today's session date
  const handleSaveTodayDate = async () => {
    if (!currentUser || !editingTodayDate) {
      console.log('Missing currentUser or editingTodayDate:', { currentUser: !!currentUser, editingTodayDate });
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Update the next session with the new date
      const updatedNextSession = {
        date: editingTodayDate,
        time: editingTodayTime || todaySessionDetails?.time || '',
        goal: editingTodayGoal || todaySessionDetails?.goal || currentUser.practiceSessionDuration || 30
      };

      await updateDoc(userRef, {
        nextPracticeSession: updatedNextSession
      });

      console.log('Successfully updated Firestore');

      // Update local state
      setTodaySessionDetails({
        date: editingTodayDate,
        time: editingTodayTime || todaySessionDetails?.time || '',
        goal: editingTodayGoal || todaySessionDetails?.goal || currentUser.practiceSessionDuration || 30
      });

      setIsEditingTodayDate(false);
    } catch (error) {
      console.error('Error updating session date:', error);
    }
  };

  const handleCancelEditTodayDate = () => {
    setIsEditingTodayDate(false);
    setEditingTodayDate('');
  };

  // Handle category timer toggle
  const toggleCategoryTimer = (category: keyof typeof categoryTimers) => {
    setCategoryTimers(prev => {
      const newTimers = { ...prev };
      
      // If this category is currently active, pause it and also pause the goal timer
      if (prev[category].isActive) {
        newTimers[category] = {
          ...prev[category],
          isActive: false
        };
        // Pause the goal timer when clicking the same active category
        setFreePracticeRunning(false);
      } else {
        // If this category is not active, pause all others and start this one
        Object.keys(newTimers).forEach(key => {
          newTimers[key as keyof typeof newTimers] = {
            ...newTimers[key as keyof typeof newTimers],
            isActive: false
          };
        });
        
        newTimers[category] = {
          ...prev[category],
          isActive: true
        };
        
        // Start the main timer when a category is selected (only set start time if not already set)
        if (!freePracticeStartTime) {
          setFreePracticeStartTime(new Date());
        }
        // Start the goal timer when any category is clicked
        setFreePracticeRunning(true);
      }
      
      return newTimers;
    });
  };


  // Format time for category timers
  const formatCategoryTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Play celebration sound and show popup
  const playCelebration = () => {
    try {
      const audio = new Audio('/cheer1.mp3');
      audio.play().catch(err => console.log('Could not play celebration sound:', err));
    } catch (err) {
      console.log('Could not play celebration sound:', err);
    }
    setShowGoalCelebration(true);
    setGoalReached(true);
  };

  // Check if daily practice goal is reached
  const checkDailyGoal = () => {
    if (!currentUser) return;
    
    // Use Today's Session Goal as the daily goal target
    const todaySessionGoal = todaySessionDetails?.goal || plannedTotalMinutes || currentUser.practiceSessionDuration || 30;
    const totalPracticeTimeMinutes = freePracticeElapsed / 60; // Use fractional minutes for accurate comparison
    
    if (totalPracticeTimeMinutes >= todaySessionGoal && !goalReached) {
      playCelebration();
    }
  };

  // Check for 10-minute streak milestone
  const checkStreakMilestone = () => {
    if (!currentUser || isTeacherMode) return;
    
    const currentPracticeMinutes = freePracticeElapsed / 60;
    
    // Check if we just hit 10 minutes (with a small buffer to avoid multiple triggers)
    if (currentPracticeMinutes >= 10 && currentPracticeMinutes < 10.5) {
      // Calculate current streak from completed sessions
      const today = new Date();
      const dayOfWeek = today.getDay();
      
      // Get the start of the current week (based on user's weekStartDay preference)
      const weekStartDay = currentUser.weekStartDay || 'Sunday';
      let daysToSubtract;
      if (weekStartDay === 'Sunday') {
        daysToSubtract = dayOfWeek;
      } else if (weekStartDay === 'Monday') {
        daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      } else {
        daysToSubtract = dayOfWeek;
      }
      
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - daysToSubtract);
      weekStart.setHours(0, 0, 0, 0);
      
      // Count consecutive days with 10+ minute sessions
      let streakDays = 0;
      let currentDate = new Date(weekStart);
      
      for (let i = 0; i < 7; i++) {
        const checkDate = new Date(currentDate);
        const dateString = checkDate.toISOString().split('T')[0];
        
        // Check if there's a session of 10+ minutes on this date
        const daySessions = allWeeklySessions.filter(session => {
          const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
          const sessionDateString = sessionDate.toISOString().split('T')[0];
          return sessionDateString === dateString && (session.durationSec || 0) >= 600; // 10 minutes = 600 seconds
        });
        
        if (daySessions.length > 0) {
          streakDays++;
        } else {
          break; // Streak broken
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Determine if this is first day or continuing streak
      const isFirstDay = streakDays === 1;
      
      let message = '';
      if (isFirstDay) {
        message = "🎉 Congratulations! You've created your first practice streak!\n\nKeep practicing tomorrow to build momentum and earn bonus streak multipliers!";
      } else {
        message = `🔥 Amazing! You've kept your streak alive for ${streakDays} day${streakDays !== 1 ? 's' : ''}!\n\nContinue your streak tomorrow for bonus streak point multipliers!`;
      }
      
      // Show celebration
      setStreakCelebrationData({
        isFirstDay,
        currentStreak: streakDays,
        message
      });
      setShowStreakCelebration(true);
      
      // Play celebration sound
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
        audio.play().catch(() => {}); // Ignore errors if audio fails
      } catch (error) {
        // Ignore audio errors
      }
    }
  };

  
  // Handle teacher access via query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const studentId = urlParams.get('studentId');
    
    if (studentId && currentUser?.role === 'teacher') {
      setIsTeacherMode(true);
      setViewingStudentId(studentId);
      // Reset weekly progress state when switching students to prevent stale data
      setWeeklyCompletedSessions([]);
      setAllWeeklySessions([]);
      setWeeklyProgressLoaded(false);
      // Fetch student info
      const fetchStudentInfo = async () => {
        try {
          const studentDoc = await getDoc(doc(db, 'users', studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            setViewingStudent({
              name: studentData.name || 'Unknown',
              nickname: studentData.nickname || 'Unknown',
              instrument: studentData.instrument || 'Unknown',
              birthdate: studentData.birthdate,
              practiceSessionDuration: studentData.practiceSessionDuration,
              practiceSessionsPerWeek: studentData.practiceSessionsPerWeek
            });
          }
        } catch (error) {
          console.error('Error fetching student info:', error);
        }
      };
      fetchStudentInfo();
    } else if (currentUser?.role === 'student') {
      setIsTeacherMode(false);
      setViewingStudentId(null);
      setViewingStudent(null);
      // Reset weekly progress state when switching to student mode to prevent stale data
      setWeeklyCompletedSessions([]);
      setAllWeeklySessions([]);
      setWeeklyProgressLoaded(false);
    }
  }, [location.search, currentUser?.role]);
  
  // Planning flow state
  const [plannedTotalMinutes, setPlannedTotalMinutes] = useState(0);
  const [taskDurations, setTaskDurations] = useState<Record<string, number>>({});
  const [planningComplete, setPlanningComplete] = useState(false);
  const [taskTimePreferences, setTaskTimePreferences] = useState<TaskTimePreferences>({});
  const [hasLoadedPreferences, setHasLoadedPreferences] = useState(false);

  
  // Saved sessions
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [showSaveSessionModal, setShowSaveSessionModal] = useState(false);
  const [showLoadSessionModal, setShowLoadSessionModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDescription, setNewSessionDescription] = useState('');
  
  // Teacher session planning
  const [showTeacherSessionModal, setShowTeacherSessionModal] = useState(false);
  const [teacherSessionName, setTeacherSessionName] = useState('');
  const [teacherSessionDescription, setTeacherSessionDescription] = useState('');
  
  // Edit session states
  const [editingSession, setEditingSession] = useState<SavedSession | null>(null);
  const [showEditSessionModal, setShowEditSessionModal] = useState(false);
  
  // Practice tips popup states
  const [showTipsPopup, setShowTipsPopup] = useState(false);
  const [currentTaskForTips, setCurrentTaskForTips] = useState<Task | null>(null);
  const [newTipText, setNewTipText] = useState('');
  const [showAddTipsInterface, setShowAddTipsInterface] = useState(false);
  
  // Extend practice states
  const [isExtendPracticeMode, setIsExtendPracticeMode] = useState(false);
  const [extendPracticeTaskTimers, setExtendPracticeTaskTimers] = useState<Record<string, number>>({});
  const [extendPracticeOverallTimer, setExtendPracticeOverallTimer] = useState<number>(0);
  
  
  
  // Tip completion tracking
  const [completedTips, setCompletedTips] = useState<Set<string>>(new Set());
  const [showTipsCompletionDialog, setShowTipsCompletionDialog] = useState(false);
  const [isFinishPracticeDialog, setIsFinishPracticeDialog] = useState(false);
  
  // Tip response popup
  const [showTipResponsePopup, setShowTipResponsePopup] = useState(false);
  const [currentTipText, setCurrentTipText] = useState('');
  const [currentTipKey, setCurrentTipKey] = useState('');
  
  // Tip selection tracking
  const [selectedTips, setSelectedTips] = useState<Set<string>>(new Set());
  const [hiddenTips, setHiddenTips] = useState<Set<string>>(new Set());
  const [showRepracticeDialog, setShowRepracticeDialog] = useState(false);
  const [hasTipSelectionChanged, setHasTipSelectionChanged] = useState(false);
  const [isRepracticeMode, setIsRepracticeMode] = useState(false);

  
  // Description card state
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  
  // Practice mode state
  const [practiceMode, setPracticeMode] = useState(false);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [currentTaskStartTime, setCurrentTaskStartTime] = useState<Date | null>(null);
  const [currentTaskElapsed, setCurrentTaskElapsed] = useState(0);
  const [sessionSummary, setSessionSummary] = useState<PracticeSession | null>(null);
  const [returnToTaskIndex, setReturnToTaskIndex] = useState<number | null>(null);
  const [returnToTaskElapsed, setReturnToTaskElapsed] = useState<number | null>(null);
  const [returnToOverallStartTime, setReturnToOverallStartTime] = useState<Date | null>(null);
  const [returnToOverallRemainingTime, setReturnToOverallRemainingTime] = useState<number | null>(null);
  const [showDailyGoalMessage, setShowDailyGoalMessage] = useState(false);
  const [showFinishPracticeConfirmation, setShowFinishPracticeConfirmation] = useState(false);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [streakCelebrationData, setStreakCelebrationData] = useState<{
    isFirstDay: boolean;
    currentStreak: number;
    message: string;
  } | null>(null);
  
  // Individual task timer state
  const [taskTimers, setTaskTimers] = useState<Record<string, {
    isRunning: boolean;
    elapsed: number;
    startTime: Date | null;
    completed: boolean;
  }>>({});
  const [overallRemainingTime, setOverallRemainingTime] = useState(0);
  const [overallStartTime, setOverallStartTime] = useState<Date | null>(null);
  const [overallIsRunning, setOverallIsRunning] = useState(false);
  const [forceRender, setForceRender] = useState(0);
  const [displayTimer, setDisplayTimer] = useState(0);
  // Main timer - using the simple timer logic that works
  const [simpleTimer, setSimpleTimer] = useState(0);
  const [simpleTimerRunning, setSimpleTimerRunning] = useState(false);
  const [simpleTaskTimes, setSimpleTaskTimes] = useState<Record<string, number>>({});
  const simpleTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Session state
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionPaused, setSessionPaused] = useState(false);
  const [currentSession, setCurrentSession] = useState<PracticeSession | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Custom task form state
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState<'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble'>('Scales');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [quickEditCategory, setQuickEditCategory] = useState<'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble'>('Scales');
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editTaskTitle, setEditTaskTitle] = useState('');
  const [editTaskCategory, setEditTaskCategory] = useState<'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble'>('Scales');
  
  
  // Section ordering state
  const [sectionOrder, setSectionOrder] = useState<('Technique' | 'Repertoire' | 'Ensemble')[]>(['Technique', 'Repertoire', 'Ensemble']);
  
  // Technique subsection ordering state
  const [techniqueSubsectionOrder, setTechniqueSubsectionOrder] = useState<('Scales' | 'Exercises' | 'Studies')[]>(['Scales', 'Exercises', 'Studies']);
  
  // Drag and drop state
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null);
  const [draggedTaskType, setDraggedTaskType] = useState<'lesson' | 'custom' | null>(null);
  const [draggedSection, setDraggedSection] = useState<string | null>(null);
  const [draggedSubsection, setDraggedSubsection] = useState<string | null>(null);
  
  // Task completion celebration state
  const [showTaskComplete, setShowTaskComplete] = useState(false);
  const [completedTaskTitle, setCompletedTaskTitle] = useState('');
  const [celebratedTasks, setCelebratedTasks] = useState<Set<string>>(new Set());
  const [currentCelebratedTaskId, setCurrentCelebratedTaskId] = useState<string | null>(null);
  const [completedReminders, setCompletedReminders] = useState<Set<string>>(new Set());
  const [practiceReminders, setPracticeReminders] = useState<(string | { content: string; type?: string; level?: number })[]>([]);
  const [showFreePractice, setShowFreePractice] = useState(false);
  const [freePracticeStartTime, setFreePracticeStartTime] = useState<Date | null>(null);
  const [freePracticeElapsed, setFreePracticeElapsed] = useState(0);
  const [freePracticeRunning, setFreePracticeRunning] = useState(false);
  const [freePracticeGoal, setFreePracticeGoal] = useState<number | null>(null);
  
  // Update category timers and goal timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCategoryTimers(prev => {
        const updated = { ...prev };
        let hasActive = false;
        Object.keys(updated).forEach(key => {
          if (updated[key as keyof typeof updated].isActive) {
            hasActive = true;
            updated[key as keyof typeof updated] = {
              ...updated[key as keyof typeof updated],
              elapsed: updated[key as keyof typeof updated].elapsed + 1
            };
          }
        });
        
        // Update goal timer with total of all category timers only when running
        if (freePracticeRunning) {
          const totalCategoryTime = Object.values(updated).reduce((sum, timer) => sum + timer.elapsed, 0);
          setFreePracticeElapsed(totalCategoryTime);
          
          // Check for streak milestone (10 minutes)
          checkStreakMilestone();
        }
        
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [freePracticeRunning]);
  
  // Check daily goal when practice time updates
  useEffect(() => {
    if (freePracticeRunning && freePracticeElapsed > 0) {
      checkDailyGoal();
    }
  }, [freePracticeElapsed, freePracticeRunning, currentUser, freePracticeGoal]);
  
  // Auto-rotate carousel every 5 seconds when Free Practice is open
  useEffect(() => {
    if (showFreePractice && carouselItems.length > 1) {
      const interval = setInterval(() => {
        setCurrentCarouselIndex((prevIndex) => 
          (prevIndex + 1) % carouselItems.length
        );
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [showFreePractice, carouselItems.length]);
  
  // Practice reminders state
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [selectedTaskForReminder, setSelectedTaskForReminder] = useState<string | null>(null);
  const [newReminderText, setNewReminderText] = useState('');
  const [editingReminder, setEditingReminder] = useState<string | null>(null);
  const [editReminderText, setEditReminderText] = useState('');
  
  // Metronome options for practice reminders
  const [newReminderHasMetronome, setNewReminderHasMetronome] = useState(false);
  const [newReminderTempo, setNewReminderTempo] = useState(120);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Profile setup modal state
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(30);
  const [sessionsPerWeek, setSessionsPerWeek] = useState(5);
  const [savingSetup, setSavingSetup] = useState(false);

  // Weekly progress tracking state
  const [weeklyCompletedSessions, setWeeklyCompletedSessions] = useState<PracticeSession[]>([]);
  const [allWeeklySessions, setAllWeeklySessions] = useState<PracticeSession[]>([]);
  const [weeklyProgressLoaded, setWeeklyProgressLoaded] = useState(false);
  
  // Badge system state
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);
  const [badgesLoaded, setBadgesLoaded] = useState(false);
  
  // Metronome state for guided practice
  const [expandedMetronomes, setExpandedMetronomes] = useState<Set<string>>(new Set());
  
  // Metronome state preservation
  const [metronomeState, setMetronomeState] = useState<{isRunning: boolean; tempo: number}>({isRunning: false, tempo: 120});
  
  // Auto-start metronome state
  const [currentTempoGoal, setCurrentTempoGoal] = useState<{tempo: number; taskId: string; tipIndex: number} | null>(null);
  const metronomeRef = useRef<CompactMetronomeRef | null>(null);
  const metronomeAutoStarting = useRef(false);
  const metronomeRefSet = useRef(false);
  
  // Handle metronome expansion toggle
  const toggleMetronomeExpansion = (taskId: string) => {
    setExpandedMetronomes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };
  
  // Stop all metronomes (called when practice actions are taken)
  const stopAllMetronomes = () => {
    setExpandedMetronomes(new Set());
  };

  // Restore metronome state when switching to practice mode
  useEffect(() => {
    if (practiceMode && metronomeState.isRunning && metronomeRef.current) {
      // Small delay to ensure the metronome component is mounted
      setTimeout(() => {
        if (metronomeRef.current) {
          metronomeRef.current.setTempo(metronomeState.tempo);
          metronomeRef.current.startMetronome();
        }
      }, 200);
    }
  }, [practiceMode, metronomeState.isRunning, metronomeState.tempo]);

  // Auto-start metronome when tempo goal is selected
  useEffect(() => {
    if (currentTempoGoal && metronomeRef.current && !metronomeAutoStarting.current) {
      metronomeAutoStarting.current = true;
      // Stop any existing metronome first
      metronomeRef.current.stopMetronome();
      // Set tempo and start with a delay
      metronomeRef.current.setTempo(currentTempoGoal.tempo);
      setTimeout(() => {
        if (metronomeRef.current) {
          metronomeRef.current.startMetronome();
        }
        metronomeAutoStarting.current = false;
      }, 100);
    } else if (currentTempoGoal && !metronomeRef.current && !metronomeAutoStarting.current) {
      metronomeAutoStarting.current = true;
      // Retry when metronome component mounts
      setTimeout(() => {
        if (currentTempoGoal && metronomeRef.current) {
          metronomeRef.current.stopMetronome();
          metronomeRef.current.setTempo(currentTempoGoal.tempo);
          setTimeout(() => {
            if (metronomeRef.current) {
              metronomeRef.current.startMetronome();
            }
            metronomeAutoStarting.current = false;
          }, 100);
        } else {
          metronomeAutoStarting.current = false;
        }
      }, 200);
    }
  }, [currentTempoGoal]);

  
  // Timer refs
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const taskTimerRef = useRef<NodeJS.Timeout | null>(null);
  const celebratedTasksRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Check if user needs to complete setup (only for students)
    if (currentUser.role === 'student' && !currentUser.hasCompletedSetup) {
      setShowSetupModal(true);
    }

    fetchLatestLesson();
    loadCustomTasks(); // Always load custom tasks regardless of lesson status
    loadTaskTimePreferences();
    loadSavedSessions();
    loadWeeklyProgress();
    loadBadges();
  }, [currentUser, navigate, isTeacherMode, viewingStudentId]);

  // Refresh user data when window regains focus (e.g., returning from profile edit)
  useEffect(() => {
    const handleFocus = () => {
      if (currentUser?.role === 'student') {
        refreshUserData();
        loadWeeklyProgress();
        loadCustomTasks(); // Reload custom tasks when returning to the page
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currentUser?.role, refreshUserData]);

  // Refresh weekly progress when practice frequency goal changes
  useEffect(() => {
    if (currentUser?.practiceSessionsPerWeek) {
      loadWeeklyProgress();
    }
  }, [currentUser?.practiceSessionsPerWeek]);

  // Refresh weekly progress when viewing student changes (teacher mode)
  useEffect(() => {
    if (isTeacherMode && viewingStudentId) {
      loadWeeklyProgress();
    }
  }, [viewingStudentId, isTeacherMode]);

  // Additional useEffect to ensure weekly progress loads when required data is available
  useEffect(() => {
    if (isTeacherMode) {
      // Teacher mode: ensure viewingStudent data is loaded
      if (viewingStudentId && viewingStudent && viewingStudent.practiceSessionsPerWeek) {
        // Only reload if we haven't loaded yet or if the student data changed
        if (!weeklyProgressLoaded || weeklyCompletedSessions.length === 0) {
          loadWeeklyProgress();
        }
      }
    } else {
      // Student mode: ensure currentUser data is available
      if (currentUser && currentUser.practiceSessionsPerWeek && !weeklyProgressLoaded) {
        loadWeeklyProgress();
      }
    }
  }, [viewingStudent, isTeacherMode, viewingStudentId, currentUser, weeklyProgressLoaded, weeklyCompletedSessions.length]);


  // Set default daily goal from user preferences
  useEffect(() => {
    if (currentUser?.practiceSessionDuration && !plannedTotalMinutes && hasLoadedPreferences) {
      setPlannedTotalMinutes(currentUser.practiceSessionDuration);
      setOverallRemainingTime(currentUser.practiceSessionDuration * 60);
    }
  }, [currentUser?.practiceSessionDuration, plannedTotalMinutes, hasLoadedPreferences]);

  // Set default daily goal from student preferences when in teacher mode
  useEffect(() => {
    if (isTeacherMode && viewingStudent?.practiceSessionDuration && !plannedTotalMinutes && hasLoadedPreferences) {
      setPlannedTotalMinutes(viewingStudent.practiceSessionDuration);
      setOverallRemainingTime(viewingStudent.practiceSessionDuration * 60);
    }
  }, [isTeacherMode, viewingStudent?.practiceSessionDuration, plannedTotalMinutes, hasLoadedPreferences]);

  // Recalculate overall remaining time when task durations change
  useEffect(() => {
    if (plannedTotalMinutes > 0) {
      const totalAllocatedMinutes = Object.values(taskDurations).reduce((sum, minutes) => sum + minutes, 0);
      if (totalAllocatedMinutes > 0) {
        setOverallRemainingTime(totalAllocatedMinutes * 60);
      } else {
        setOverallRemainingTime(plannedTotalMinutes * 60);
      }
    }
  }, [taskDurations, plannedTotalMinutes]);

  useEffect(() => {
    // Clear any existing interval first
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (sessionActive && !sessionPaused) {
      timerRef.current = setInterval(() => {
        if (sessionStartTime) {
          const now = new Date();
          const elapsed = Math.floor((now.getTime() - sessionStartTime.getTime()) / 1000);
          setElapsedTime(elapsed);
        }
      }, 1000);
    } else if (practiceMode && !sessionPaused && currentTaskStartTime) {
      timerRef.current = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - currentTaskStartTime.getTime()) / 1000);
        
        // Use the calculated elapsed time directly - the adjusted start time should give us the correct value
        setCurrentTaskElapsed(elapsed);
        
        // Check if current task in guided practice has reached its assigned time
        const currentTask = getGuidedTasks()[currentTaskIndex];
        if (currentTask) {
          // Create a task with source info for getTaskId
          const taskWithSource = {
            ...currentTask,
            source: 'custom' as const,
            originalIndex: currentTaskIndex
          };
          const taskId = getTaskId(taskWithSource);
          const assignedMinutes = taskDurations[taskId] || 0;
          const assignedSeconds = assignedMinutes * 60;
          
          
          // Only check if we haven't already celebrated this task
          if (assignedSeconds > 0 && elapsed >= assignedSeconds && !celebratedTasksRef.current.has(taskId)) {
            // Mark as celebrated immediately to prevent repeats
            celebratedTasksRef.current.add(taskId);
            setCelebratedTasks(prev => new Set(Array.from(prev).concat(taskId)));
            // Show celebration for guided practice task completion
            showTaskCompleteCelebration(currentTask.title, taskId);
          }
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sessionActive, sessionPaused, sessionStartTime, practiceMode, currentTaskStartTime, currentTaskIndex, taskDurations, categoryTimers]);

  // Combined timer effect - both countdown and individual task timers
  useEffect(() => {
    if (overallIsRunning && overallStartTime && !sessionPaused) {
      taskTimerRef.current = setInterval(() => {
        const now = new Date();
        const totalElapsed = Math.floor((now.getTime() - overallStartTime.getTime()) / 1000);
        const remaining = Math.max(0, (plannedTotalMinutes * 60) - totalElapsed);
        
        // Update both timers in one state update
        setOverallRemainingTime(remaining);
        setForceRender(prev => prev + 1); // Force re-render for real-time display
        
        // Check for streak milestone (10 minutes) in guided practice
        const totalElapsedMinutes = totalElapsed / 60;
        if (totalElapsedMinutes >= 10 && totalElapsedMinutes < 10.5) {
          checkStreakMilestone();
        }
        
        setTaskTimers(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(taskId => {
            if (updated[taskId].isRunning && updated[taskId].startTime) {
              const taskElapsed = Math.floor((now.getTime() - updated[taskId].startTime!.getTime()) / 1000);
              
              // Check if task has reached its assigned time
              const assignedMinutes = taskDurations[taskId] || 0;
              const assignedSeconds = assignedMinutes * 60;
              
              if (assignedSeconds > 0 && taskElapsed >= assignedSeconds && !updated[taskId].completed) {
                // Mark as completed and show celebration
                updated[taskId] = {
                  ...updated[taskId],
                  elapsed: taskElapsed,
                  completed: true
                };
                
                // Find the task title for celebration
                const task = getTaskByTaskId(taskId);
                if (task) {
                  showTaskCompleteCelebration(task.title, taskId);
                }
              } else {
                updated[taskId] = {
                  ...updated[taskId],
                  elapsed: taskElapsed
                };
              }
            }
          });
          return updated;
        });
      }, 1000);
    } else {
      if (taskTimerRef.current) {
        clearInterval(taskTimerRef.current);
        taskTimerRef.current = null;
      }
    }

    return () => {
      if (taskTimerRef.current) {
        clearInterval(taskTimerRef.current);
      }
    };
  }, [overallIsRunning, overallStartTime, plannedTotalMinutes, sessionPaused, taskDurations]);

  // Separate effect for display timer - simple and reliable
  useEffect(() => {
    let displayInterval: NodeJS.Timeout | null = null;
    
    if (overallIsRunning && !sessionPaused) {
      displayInterval = setInterval(() => {
        setDisplayTimer(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (displayInterval) {
        clearInterval(displayInterval);
      }
    };
  }, [overallIsRunning, sessionPaused]);

  // Simple independent timer effect - completely independent of taskTimers
  useEffect(() => {
    if (simpleTimerRunning) {
      simpleTimerRef.current = setInterval(() => {
        setSimpleTimer(prev => prev + 1);
        
        // Update individual task times independently - don't rely on taskTimers
        setSimpleTaskTimes(prev => {
          const updated = { ...prev };
          // Just increment each running task by 1 second
          Object.keys(updated).forEach(taskId => {
            if (updated[taskId] >= 0) { // If task has been started
              updated[taskId] = updated[taskId] + 1;
            }
          });
          return updated;
        });
      }, 1000);
    } else {
      if (simpleTimerRef.current) {
        clearInterval(simpleTimerRef.current);
        simpleTimerRef.current = null;
      }
    }

    return () => {
      if (simpleTimerRef.current) {
        clearInterval(simpleTimerRef.current);
      }
    };
  }, [simpleTimerRunning]);





  const fetchLatestLesson = async () => {
    try {
      setLoading(true);
      const targetStudentId = isTeacherMode ? viewingStudentId : currentUser!.uid;
      if (!targetStudentId) return;
      
      const lessonsQuery = query(
        collection(db, 'lessons'),
        where('studentId', '==', targetStudentId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(lessonsQuery);
      
      if (querySnapshot.empty) {
        setLatestLesson(null);
        setAvailableTasks([]);
        setCustomTasks([]);
        setError('');
      } else {
        const lessonDoc = querySnapshot.docs[0];
        const lessonData = {
          id: lessonDoc.id,
          ...lessonDoc.data()
        } as Lesson;
        
        setLatestLesson(lessonData);
        setAvailableTasks([]); // No longer using lesson-generated tasks
        setTaskCounts({});
        
        // Extract practice reminders from lesson data
        const lessonDataWithReminders = lessonData as any;
        if (lessonDataWithReminders.practiceReminders) {
          if (Array.isArray(lessonDataWithReminders.practiceReminders)) {
            setPracticeReminders(lessonDataWithReminders.practiceReminders);
          } else {
            // Handle old string format by splitting on newlines and bullet points
            const reminders = lessonDataWithReminders.practiceReminders
              .split('\n')
              .filter((line: string) => line.trim() && line.trim().startsWith('-'))
              .map((line: string) => line.trim().substring(1).trim());
            setPracticeReminders(reminders);
          }
        }
        
        // Load custom tasks from Firestore
        await loadCustomTasks();
        
        // Load quotes and mindset messages for carousel
        await loadQuotesAndMessages(targetStudentId);
      }
    } catch (err) {
      console.error('Error fetching latest lesson:', err);
      setError('Failed to load practice tasks');
    } finally {
      setLoading(false);
    }
  };

  // Load inspirational quotes for carousel
  const loadQuotesAndMessages = async (studentId: string) => {
    try {
      const inspirationalQuotes = [
        "Don't practice until you get it right. Practice until you can't get it wrong. – Unknown",
        "Practice is the best of all instructors. – Publilius Syrus",
        "Amateurs practice until they can play it right. Professionals practice until they can't play it wrong. – Unknown",
        "Without craftsmanship, inspiration is a mere reed shaken in the wind. – Johannes Brahms",
        "An hour of practice is worth five hours of practicing in your head. – Leopold Auer",
        "You don't have to practice every day, only on the days you eat. – Shinichi Suzuki",
        "Knowledge is not skill. Skill is knowledge plus ten thousand times. – Shinichi Suzuki",
        "To stop the flow of music would be like the stopping of time itself. – Aaron Copland",
        "Genius is one percent inspiration and ninety-nine percent perspiration. – Thomas Edison",
        "The more I practice, the luckier I get. – Gary Player (often adapted by musicians)",
        "The expert in anything was once a beginner. – Helen Hayes",
        "Success is the sum of small efforts, repeated day in and day out. – Robert Collier",
        "It's not that I'm so smart, it's just that I stay with problems longer. – Albert Einstein",
        "A river cuts through rock not because of its power, but because of its persistence. – Jim Watkins",
        "Fall seven times and stand up eight. – Japanese Proverb",
        "Slow practice is fast progress. – Unknown",
        "Small daily improvements over time lead to stunning results. – Robin Sharma",
        "Every strike brings me closer to the next home run. – Babe Ruth",
        "Courage is not having the strength to go on; it is going on when you don't have the strength. – Theodore Roosevelt",
        "The only way to do great work is to love what you do. – Steve Jobs",
        "Music is the divine way to tell beautiful, poetic things to the heart. – Pablo Casals",
        "Music is the universal language of mankind. – Henry Wadsworth Longfellow",
        "The music is not in the notes, but in the silence between. – W.A. Mozart",
        "Music is the mediator between the spiritual and the sensual life. – Ludwig van Beethoven",
        "Music washes away from the soul the dust of everyday life. – Berthold Auerbach",
        "Where words fail, music speaks. – Hans Christian Andersen",
        "Music is the shorthand of emotion. – Leo Tolstoy",
        "The true beauty of music is that it connects people. – Roy Ayers",
        "Life is like a piano. What you get out of it depends on how you play it. – Tom Lehrer",
        "The aim of art is to represent not the outward appearance of things, but their inward significance. – Aristotle",
        "We are what we repeatedly do. Excellence, then, is not an act but a habit. – Aristotle",
        "Mistakes are proof that you are trying. – Unknown",
        "A person who never made a mistake never tried anything new. – Albert Einstein",
        "Your mind is a powerful thing. When you fill it with positive thoughts, your life will start to change. – Unknown",
        "The greatest teacher, failure is. – Yoda",
        "Discipline is the bridge between goals and accomplishment. – Jim Rohn",
        "Motivation gets you started. Habit keeps you going. – Jim Rohn",
        "Learning is a treasure that will follow its owner everywhere. – Chinese Proverb",
        "If you can dream it, you can do it. – Walt Disney",
        "Don't let the fear of striking out keep you from playing the game. – Babe Ruth",
        "Art enables us to find ourselves and lose ourselves at the same time. – Thomas Merton",
        "Creativity takes courage. – Henri Matisse",
        "Inspiration is a guest that does not willingly visit the lazy. – Pyotr Ilyich Tchaikovsky",
        "You can't use up creativity. The more you use, the more you have. – Maya Angelou",
        "Art is not freedom from discipline, but disciplined freedom. – John F. Kennedy",
        "Don't play the notes. Play the meaning of the notes. – Pablo Casals",
        "Music begins where the possibilities of language end. – Jean Sibelius",
        "An artist is not paid for his labor but for his vision. – James Whistler",
        "A painter paints pictures on canvas. But musicians paint their pictures on silence. – Leopold Stokowski",
        "To play a wrong note is insignificant; to play without passion is inexcusable. – Ludwig van Beethoven"
      ];

      // Convert to carousel items and shuffle for random order
      const allItems = inspirationalQuotes.map((quote, index) => ({
        content: quote,
        id: `quote-${index}`
      }));

      // Shuffle the array for random order
      const shuffledItems = allItems.sort(() => Math.random() - 0.5);
      setCarouselItems(shuffledItems);
    } catch (error) {
      console.error('Error loading quotes and messages:', error);
    }
  };

  // Load task time preferences
  const loadTaskTimePreferences = async () => {
    try {
      if (!currentUser) return;

      const prefsDoc = await getDoc(doc(db, 'taskTimePreferences', currentUser.uid));
      if (prefsDoc.exists()) {
        const prefs = prefsDoc.data() as TaskTimePreferences;
        setTaskTimePreferences(prefs);
        
        // Apply saved preferences to task durations
        const durations: Record<string, number> = {};
        Object.entries(prefs).forEach(([taskId, minutes]) => {
          durations[taskId] = minutes;
        });
        setTaskDurations(durations);
      }
    } catch (error) {
      console.error('Error loading task time preferences:', error);
    } finally {
      setHasLoadedPreferences(true);
    }
  };

  // Save task time preferences
  const saveTaskTimePreferences = async (preferences: TaskTimePreferences) => {
    try {
      if (!currentUser) return;

      // Use setDoc with merge to create the document if it doesn't exist
      await setDoc(doc(db, 'taskTimePreferences', currentUser.uid), preferences, { merge: true });
      setTaskTimePreferences(preferences);
    } catch (error) {
      console.error('Error saving task time preferences:', error);
    }
  };

  // Saved sessions functions
  const loadSavedSessions = async () => {
    try {
      const targetStudentId = isTeacherMode ? viewingStudentId : currentUser?.uid;
      if (!targetStudentId) return;


      const sessionsQuery = query(
        collection(db, 'savedSessions'),
        where('userId', '==', targetStudentId)
      );
      
      const querySnapshot = await getDocs(sessionsQuery);
      const sessions: SavedSession[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        sessions.push({
          id: doc.id,
          ...data,
          // Convert Firestore timestamps to Date objects
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          lastUsed: data.lastUsed?.toDate ? data.lastUsed.toDate() : data.lastUsed
        } as SavedSession);
      });
      
      
      // Sort by createdAt descending manually
      sessions.sort((a, b) => {
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
        return bTime - aTime;
      });
      
      setSavedSessions(sessions);
    } catch (error) {
      console.error('Error loading saved sessions:', error);
    }
  };

  const saveCurrentSession = async () => {
    try {
      const targetStudentId = isTeacherMode ? viewingStudentId : currentUser?.uid;
      if (!targetStudentId || !newSessionName.trim()) return;

      const sessionData = {
        userId: targetStudentId,
        name: newSessionName.trim(),
        description: newSessionDescription.trim() || '',
        plannedTotalMinutes,
        taskDurations: { ...taskDurations },
        createdAt: serverTimestamp(),
        lastUsed: serverTimestamp(),
        createdBy: isTeacherMode ? 'teacher' as const : 'student' as const,
        teacherId: isTeacherMode ? currentUser?.uid : undefined
      };

      const docRef = await addDoc(collection(db, 'savedSessions'), sessionData);
      
      const newSession: SavedSession = {
        id: docRef.id,
        ...sessionData,
        createdAt: new Date(),
        lastUsed: new Date()
      };

      setSavedSessions(prev => [newSession, ...prev]);
      setShowSaveSessionModal(false);
      setNewSessionName('');
      setNewSessionDescription('');
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };

  const saveTeacherSession = async () => {
    try {
      if (!currentUser || !viewingStudentId || !teacherSessionName.trim()) return;

      const sessionData = {
        userId: viewingStudentId,
        name: teacherSessionName.trim(),
        description: teacherSessionDescription.trim() || '',
        plannedTotalMinutes,
        taskDurations: { ...taskDurations },
        createdAt: serverTimestamp(),
        lastUsed: serverTimestamp(),
        createdBy: 'teacher' as const,
        teacherId: currentUser.uid
      };

      const docRef = await addDoc(collection(db, 'savedSessions'), sessionData);
      
      const newSession: SavedSession = {
        id: docRef.id,
        ...sessionData,
        createdAt: new Date(),
        lastUsed: new Date()
      };

      setSavedSessions(prev => [newSession, ...prev]);
      setShowTeacherSessionModal(false);
      setTeacherSessionName('');
      setTeacherSessionDescription('');
    } catch (error) {
      console.error('Error saving teacher session:', error);
    }
  };

  const loadSavedSession = async (session: SavedSession) => {
    try {
      // Update task durations
      setTaskDurations(session.taskDurations);
      
      // Update planned total minutes
      setPlannedTotalMinutes(session.plannedTotalMinutes);
      
      // Calculate and set overall remaining time based on loaded task durations
      const totalAllocatedMinutes = Object.values(session.taskDurations).reduce((sum, minutes) => sum + minutes, 0);
      if (totalAllocatedMinutes > 0) {
        setOverallRemainingTime(totalAllocatedMinutes * 60);
      } else {
        setOverallRemainingTime(session.plannedTotalMinutes * 60);
      }
      
      // Don't mark planning as complete - let user see the loaded values and make adjustments
      // setPlanningComplete(true);
      
      // Update last used timestamp
      await updateDoc(doc(db, 'savedSessions', session.id), {
        lastUsed: serverTimestamp()
      });
      
      // Update local state
      setSavedSessions(prev => 
        prev.map(s => 
          s.id === session.id 
            ? { ...s, lastUsed: new Date() }
            : s
        ).sort((a, b) => {
          const aTime = new Date(b.lastUsed || b.createdAt).getTime();
          const bTime = new Date(a.lastUsed || a.createdAt).getTime();
          return aTime - bTime;
        })
      );
      
      setShowLoadSessionModal(false);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const deleteSavedSession = async (sessionId: string) => {
    try {
      // Check if this is a teacher-created session
      const session = savedSessions.find(s => s.id === sessionId);
      if (session && session.createdBy === 'teacher') {
        setError('Cannot delete teacher-created sessions');
        return;
      }
      
      await deleteDoc(doc(db, 'savedSessions', sessionId));
      setSavedSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const updateSavedSession = async (sessionId: string, updatedData: Partial<SavedSession>) => {
    try {
      await updateDoc(doc(db, 'savedSessions', sessionId), {
        ...updatedData,
        lastUsed: serverTimestamp()
      });
      
      // Update local state
      setSavedSessions(prev => prev.map(session => 
        session.id === sessionId 
          ? { ...session, ...updatedData, lastUsed: new Date() }
          : session
      ));
      
      setShowEditSessionModal(false);
      setEditingSession(null);
    } catch (error) {
      console.error('Error updating session:', error);
      setError('Failed to update session');
    }
  };

  const startEditingSession = (session: SavedSession) => {
    setEditingSession(session);
    setTeacherSessionName(session.name);
    setTeacherSessionDescription(session.description || '');
    setShowEditSessionModal(true);
  };

  // Profile setup modal handler
  const handleSetupSave = async () => {
    if (!currentUser) return;
    
    setSavingSetup(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        practiceSessionDuration: sessionDuration,
        practiceSessionsPerWeek: sessionsPerWeek,
        hasCompletedSetup: true
      });
      
      // Update local state
      setPlannedTotalMinutes(sessionDuration);
      setOverallRemainingTime(sessionDuration * 60);
      setShowSetupModal(false);
      
      // Refresh user data
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          practiceSessionDuration: sessionDuration,
          practiceSessionsPerWeek: sessionsPerWeek,
          hasCompletedSetup: true
        };
        // Note: The AuthContext will handle updating currentUser
      }
    } catch (error) {
      console.error('Error saving practice preferences:', error);
      setError('Failed to save practice preferences');
    } finally {
      setSavingSetup(false);
    }
  };

  // Load weekly completed practice sessions
  const loadWeeklyProgress = async () => {
    const targetStudentId = isTeacherMode ? viewingStudentId : currentUser?.uid;
    if (!targetStudentId) return;
    
    try {
      // Calculate the start of the current week (Sunday)
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - currentDay); // Go back to Sunday
      startOfWeek.setHours(0, 0, 0, 0); // Start of day
      
      // Calculate end of week (Saturday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999); // End of day
      
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', targetStudentId),
        where('completed', '==', true),
        where('endAt', '>=', startOfWeek),
        where('endAt', '<=', endOfWeek),
        orderBy('endAt', 'desc')
      );
      
      const querySnapshot = await getDocs(sessionsQuery);
      const sessions: PracticeSession[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          studentId: data.studentId,
          lessonId: data.lessonId,
          plannedTotalMinutes: data.plannedTotalMinutes,
          plannedDurations: data.plannedDurations,
          actualDurations: data.actualDurations,
          completed: data.completed,
          startAt: data.startAt,
          endAt: data.endAt,
          durationSec: data.durationSec,
          taskCounts: data.taskCounts,
          createdAt: data.createdAt
        });
      });
      
      // Store all sessions for total weekly time calculation
      setAllWeeklySessions(sessions);
      
      // Calculate goal-reached days based on cumulative daily practice time
      const targetUser = isTeacherMode ? viewingStudent : currentUser;
      // Use the same goal calculation as Dashboard for consistency
      const goalMinutes = targetUser?.practiceSessionDuration || 30;
      
      // Group sessions by day
      const sessionsByDay: { [dateKey: string]: PracticeSession[] } = {};
      sessions.forEach(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
        const dateKey = sessionDate.toDateString(); // e.g., "Mon Oct 21 2024"
        
        if (!sessionsByDay[dateKey]) {
          sessionsByDay[dateKey] = [];
        }
        sessionsByDay[dateKey].push(session);
      });
      
      // Check each day to see if cumulative practice meets the daily goal
      const goalReachedDays: PracticeSession[] = [];
      Object.entries(sessionsByDay).forEach(([dateKey, daySessions]) => {
        const dayTotalMinutes = daySessions.reduce((total, session) => {
          return total + (session.durationSec || 0) / 60;
        }, 0);
        
        // If daily total meets goal, add only ONE representative session per day
        // This ensures each calendar day counts as only one weekly goal achievement
        if (dayTotalMinutes >= goalMinutes) {
          // Use the first session of the day as the representative
          goalReachedDays.push(daySessions[0]);
        }
      });
      
      setWeeklyCompletedSessions(goalReachedDays);
      setWeeklyProgressLoaded(true);
    } catch (error) {
      console.error('Error loading weekly progress:', error);
      setWeeklyProgressLoaded(true); // Still mark as loaded to avoid infinite loading
    }
  };

  // Calculate weekly progress
  const getWeeklyProgress = () => {
    const targetUser = isTeacherMode ? viewingStudent : currentUser;
    if (!targetUser?.practiceSessionsPerWeek || !weeklyProgressLoaded) {
      return { completed: 0, target: 0, percentage: 0 };
    }
    
    const completed = weeklyCompletedSessions.length;
    const target = targetUser.practiceSessionsPerWeek;
    const percentage = Math.round((completed / target) * 100);
    
    return { completed, target, percentage };
  };

  // Refresh weekly progress when a practice session is completed
  const refreshWeeklyProgress = () => {
    loadWeeklyProgress();
  };

  // Check if daily goal was reached and create feed event
  const checkDailyGoalReached = async (sessionDurationMinutes: number) => {
    if (!currentUser || isTeacherMode) return;
    
    // Use Today's Session Goal as the daily goal target
    const todaySessionGoal = todaySessionDetails?.goal || plannedTotalMinutes || currentUser.practiceSessionDuration || 30;
    
    // Check if this session meets the daily goal (10+ minutes and meets goal)
    if (sessionDurationMinutes >= 10 && sessionDurationMinutes >= todaySessionGoal) {
      try {
        // Check if we already created a daily goal feed event today
        const today = new Date().toISOString().split('T')[0];
        const lastDailyGoalDate = localStorage.getItem(`lastDailyGoalFeed_${currentUser.uid}`);
        
        if (lastDailyGoalDate !== today) {
          // Create daily goal reached feed event
          await createFeedEvent({
            type: 'daily_goal_reached',
            fromUserId: currentUser.uid,
            fromUserNickname: currentUser.nickname || currentUser.name || 'Unknown'
          });
          
          // Store today's date to prevent duplicate events
          localStorage.setItem(`lastDailyGoalFeed_${currentUser.uid}`, today);
        }
      } catch (error) {
        console.error('Error creating daily goal feed event:', error);
      }
    }
  };

  // Badge system functions

  const calculateWeeklyGoals = (sessions: PracticeSession[]) => {
    if (sessions.length === 0) return 0;
    
    const weeks = new Set<string>();
    sessions.forEach(session => {
      const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
      const weekStart = new Date(sessionDate);
      weekStart.setDate(sessionDate.getDate() - sessionDate.getDay()); // Go back to Sunday
      weekStart.setHours(0, 0, 0, 0);
      weeks.add(weekStart.toISOString().split('T')[0]);
    });
    
    return weeks.size;
  };

  // Create a feed event
  const createFeedEvent = async (eventData: Omit<FeedEvent, 'id' | 'timestamp' | 'hearts' | 'nudges'>) => {
    try {
      const eventRef = await addDoc(collection(db, 'feedEvents'), {
        ...eventData,
        timestamp: serverTimestamp(),
        hearts: [],
        nudges: []
      });
      return eventRef.id;
    } catch (error) {
      console.error('Error creating feed event:', error);
    }
  };

  const loadBadges = async () => {
    const targetStudentId = isTeacherMode ? viewingStudentId : currentUser?.uid;
    if (!targetStudentId) return;
    
    try {
      const allBadges = await loadAllUserBadges(targetStudentId, db);
      
      // Check for new badges by comparing with previously stored badges
      const storedBadges = localStorage.getItem(`badges_${targetStudentId}`);
      const previousBadges = storedBadges ? JSON.parse(storedBadges) : [];
      
      // Debug logging
      console.log('Badge comparison:', {
        allBadges,
        previousBadges,
        targetStudentId
      });
      
      // Find new badges - use Set for more reliable comparison
      const previousBadgesSet = new Set(previousBadges);
      const newBadges = allBadges.filter((badgeId: string) => !previousBadgesSet.has(badgeId));
      
      console.log('New badges found:', newBadges);
      
      // Create feed events for new badges (only for students, not teachers viewing students)
      if (newBadges.length > 0 && !isTeacherMode && currentUser) {
        for (const badgeId of newBadges) {
          // Use the main badge definitions for consistent naming
          const badgeName = badgeDefinitions[badgeId]?.name || badgeId;
          
          // Check if this badge was already awarded today to prevent duplicates
          const today = new Date().toISOString().split('T')[0];
          const lastBadgeAwardDate = localStorage.getItem(`lastBadgeAward_${badgeId}_${targetStudentId}`);
          
          if (lastBadgeAwardDate !== today) {
            await createFeedEvent({
              type: 'badge_earned',
              fromUserId: currentUser.uid,
              fromUserNickname: currentUser.nickname || currentUser.name || 'Unknown',
              badgeName: badgeName
            });
            
            // Store the date this badge was awarded
            localStorage.setItem(`lastBadgeAward_${badgeId}_${targetStudentId}`, today);
          }
        }
      }
      
      // Save current badges to localStorage for future comparison
      localStorage.setItem(`badges_${targetStudentId}`, JSON.stringify(allBadges));
      
      setEarnedBadges(allBadges);
      setBadgesLoaded(true);
    } catch (error) {
      console.error('Error loading badges:', error);
      setBadgesLoaded(true);
    }
  };


  // Badge definitions
  const badgeDefinitions: Record<string, { emoji: string; name: string; description: string; color: string }> = {
    'first-day': { emoji: '🎯', name: 'First Steps', description: 'Completed your first practice session', color: 'from-blue-500 to-cyan-500' },
    'three-day-streak': { emoji: '🔥', name: 'Hot Streak', description: '3-day practice streak', color: 'from-orange-500 to-red-500' },
    'week-streak': { emoji: '⭐', name: 'Week Warrior', description: '7-day practice streak', color: 'from-yellow-500 to-orange-500' },
    'two-week-streak': { emoji: '💪', name: 'Consistency King', description: '14-day practice streak', color: 'from-green-500 to-emerald-500' },
    'month-streak': { emoji: '👑', name: 'Practice Legend', description: '30-day practice streak', color: 'from-purple-500 to-pink-500' },
    'first-week': { emoji: '📅', name: 'Week Complete', description: 'Met weekly goal once', color: 'from-indigo-500 to-blue-500' },
    'month-consistency': { emoji: '🏆', name: 'Monthly Master', description: '4 weeks of meeting goals', color: 'from-amber-500 to-yellow-500' },
    'quarter-master': { emoji: '🎖️', name: 'Quarter Champion', description: '12 weeks of meeting goals', color: 'from-rose-500 to-pink-500' },
    'hour-master': { emoji: '⏰', name: 'Hour Hero', description: '1 hour total practice time', color: 'from-teal-500 to-cyan-500' },
    'five-hour-club': { emoji: '🎵', name: 'Five Hour Club', description: '5 hours total practice time', color: 'from-violet-500 to-purple-500' },
    'thousand-minute-master': { emoji: '💯', name: 'Thousand Minute Master', description: '1000 minutes total practice', color: 'from-emerald-500 to-green-500' },
    'practice-legend': { emoji: '🌟', name: 'Practice Legend', description: '5000 minutes total practice', color: 'from-yellow-400 to-orange-500' },
    'focused-practicer': { emoji: '🎯', name: 'Focused Practicer', description: 'Average 30+ min sessions', color: 'from-blue-600 to-indigo-600' },
    'dedicated-musician': { emoji: '🎼', name: 'Dedicated Musician', description: 'Average 60+ min sessions', color: 'from-purple-600 to-violet-600' },
    
    // Teacher Assigned Badges
    'performance-ready': { emoji: '🎭', name: 'Performance Ready', description: 'Teacher says you\'re ready to perform!', color: 'from-rose-500 to-pink-600' },
    'breakthrough-moment': { emoji: '💡', name: 'Breakthrough Moment', description: 'Teacher recognised you had a major breakthrough', color: 'from-yellow-400 to-orange-500' },
    'technique-perfectionist': { emoji: '✨', name: 'Technique Perfectionist', description: 'Teacher praised your technique work', color: 'from-blue-400 to-indigo-500' },
    'musical-expression': { emoji: '🎨', name: 'Musical Expression', description: 'Teacher loved your musical interpretation', color: 'from-purple-400 to-pink-500' },
    'sight-reading-star': { emoji: '👁️', name: 'Sight Reading Star', description: 'Teacher impressed by your sight reading', color: 'from-green-400 to-teal-500' },
    'memory-master': { emoji: '🧠', name: 'Memory Master', description: 'Teacher praised your memorisation skills', color: 'from-violet-400 to-purple-500' },
    'rhythm-king': { emoji: '🥁', name: 'Rhythm King/Queen', description: 'Teacher loved your rhythm work', color: 'from-red-400 to-pink-500' },
    'practice-champion': { emoji: '🏅', name: 'Practice Champion', description: 'Teacher noticed your excellent practice habits', color: 'from-amber-400 to-yellow-500' },
    'creative-genius': { emoji: '🎪', name: 'Creative Genius', description: 'Teacher praised your creativity', color: 'from-pink-400 to-rose-500' },
    'dedication-award': { emoji: '💎', name: 'Dedication Award', description: 'Teacher recognised your dedication', color: 'from-indigo-400 to-blue-500' },
    'improvement-star': { emoji: '⭐', name: 'Improvement Star', description: 'Teacher noticed significant improvement', color: 'from-emerald-400 to-green-500' },
    'lesson-hero': { emoji: '🦸', name: 'Lesson Hero', description: 'Teacher said you had an amazing lesson', color: 'from-orange-400 to-red-500' },

    // String Technique Badges - Bow Technique
    'bow-hold-beginner': { emoji: '🤏', name: 'Bow Hold Beginner', description: 'You are learning a correct bow hold', color: 'from-amber-400 to-yellow-500' },
    'bow-hold-developing': { emoji: '✊', name: 'Bow Hold Developing', description: 'You now have a stable bow hold', color: 'from-yellow-400 to-orange-500' },
    'bow-hold-master': { emoji: '👑', name: 'Bow Hold Master', description: 'You consistently play with an excellent bow hold', color: 'from-orange-400 to-red-500' },
    
    'straight-bow-beginner': { emoji: '📏', name: 'Straight Bow Beginner', description: 'You are learning to keep the bow straight', color: 'from-blue-400 to-indigo-500' },
    'straight-bow-developing': { emoji: '📐', name: 'Straight Bow Developing', description: 'You now understand how to keep the bow straight', color: 'from-indigo-400 to-purple-500' },
    'straight-bow-champion': { emoji: '🏆', name: 'Straight Bow Champion', description: 'You consistently play with a straight bow', color: 'from-purple-400 to-pink-500' },
    
    'spiccato-beginner': { emoji: '⚡', name: 'Spiccato Beginner', description: 'You are beginning to learn spiccato', color: 'from-green-400 to-teal-500' },
    'spiccato-developing': { emoji: '💫', name: 'Spiccato Developing', description: 'You now understand how to bounce the bow clearly', color: 'from-teal-400 to-cyan-500' },
    'spiccato-specialist': { emoji: '⭐', name: 'Spiccato Specialist', description: 'You consistently play controlled spiccato', color: 'from-cyan-400 to-blue-500' },
    
    'staccato-beginner': { emoji: '🔸', name: 'Staccato Beginner', description: 'You are learning short, separated strokes', color: 'from-red-400 to-pink-500' },
    'staccato-developing': { emoji: '🔹', name: 'Staccato Developing', description: 'You now understand how to play staccato cleanly', color: 'from-pink-400 to-rose-500' },
    'staccato-star': { emoji: '✨', name: 'Staccato Star', description: 'You consistently play crisp staccato', color: 'from-rose-400 to-red-500' },
    
    'legato-beginner': { emoji: '🌊', name: 'Legato Beginner', description: 'You are learning smooth connected bowing', color: 'from-violet-400 to-purple-500' },
    'legato-developing': { emoji: '🎵', name: 'Legato Developing', description: 'You now have flowing legato phrases', color: 'from-purple-400 to-indigo-500' },
    'legato-legend': { emoji: '🎼', name: 'Legato Legend', description: 'You consistently play beautiful legato', color: 'from-indigo-400 to-blue-500' },
    
    'bow-distribution-beginner': { emoji: '⚖️', name: 'Bow Distribution Beginner', description: 'You are learning how to share bow evenly', color: 'from-emerald-400 to-green-500' },
    'bow-distribution-developing': { emoji: '🎯', name: 'Bow Distribution Developing', description: 'You now understand bow planning', color: 'from-green-400 to-emerald-500' },
    'bow-distribution-pro': { emoji: '🏅', name: 'Bow Distribution Pro', description: 'You consistently use bow distribution effectively', color: 'from-emerald-400 to-teal-500' },
    
    'sautille-beginner': { emoji: '🦗', name: 'Sautillé Beginner', description: 'You are exploring Sautillé strokes', color: 'from-lime-400 to-green-500' },
    'sautille-developing': { emoji: '🦎', name: 'Sautillé Developing', description: 'You now understand how to play Sautillé', color: 'from-green-400 to-emerald-500' },
    'sautille-master': { emoji: '🦅', name: 'Sautillé Master', description: 'You consistently play controlled Sautillé', color: 'from-emerald-400 to-teal-500' },

    // String Technique Badges - Left Hand Technique
    'vibrato-beginner': { emoji: '🌊', name: 'Vibrato Beginner', description: 'You are starting to explore vibrato', color: 'from-pink-400 to-rose-500' },
    'vibrato-developing': { emoji: '💫', name: 'Vibrato Developing', description: 'You now have basic vibrato control', color: 'from-rose-400 to-red-500' },
    'vibrato-virtuoso': { emoji: '🎭', name: 'Vibrato Virtuoso', description: 'You consistently play with beautiful vibrato', color: 'from-red-400 to-pink-500' },
    
    'intonation-beginner': { emoji: '🎯', name: 'Intonation Beginner', description: 'You are learning to check finger accuracy', color: 'from-blue-400 to-indigo-500' },
    'intonation-developing': { emoji: '📐', name: 'Intonation Developing', description: 'You now understand how to adjust intonation', color: 'from-indigo-400 to-purple-500' },
    'intonation-ace': { emoji: '🎪', name: 'Intonation Ace', description: 'You consistently play with accurate intonation', color: 'from-purple-400 to-pink-500' },
    
    'shifting-beginner': { emoji: '🚀', name: 'Shifting Beginner', description: 'You are beginning to shift between positions', color: 'from-orange-400 to-red-500' },
    'shifting-developing': { emoji: '🎢', name: 'Shifting Developing', description: 'You now have smoother position changes', color: 'from-red-400 to-pink-500' },
    'shifting-star': { emoji: '⭐', name: 'Shifting Star', description: 'You consistently shift with accuracy and ease', color: 'from-pink-400 to-rose-500' },
    
    'double-stops-beginner': { emoji: '🤝', name: 'Double Stops Beginner', description: 'You are learning to balance two notes', color: 'from-violet-400 to-purple-500' },
    'double-stops-developing': { emoji: '⚖️', name: 'Double Stops Developing', description: 'You now understand how to tune double stops', color: 'from-purple-400 to-indigo-500' },
    'double-stops-master': { emoji: '🎼', name: 'Double Stops Master', description: 'You consistently play clear, balanced double stops', color: 'from-indigo-400 to-blue-500' },
    
    'trills-beginner': { emoji: '🦋', name: 'Trills Beginner', description: 'You are learning how to play trills', color: 'from-emerald-400 to-green-500' },
    'trills-developing': { emoji: '🌿', name: 'Trills Developing', description: 'You now understand how to make trills even', color: 'from-green-400 to-emerald-500' },
    'trills-talent': { emoji: '🎪', name: 'Trills Talent', description: 'You consistently play fast, clear trills', color: 'from-emerald-400 to-teal-500' },

    // String Technique Badges - Advanced Techniques
    'harmonics-beginner': { emoji: '🔮', name: 'Harmonics Beginner', description: 'You are discovering natural harmonics', color: 'from-cyan-400 to-blue-500' },
    'harmonics-developing': { emoji: '✨', name: 'Harmonics Developing', description: 'You now understand how to produce harmonics', color: 'from-blue-400 to-indigo-500' },
    'harmonics-hero': { emoji: '🦸', name: 'Harmonics Hero', description: 'You consistently play clear harmonics', color: 'from-indigo-400 to-purple-500' },
    
    'pizzicato-beginner': { emoji: '👆', name: 'Pizzicato Beginner', description: 'You are learning how to pluck clearly', color: 'from-amber-400 to-yellow-500' },
    'pizzicato-developing': { emoji: '🤏', name: 'Pizzicato Developing', description: 'You now understand how to project pizzicato', color: 'from-yellow-400 to-orange-500' },
    'pizzicato-pro': { emoji: '💪', name: 'Pizzicato Pro', description: 'You consistently play strong, even pizzicato', color: 'from-orange-400 to-red-500' },
    
    'col-legno-beginner': { emoji: '🪵', name: 'Col Legno Beginner', description: 'You are exploring col legno strokes', color: 'from-stone-400 to-gray-500' },
    'col-legno-developing': { emoji: '🌳', name: 'Col Legno Developing', description: 'You now understand how to play col legno', color: 'from-gray-400 to-zinc-500' },
    'col-legno-champion': { emoji: '🏆', name: 'Col Legno Champion', description: 'You consistently use col legno effectively', color: 'from-zinc-400 to-neutral-500' },
    
    'sul-ponticello-beginner': { emoji: '🌊', name: 'Sul Ponticello Beginner', description: 'You are exploring sul ponticello sounds', color: 'from-sky-400 to-blue-500' },
    'sul-ponticello-developing': { emoji: '🌀', name: 'Sul Ponticello Developing', description: 'You now understand how to control sul ponticello', color: 'from-blue-400 to-indigo-500' },
    'sul-ponticello-star': { emoji: '💫', name: 'Sul Ponticello Star', description: 'You consistently create a strong sul ponticello sound', color: 'from-indigo-400 to-purple-500' },

    // Universal Technique Badges - Musical Expression
    'dynamics-beginner': { emoji: '🔊', name: 'Dynamics Beginner', description: 'You are learning to play soft and loud with control', color: 'from-blue-400 to-indigo-500' },
    'dynamics-developing': { emoji: '🎚️', name: 'Dynamics Developing', description: 'You now understand how to shape dynamics effectively', color: 'from-indigo-400 to-purple-500' },
    'dynamics-expert': { emoji: '🎛️', name: 'Dynamics Expert', description: 'You consistently control dynamics with expression', color: 'from-purple-400 to-pink-500' },
    
    'phrasing-beginner': { emoji: '🎵', name: 'Phrasing Beginner', description: 'You are learning to shape musical phrases', color: 'from-green-400 to-emerald-500' },
    'phrasing-developing': { emoji: '🌊', name: 'Phrasing Developing', description: 'You now understand how to connect notes into flowing phrases', color: 'from-emerald-400 to-teal-500' },
    'phrasing-pro': { emoji: '🎼', name: 'Phrasing Pro', description: 'You consistently play with expressive phrasing', color: 'from-teal-400 to-cyan-500' },
    
    'articulation-beginner': { emoji: '🎯', name: 'Articulation Beginner', description: 'You are learning clear starts and stops', color: 'from-orange-400 to-red-500' },
    'articulation-developing': { emoji: '🎪', name: 'Articulation Developing', description: 'You now understand how to vary articulation styles', color: 'from-red-400 to-pink-500' },
    'articulation-ace': { emoji: '🏆', name: 'Articulation Ace', description: 'You consistently play with clear, accurate articulation', color: 'from-pink-400 to-rose-500' },
    
    'tone-quality-beginner': { emoji: '🌟', name: 'Tone Quality Beginner', description: 'You are learning to produce a steady tone', color: 'from-yellow-400 to-orange-500' },
    'tone-quality-developing': { emoji: '✨', name: 'Tone Quality Developing', description: 'You now understand how to refine and shape your tone', color: 'from-orange-400 to-red-500' },
    'tone-quality-star': { emoji: '💫', name: 'Tone Quality Star', description: 'You consistently create a beautiful, resonant tone', color: 'from-red-400 to-pink-500' },

    // Universal Technique Badges - Technical Foundation
    'posture-beginner': { emoji: '🧍', name: 'Posture Beginner', description: 'You are learning correct playing posture', color: 'from-emerald-400 to-green-500' },
    'posture-developing': { emoji: '🚶', name: 'Posture Developing', description: 'You now understand how posture affects your playing', color: 'from-green-400 to-emerald-500' },
    'posture-perfect': { emoji: '👑', name: 'Posture Perfect', description: 'You consistently maintain excellent posture', color: 'from-emerald-400 to-teal-500' },
    
    'relaxation-beginner': { emoji: '🌱', name: 'Relaxation Beginner', description: 'You are learning to release tension while playing', color: 'from-lime-400 to-green-500' },
    'relaxation-developing': { emoji: '🌿', name: 'Relaxation Developing', description: 'You now understand how to stay relaxed under pressure', color: 'from-green-400 to-emerald-500' },
    'relaxation-mastery': { emoji: '🕊️', name: 'Relaxation Mastery', description: 'You consistently play with freedom and no excess tension', color: 'from-emerald-400 to-teal-500' },
    
    'endurance-beginner': { emoji: '⏱️', name: 'Endurance Beginner', description: 'You are learning to maintain focus in practice', color: 'from-violet-400 to-purple-500' },
    'endurance-developing': { emoji: '💪', name: 'Endurance Developing', description: 'You now understand how to build stamina in longer sessions', color: 'from-purple-400 to-indigo-500' },
    'endurance-champion': { emoji: '🏅', name: 'Endurance Champion', description: 'You consistently sustain energy and concentration in practice', color: 'from-indigo-400 to-blue-500' },

    // Special Occasion Badges
    'birthday-celebration': { emoji: '🎂', name: 'Birthday Practice Star', description: 'You practised on your birthday! What dedication!', color: 'from-pink-400 to-purple-600' }
  };

  const renderBadge = (badgeId: string) => {
    const badge = badgeDefinitions[badgeId];
    if (!badge) return null;

    return (
      <div
        key={badgeId}
        className={`bg-gradient-to-br ${badge.color} text-white p-2 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300 cursor-pointer group relative`}
        title={`${badge.name}: ${badge.description}`}
      >
        <div className="text-lg sm:text-xl">{badge.emoji}</div>
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
          <div className="font-semibold">{badge.name}</div>
          <div className="text-gray-300">{badge.description}</div>
        </div>
      </div>
    );
  };

  const playTickSound = () => {
    // Create a simple tick sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (err) {
      // Audio not supported, skipping sound effect
    }
  };

  const playTaskCompleteSound = () => {
    try {
      // Try to play custom sound file first
      const audio = new Audio('/ding.mp3');
      audio.volume = 0.7; // Set volume to 70%
      audio.play().catch(() => {
        // Fallback to Web Audio API if custom file fails
        playFallbackSound();
      });
    } catch (err) {
      // Audio not supported, skipping celebration sound
    }
  };

  const playFallbackSound = () => {
    // Fallback sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Simple ding sound - single frequency with quick decay
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (err) {
      // Fallback audio not supported
    }
  };

  const showTaskCompleteCelebration = (taskTitle: string, taskId?: string) => {
    // Prevent multiple celebrations for the same task
    if (taskId && celebratedTasks.has(taskId)) {
      return;
    }
    
    if (taskId) {
      setCelebratedTasks(prev => new Set(Array.from(prev).concat(taskId)));
    }
    
    setCompletedTaskTitle(taskTitle);
    setShowTaskComplete(true);
    playTaskCompleteSound();
    
    // No auto-dismiss - user must click button to close
  };

  const closeTaskCompleteCelebration = () => {
    setShowTaskComplete(false);
    setCurrentCelebratedTaskId(null);
  };

  const handleTaskClick = (taskIndex: number, isCustomTask: boolean = false) => {
    if (!sessionActive || sessionPaused) return;
    
    let task: Task;
    let taskId: string;
    
    if (isCustomTask) {
      task = customTasks[taskIndex];
      taskId = `custom_${task.id}`;
      
      // Remove from custom tasks
      setCustomTasks(prev => prev.filter((_, index) => index !== taskIndex));
    } else {
      task = availableTasks[taskIndex];
      taskId = `${latestLesson!.lessonId}_task_${taskIndex}`;
      
      // Remove task from available list
      const newAvailableTasks = availableTasks.filter((_, index) => index !== taskIndex);
      setAvailableTasks(newAvailableTasks);
    }
    
    // Play tick sound
    playTickSound();
    
    // Increment task count
    setTaskCounts(prev => ({
      ...prev,
      [taskId]: (prev[taskId] || 0) + 1
    }));
  };

  const startPractice = async () => {
    if (!latestLesson) return;
    
    try {
      const sessionData = {
        studentId: currentUser!.uid,
        lessonId: latestLesson.lessonId,
        plannedTotalMinutes: 0,
        plannedDurations: {},
        actualDurations: {},
        completed: false,
        startAt: serverTimestamp(),
        taskCounts: {},
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'practiceSessions'), sessionData);
      
      const session: PracticeSession = {
        id: docRef.id,
        studentId: sessionData.studentId,
        lessonId: sessionData.lessonId,
        plannedTotalMinutes: sessionData.plannedTotalMinutes,
        plannedDurations: sessionData.plannedDurations,
        actualDurations: sessionData.actualDurations,
        completed: sessionData.completed,
        taskCounts: sessionData.taskCounts,
        startAt: new Date(),
        createdAt: new Date()
      };
      
      setCurrentSession(session);
      setSessionActive(true);
      setSessionPaused(false);
      setSessionStartTime(new Date());
      setElapsedTime(0);
      setTaskCounts({});
      
    } catch (err) {
      console.error('Error starting practice session:', err);
      setError('Failed to start practice session');
    }
  };

  const pausePractice = () => {
    setSessionPaused(true);
  };

  const resumePractice = () => {
    setSessionPaused(false);
  };

  const finishPractice = async () => {
    if (!currentSession || !sessionStartTime) return;
    
    try {
      const endTime = new Date();
      const durationSec = Math.floor((endTime.getTime() - sessionStartTime.getTime()) / 1000);
      
      await updateDoc(doc(db, 'practiceSessions', currentSession.id), {
        endAt: serverTimestamp(),
        durationSec: durationSec,
        taskCounts: taskCounts
      });
      
      // Reset session state
      setSessionActive(false);
      setSessionPaused(false);
      setCurrentSession(null);
      setSessionStartTime(null);
      setElapsedTime(0);
      
      // Refresh weekly progress and badges
      refreshWeeklyProgress();
      loadBadges();
      
      // Check if daily goal was reached
      const sessionDurationMinutes = durationSec / 60;
      await checkDailyGoalReached(sessionDurationMinutes);
      
      // Reset tasks for next session
      if (latestLesson) {
        setAvailableTasks([]); // No longer using lesson-generated tasks
        setTaskCounts({});
      }
      
      // Reload custom tasks for next session
      await loadCustomTasks();
      
    } catch (err) {
      console.error('Error finishing practice session:', err);
      setError('Failed to finish practice session');
    }
  };

  const loadCustomTasks = async () => {
    try {
      const targetStudentId = isTeacherMode ? viewingStudentId : currentUser!.uid;
      if (!targetStudentId) return;
      
      
      // First try with the composite index (orderBy included)
      let customTasksQuery = query(
        collection(db, 'customTasks'),
        where('studentId', '==', targetStudentId),
        orderBy('createdAt', 'desc')
      );

      try {
        const querySnapshot = await getDocs(customTasksQuery);
        const tasks: Task[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          tasks.push({
            id: doc.id,
            title: data.title,
            category: data.category,
            isCustom: true,
            createdBy: data.createdBy,
            teacherId: data.teacherId,
            practiceReminders: data.practiceReminders || []
          });
        });
        
        setCustomTasks(tasks);
      } catch (indexError: any) {
        // If index not ready, fallback to simple query and sort client-side
        if (indexError.code === 'failed-precondition') {
          
          const fallbackQuery = query(
            collection(db, 'customTasks'),
            where('studentId', '==', targetStudentId)
          );

          const querySnapshot = await getDocs(fallbackQuery);
          const tasks: Task[] = [];
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            tasks.push({
              id: doc.id,
              title: data.title,
              category: data.category,
              isCustom: true,
              createdBy: data.createdBy,
              teacherId: data.teacherId,
              practiceReminders: data.practiceReminders || []
            });
          });

          // Sort by createdAt descending on client side
          tasks.sort((a, b) => {
            // For custom tasks, we'll sort by the order they were added (most recent first)
            // Since we can't access createdAt in the Task interface, we'll maintain the order from Firestore
            return 0; // Keep original order from Firestore
          });
          
          setCustomTasks(tasks);
        } else {
          throw indexError;
        }
      }
    } catch (err) {
      console.error('Error loading custom tasks:', err);
      // Don't show error for custom tasks, just continue without them
    }
  };

  const addCustomTask = async () => {
    if (!newTaskTitle.trim()) return;
    
    // Ensure title doesn't exceed 35 characters
    const trimmedTitle = newTaskTitle.trim();
    if (trimmedTitle.length > 35) {
      setError('Task title must be 35 characters or less');
      return;
    }
    
    try {
      const targetStudentId = isTeacherMode ? viewingStudentId : currentUser!.uid;
      const taskData = {
        studentId: targetStudentId,
        title: trimmedTitle,
        category: newTaskCategory,
        createdBy: isTeacherMode ? 'teacher' : 'student',
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'customTasks'), taskData);
      
      const newTask: Task = {
        id: docRef.id,
        title: trimmedTitle,
        category: newTaskCategory,
        isCustom: true,
        createdBy: isTeacherMode ? 'teacher' : 'student',
        practiceReminders: []
      };
      
      setCustomTasks(prev => [newTask, ...prev]);
      setNewTaskTitle('');
      setShowAddTask(false);
      
    } catch (err) {
      console.error('Error adding custom task:', err);
      setError('Failed to add custom task');
    }
  };

  const removeCustomTask = async (taskId: string) => {
    try {
      // Check if this is a teacher-created task
      const task = customTasks.find(t => t.id === taskId);
      if (task && task.createdBy === 'teacher') {
        setError('Cannot delete teacher-created tasks');
        return;
      }
      
      await deleteDoc(doc(db, 'customTasks', taskId));
      setCustomTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (err) {
      console.error('Error removing custom task:', err);
      setError('Failed to remove custom task');
    }
  };

  const startEditingCategory = (task: any) => {
    setEditingCategory(task.id || `${task.source}-${task.originalIndex}`);
    setQuickEditCategory(task.category as 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble');
  };

  const cancelCategoryEdit = () => {
    setEditingCategory(null);
    setQuickEditCategory('Scales');
  };

  const updateTaskCategory = async (task: any) => {
    try {
      if (task.source === 'custom' && task.id) {
        // Update custom task category
        await updateDoc(doc(db, 'customTasks', task.id), {
          category: quickEditCategory
        });
        // Update local state
        setCustomTasks(prev => prev.map(t => 
          t.id === task.id ? { ...t, category: quickEditCategory } : t
        ));
      } else if (task.source === 'lesson' && latestLesson) {
        // Lesson tasks are no longer editable - this should not happen
        console.warn('Attempted to edit lesson task, but lesson tasks are no longer editable');
        return;
      }
      
      setEditingCategory(null);
      setQuickEditCategory('Scales');
    } catch (error) {
      console.error('Error updating task category:', error);
      setError('Failed to update task category');
    }
  };

  const startEditingTask = (task: any) => {
    setEditingTask(task.id || `${task.source}-${task.originalIndex}`);
    setEditTaskTitle(task.title);
    setEditTaskCategory(task.category as 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble');
  };

  const cancelEditing = () => {
    setEditingTask(null);
    setEditTaskTitle('');
    setEditTaskCategory('Scales');
  };

  const updateTask = async (task: any) => {
    if (!editTaskTitle.trim()) return;

    // Ensure title doesn't exceed 35 characters
    const trimmedTitle = editTaskTitle.trim();
    if (trimmedTitle.length > 35) {
      setError('Task title must be 35 characters or less');
      return;
    }

    try {
      if (task.source === 'custom' && task.id) {
        // Update custom task
        await updateDoc(doc(db, 'customTasks', task.id), {
          title: trimmedTitle,
          category: editTaskCategory
        });
        // Update local state
        setCustomTasks(prev => prev.map(t => 
          t.id === task.id ? { ...t, title: trimmedTitle, category: editTaskCategory } : t
        ));
      } else if (task.source === 'lesson' && latestLesson) {
        // Lesson tasks are no longer editable - this should not happen
        console.warn('Attempted to edit lesson task, but lesson tasks are no longer editable');
        return;
      }
      
      setEditingTask(null);
      setEditTaskTitle('');
      setEditTaskCategory('Scales');
    } catch (error) {
      console.error('Error updating task:', error);
      setError('Failed to update task');
    }
  };

  const deleteLessonTask = async (taskIndex: number) => {
    // Lesson tasks are no longer editable - this should not happen
    console.warn('Attempted to delete lesson task, but lesson tasks are no longer editable');
    return;
  };

  // Practice reminder functions
  const addPracticeReminder = async (taskId: string) => {
    if (!newReminderText.trim()) return;

    try {
      const taskRef = doc(db, 'customTasks', taskId);
      const currentTask = customTasks.find(t => t.id === taskId);
      if (!currentTask) return;

      // Create reminder object with optional metronome tempo
      const reminderObj = newReminderHasMetronome 
        ? { 
            content: newReminderText.trim(), 
            hasMetronome: true, 
            tempo: newReminderTempo 
          }
        : newReminderText.trim();

      const updatedReminders = [...(currentTask.practiceReminders || []), reminderObj];
      
      await updateDoc(taskRef, {
        practiceReminders: updatedReminders
      });

      // Update local state
      setCustomTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, practiceReminders: updatedReminders } : t
      ));

      setNewReminderText('');
      setNewReminderHasMetronome(false);
      setNewReminderTempo(120);
      setShowAddReminder(false);
      setSelectedTaskForReminder(null);
    } catch (error) {
      console.error('Error adding practice reminder:', error);
      setError('Failed to add practice reminder');
    }
  };

  const editPracticeReminder = async (taskId: string, reminderIndex: number) => {
    if (!editReminderText.trim()) return;

    try {
      const taskRef = doc(db, 'customTasks', taskId);
      const currentTask = customTasks.find(t => t.id === taskId);
      if (!currentTask || !currentTask.practiceReminders) return;

      const updatedReminders = [...currentTask.practiceReminders];
      updatedReminders[reminderIndex] = editReminderText.trim();
      
      await updateDoc(taskRef, {
        practiceReminders: updatedReminders
      });

      // Update local state
      setCustomTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, practiceReminders: updatedReminders } : t
      ));

      setEditReminderText('');
      setEditingReminder(null);
    } catch (error) {
      console.error('Error editing practice reminder:', error);
      setError('Failed to edit practice reminder');
    }
  };

  const deletePracticeReminder = async (taskId: string, reminderIndex: number) => {
    try {
      const taskRef = doc(db, 'customTasks', taskId);
      const currentTask = customTasks.find(t => t.id === taskId);
      if (!currentTask || !currentTask.practiceReminders) return;

      const updatedReminders = currentTask.practiceReminders.filter((_, index) => index !== reminderIndex);
      
      await updateDoc(taskRef, {
        practiceReminders: updatedReminders
      });

      // Update local state
      setCustomTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, practiceReminders: updatedReminders } : t
      ));
    } catch (error) {
      console.error('Error deleting practice reminder:', error);
      setError('Failed to delete practice reminder');
    }
  };

  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const toggleReminderCompletion = (reminder: string) => {
    setCompletedReminders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reminder)) {
        newSet.delete(reminder);
      } else {
        newSet.add(reminder);
        // Show tip response popup when completing a tip
        setCurrentTipText(reminder);
        setCurrentTipKey(reminder); // Using reminder text as key for now
        setShowTipResponsePopup(true);
      }
      return newSet;
    });
  };

  const saveTipResponse = async (response: string) => {
    if (!currentUser || !currentTipText) return;

    try {
      const { addDoc, collection } = await import('firebase/firestore');
      await addDoc(collection(db, 'tipResponses'), {
        studentId: currentUser.uid,
        tipText: currentTipText,
        response: response,
        timestamp: new Date(),
        sessionId: currentSession?.id || 'unknown'
      });
    } catch (error) {
      console.error('Error saving tip response:', error);
    }
  };

  const handleTipResponse = async (response: string) => {
    await saveTipResponse(response);
    setShowTipResponsePopup(false);
    setCurrentTipText('');
    setCurrentTipKey('');
  };

  const startFreePractice = async () => {
    setShowFreePractice(true);
    setFreePracticeStartTime(null); // Don't set start time until category is selected
    setFreePracticeElapsed(0);
    setFreePracticeRunning(false); // Don't start timer until category is selected
    setCompletedReminders(new Set()); // Reset completed reminders
    setFreePracticeGoal(null); // Reset to user's default goal
    
    // Load quotes and messages for the carousel
    const targetStudentId = isTeacherMode ? viewingStudentId : currentUser?.uid;
    if (targetStudentId) {
      await loadQuotesAndMessages(targetStudentId);
    }
    
    // Reset all category timers to 0
    setCategoryTimers({
      scales: { isActive: false, elapsed: 0 },
      exercises: { isActive: false, elapsed: 0 },
      studies: { isActive: false, elapsed: 0 },
      repertoire: { isActive: false, elapsed: 0 },
      ensemble: { isActive: false, elapsed: 0 }
    });
    
    // Reset goal reached state for new session
    setGoalReached(false);
  };

  const pauseFreePractice = () => {
    setFreePracticeRunning(false);
    // Also pause all category timers
    setCategoryTimers(prev => {
      const newTimers = { ...prev };
      Object.keys(newTimers).forEach(key => {
        newTimers[key as keyof typeof newTimers] = {
          ...newTimers[key as keyof typeof newTimers],
          isActive: false
        };
      });
      return newTimers;
    });
  };

  const resumeFreePractice = () => {
    setFreePracticeRunning(true);
    // Don't automatically resume category timers - let user click a category button
  };

  const finishFreePractice = async () => {
    if (!currentUser) return;

    // Only save session data if there was actual practice time
    if (freePracticeStartTime && freePracticeElapsed > 0) {
      try {
        // Calculate total practice time
        const totalMinutes = Math.floor(freePracticeElapsed / 60);
        
        // Create actual durations object from category timers
        const actualDurations: Record<string, number> = {};
        Object.entries(categoryTimers).forEach(([category, timer]) => {
          if (timer.elapsed > 0) {
            // Convert seconds to minutes (with decimal precision for short sessions)
            actualDurations[`free_${category}`] = Math.round((timer.elapsed / 60) * 100) / 100;
          }
        });

        // Create session data
        const sessionData = {
          studentId: currentUser.uid,
          lessonId: 'free_practice',
          plannedTotalMinutes: currentUser.practiceSessionDuration || 30,
          plannedDurations: {},
          actualDurations: actualDurations,
          completed: true,
          startAt: freePracticeStartTime,
          endAt: new Date(),
          durationSec: freePracticeElapsed,
          taskCounts: {},
          createdAt: new Date(),
          sessionType: 'free_practice',
          categoryTimers: categoryTimers
        };

        // Save to Firestore
        const { addDoc, collection } = await import('firebase/firestore');
        await addDoc(collection(db, 'practiceSessions'), sessionData);

        // Free practice session saved
        
        // Refresh weekly progress data to update Today's Practice and This Week totals
        loadWeeklyProgress();
      } catch (error) {
        console.error('Error saving free practice session:', error);
      }
    }

    // Check if there was actual practice time before resetting state
    const hadActualPractice = freePracticeStartTime && freePracticeElapsed > 0;
    
    // Reset state
    setShowFreePractice(false);
    setFreePracticeRunning(false);
    setFreePracticeStartTime(null);
    setFreePracticeElapsed(0);
    setCompletedReminders(new Set());
    setGoalReached(false);
    
    // Only show prompt to set up next session if there was actual practice time
    if (hadActualPractice) {
      setShowNextSessionPrompt(true);
    }
    
    // Reset category timers
    setCategoryTimers({
      scales: { isActive: false, elapsed: 0 },
      exercises: { isActive: false, elapsed: 0 },
      studies: { isActive: false, elapsed: 0 },
      repertoire: { isActive: false, elapsed: 0 },
      ensemble: { isActive: false, elapsed: 0 }
    });
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Render a section based on section name
  const renderSection = (sectionName: 'Technique' | 'Repertoire' | 'Ensemble') => {
    
    if (sectionName === 'Technique') {
      return (
        <div
          key="Technique"
          draggable={!sessionActive}
          onDragStart={(e) => handleSectionDragStart(e, 'Technique')}
          onDragOver={handleSectionDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleSectionDrop(e, 'Technique')}
          onDragEnd={handleSectionDragEnd}
          className={`bg-white border border-blue-200 shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
            draggedSection === 'Technique' ? 'opacity-50 scale-95' : ''
          } ${!sessionActive ? 'cursor-move' : ''}`}
        >
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-5 sm:px-7 py-4">
            <h4 className="text-lg sm:text-xl font-bold text-white flex items-center">
              <span className="w-3 h-3 bg-white rounded-full mr-3"></span>
              Technique
              {!sessionActive && (
                <div className="ml-auto cursor-move text-white hover:text-blue-100 transition-colors duration-200" title="Drag to reorder sections">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
              )}
            </h4>
          </div>
          <div className="p-5 sm:p-7">
          
          {/* Dynamic Technique Sub-sections */}
          {techniqueSubsectionOrder.map((subsection, index) => {
            const isLast = index === techniqueSubsectionOrder.length - 1;
            const subsectionConfig = {
              Scales: {
                color: 'blue',
                icon: '♫',
                title: 'Scales',
                bgColor: 'bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-blue-100',
                titleColor: 'text-blue-800',
                iconBg: 'bg-gradient-to-r from-blue-400 to-blue-500',
                emptyColor: 'text-blue-600',
                emptyBg: 'bg-blue-50'
              },
              Exercises: {
                color: 'indigo',
                icon: '💪',
                title: 'Exercises',
                bgColor: 'bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-indigo-100',
                titleColor: 'text-indigo-800',
                iconBg: 'bg-gradient-to-r from-indigo-400 to-indigo-500',
                emptyColor: 'text-indigo-600',
                emptyBg: 'bg-indigo-50'
              },
              Studies: {
                color: 'cyan',
                icon: '📚',
                title: 'Studies',
                bgColor: 'bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-cyan-100',
                titleColor: 'text-cyan-800',
                iconBg: 'bg-gradient-to-r from-cyan-400 to-cyan-500',
                emptyColor: 'text-cyan-600',
                emptyBg: 'bg-cyan-50'
              }
            };
            
            const config = subsectionConfig[subsection];
            
            return (
              <div
                key={subsection}
                draggable={!sessionActive}
                onDragStart={(e) => handleSubsectionDragStart(e, subsection)}
                onDragOver={handleSubsectionDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleSubsectionDrop(e, subsection)}
                onDragEnd={handleSubsectionDragEnd}
                className={`${config.bgColor} ${isLast ? 'mb-2' : 'mb-5 sm:mb-7'} transition-all duration-300 hover:shadow-lg hover:scale-[1.01] ${
                  draggedSubsection === subsection ? 'opacity-50 scale-95' : ''
                } ${!sessionActive ? 'cursor-move' : ''}`}
              >
                <h5 className={`text-sm sm:text-base font-semibold ${config.titleColor} mb-3 flex items-center`}>
                  <div className={`w-5 h-5 ${config.iconBg} rounded-full mr-2 flex items-center justify-center`}>
                    <span className="text-white text-xs">{config.icon}</span>
                  </div>
                  {config.title}
                  {!sessionActive && (
                    <div className="ml-auto cursor-move text-gray-400 hover:text-gray-600 transition-colors duration-200" title="Drag to reorder subsections">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                  )}
                </h5>
                <div className="space-y-2">
                  {getUnifiedTasks()
                    .filter(task => task.category === subsection)
                    .map((task, taskIndex) => renderTaskItem(task, taskIndex))}
                  {getUnifiedTasks().filter(task => task.category === subsection).length === 0 && (
                    <div className={`${config.emptyColor} text-sm pl-6 italic ${config.emptyBg} rounded-md p-2`}>No {config.title.toLowerCase()} tasks yet</div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      );
    }
    
    if (sectionName === 'Repertoire') {
      return (
        <div
          key="Repertoire"
          draggable={!sessionActive}
          onDragStart={(e) => handleSectionDragStart(e, 'Repertoire')}
          onDragOver={handleSectionDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleSectionDrop(e, 'Repertoire')}
          onDragEnd={handleSectionDragEnd}
          className={`bg-white border border-green-200 shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
            draggedSection === 'Repertoire' ? 'opacity-50 scale-95' : ''
          } ${!sessionActive ? 'cursor-move' : ''}`}
        >
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-5 sm:px-7 py-4">
            <h4 className="text-lg sm:text-xl font-bold text-white flex items-center">
              <span className="w-3 h-3 bg-white rounded-full mr-3"></span>
              Repertoire
              {!sessionActive && (
                <div className="ml-auto cursor-move text-white hover:text-green-100 transition-colors duration-200" title="Drag to reorder sections">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
              )}
            </h4>
          </div>
          <div className="p-5 sm:p-7">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-green-100">
            <div className="space-y-2">
              {getUnifiedTasks()
                .filter(task => task.category === 'Repertoire')
                .map((task, index) => renderTaskItem(task, index))}
              {getUnifiedTasks().filter(task => task.category === 'Repertoire').length === 0 && (
                <div className="text-green-600 text-sm italic bg-green-50 rounded-md p-3 text-center">
                  <div className="text-2xl mb-2">🎼</div>
                  No repertoire tasks yet
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      );
    }
    
    if (sectionName === 'Ensemble') {
      return (
        <div
          key="Ensemble"
          draggable={!sessionActive}
          onDragStart={(e) => handleSectionDragStart(e, 'Ensemble')}
          onDragOver={handleSectionDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleSectionDrop(e, 'Ensemble')}
          onDragEnd={handleSectionDragEnd}
          className={`bg-white border border-purple-200 shadow-lg rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
            draggedSection === 'Ensemble' ? 'opacity-50 scale-95' : ''
          } ${!sessionActive ? 'cursor-move' : ''}`}
        >
          <div className="bg-gradient-to-r from-purple-500 to-violet-600 px-5 sm:px-7 py-4">
            <h4 className="text-lg sm:text-xl font-bold text-white flex items-center">
              <span className="w-3 h-3 bg-white rounded-full mr-3"></span>
              Ensemble
              {!sessionActive && (
                <div className="ml-auto cursor-move text-white hover:text-purple-100 transition-colors duration-200" title="Drag to reorder sections">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>
              )}
            </h4>
          </div>
          <div className="p-5 sm:p-7">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-purple-100">
            <div className="space-y-2">
              {getUnifiedTasks()
                .filter(task => task.category === 'Ensemble')
                .map((task, index) => renderTaskItem(task, index))}
              {getUnifiedTasks().filter(task => task.category === 'Ensemble').length === 0 && (
                <div className="text-purple-600 text-sm italic bg-purple-50 rounded-md p-3 text-center">
                  <div className="text-2xl mb-2">🎪</div>
                  No ensemble tasks yet
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Render individual task item
  const renderTaskItem = (task: Task & { source: 'custom'; originalIndex: number }, index: number) => {
    const taskId = getTaskId(task);
    
    return (
      <div
        key={`${task.source}-${task.originalIndex}`}
        draggable={!sessionActive}
        onDragStart={(e) => handleDragStart(e, task)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, task)}
        onDragEnd={handleDragEnd}
        className={`shadow rounded-lg p-3 sm:p-4 transition-all duration-200 min-h-[60px] ${
          planningComplete 
            ? (sessionActive && !sessionPaused ? 'cursor-pointer hover:shadow-lg hover:scale-105 active:scale-95' : 'opacity-50 cursor-not-allowed')
            : ''
        } ${
          taskTimers[taskId]?.completed ? 'bg-green-50 border-2 border-green-300' : 'bg-white'
        } ${
          draggedTaskIndex === customTasks.findIndex(t => t.id === task.id) ? 'ring-2 ring-blue-400 opacity-50 scale-95 rotate-2' : ''
        }`}
        onClick={() => planningComplete && handleTaskClick(index, true)}
      >
        {/* Mobile-first layout: Full width content area */}
        <div className="w-full">
          {editingTask === (task.id || `${task.source}-${task.originalIndex}`) ? (
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <select
                value={editTaskCategory}
                onChange={(e) => setEditTaskCategory(e.target.value as 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble')}
                className="text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value="Scales">Scales</option>
                <option value="Exercises">Exercises</option>
                <option value="Studies">Studies</option>
                <option value="Repertoire">Repertoire</option>
                <option value="Ensemble">Ensemble</option>
              </select>
              <input
                type="text"
                value={editTaskTitle}
                onChange={(e) => setEditTaskTitle(e.target.value)}
                maxLength={35}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
              />
              <div className="flex space-x-2">
                <button
                  onClick={() => updateTask(task)}
                  className="text-green-600 hover:text-green-800 p-1"
                  title="Save changes"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={cancelEditing}
                  className="text-gray-600 hover:text-gray-800 p-1"
                  title="Cancel editing"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Main content area - full width on mobile */}
              <div className="w-full">
                {/* Top row: Drag handle, category, title, completion status, task type */}
                <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
                  {!sessionActive && (
                    <div className={`cursor-move ${planningComplete ? 'text-blue-500 hover:text-blue-600' : 'text-gray-400'} transition-colors duration-200 flex-shrink-0`} title="Drag to reorder">
                      <svg className={`h-4 w-4 sm:h-5 sm:w-5 ${planningComplete ? 'animate-pulse' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                  )}
                  {editingCategory === (task.id || `${task.source}-${task.originalIndex}`) ? (
                    <div className="flex items-center space-x-2">
                      <select
                        value={quickEditCategory}
                        onChange={(e) => setQuickEditCategory(e.target.value as 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble')}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="Scales">Scales</option>
                        <option value="Exercises">Exercises</option>
                        <option value="Studies">Studies</option>
                        <option value="Repertoire">Repertoire</option>
                        <option value="Ensemble">Ensemble</option>
                      </select>
                      <button
                        onClick={() => updateTaskCategory(task)}
                        className="text-green-600 hover:text-green-800 p-1"
                        title="Save category"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={cancelCategoryEdit}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title="Cancel editing"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEditingCategory(task)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${
                        task.category === 'Scales'
                          ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          : task.category === 'Exercises'
                          ? 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                          : task.category === 'Studies'
                          ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                          : task.category === 'Repertoire'
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-pink-100 text-pink-800 hover:bg-pink-200'
                      }`}
                      title="Click to edit category"
                    >
                      {task.category}
                    </button>
                  )}
                  
                  <span className="text-gray-900 font-medium text-sm sm:text-base flex-1 min-w-0" title={task.title}>
                    {task.title.length > 35 ? `${task.title.substring(0, 35)}...` : task.title}
                  </span>
                  
                  {taskTimers[taskId]?.completed && (
                    <span className="text-green-600 text-lg flex-shrink-0">✓</span>
                  )}
                  
                  <span className={`text-xs text-gray-500 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded flex-shrink-0 ${
                    task.createdBy === 'teacher'
                      ? 'bg-purple-100 text-purple-600'
                      : 'bg-orange-100 text-orange-500'
                  }`}>
                    <span className="hidden sm:inline">{task.createdBy === 'teacher' ? 'Teacher Task' : 'Student Task'}</span>
                    <span className="sm:hidden">{task.createdBy === 'teacher' ? 'T' : 'S'}</span>
                  </span>
                </div>
              
                {/* Practice Reminders Section - Full width */}
                {task.practiceReminders && task.practiceReminders.length > 0 && (
                  <div className="mt-3 w-full">
                    <div className="mb-2">
                      <div className="text-xs font-medium text-gray-600 flex items-center space-x-2">
                        <span>Task Goals ({task.practiceReminders.length}):</span>
                        <button
                          onClick={() => toggleTaskExpansion(task.id!)}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                        >
                          <span>{expandedTasks.has(task.id!) ? 'Collapse' : 'Expand'}</span>
                          <svg 
                            className={`w-3 h-3 transition-transform ${expandedTasks.has(task.id!) ? 'rotate-180' : ''}`} 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  {expandedTasks.has(task.id!) && (
                    <div className="space-y-1">
                      {task.practiceReminders.map((reminder, reminderIndex) => {
                        // Handle both string and structured object formats
                        const reminderText = typeof reminder === 'string' ? reminder : reminder.content || '';
                        const hasMetronome = typeof reminder === 'object' && reminder.hasMetronome;
                        const tempo = typeof reminder === 'object' && reminder.tempo;
                        return (
                      <div key={reminderIndex} className="group">
                        {editingReminder === `${task.id}-${reminderIndex}` ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editReminderText}
                              onChange={(e) => setEditReminderText(e.target.value)}
                              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
                              autoFocus
                            />
                            <button
                              onClick={() => editPracticeReminder(task.id!, reminderIndex)}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Save reminder"
                            >
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setEditingReminder(null);
                                setEditReminderText('');
                              }}
                              className="text-gray-600 hover:text-gray-800 p-1"
                              title="Cancel editing"
                            >
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-700">•</span>
                            {hasMetronome && (
                              <svg className="w-3 h-3 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.792L5.617 15H3a1 1 0 01-1-1V6a1 1 0 011-1h2.617l2.766-1.792a1 1 0 011-.332zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                              </svg>
                            )}
                            <span className="text-xs text-gray-700">{reminderText}</span>
                            {hasMetronome && tempo && (
                              <span className="text-xs text-blue-600 font-medium">
                                ({tempo} BPM)
                              </span>
                            )}
                            {!sessionActive && (
                              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    setEditingReminder(`${task.id}-${reminderIndex}`);
                                    setEditReminderText(reminderText);
                                  }}
                                  className="text-indigo-600 hover:text-indigo-800 p-1"
                                  title="Edit reminder"
                                >
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => deletePracticeReminder(task.id!, reminderIndex)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Delete reminder"
                                >
                                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                        );
                      })}
                    </div>
                  )}
                  {!expandedTasks.has(task.id!) && (
                    <div className="text-xs text-gray-500 italic">
                      Click "Expand" to view {task.practiceReminders.length} goal{task.practiceReminders.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            
            {/* Bottom section: Action buttons and duration input - Mobile: stacked, Desktop: inline */}
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Duration input - always visible */}
              <div className="flex items-center space-x-1 sm:space-x-2">
                <input
                  type="number"
                  value={taskDurations[taskId] || 0}
                  onChange={(e) => updateTaskDuration(taskId, parseInt(e.target.value) || 0)}
                  min="0"
                  max="60"
                  className="w-12 sm:w-16 px-1 sm:px-2 py-1 border border-gray-300 rounded text-center text-xs sm:text-sm"
                  placeholder="0"
                  title={getTaskTooltip()}
                />
                <span className="text-xs sm:text-sm text-gray-500">min</span>
              </div>
              
              {/* Action buttons - Mobile: full width, Desktop: inline */}
              <div className="flex items-center justify-end space-x-2">
                {!sessionActive && !planningComplete && (
                  <>
                    <button
                      onClick={() => startEditingTask(task)}
                      className="text-indigo-600 hover:text-indigo-800 p-1"
                      title="Edit task"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {task.createdBy !== 'teacher' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (task.source === 'custom' && task.id) {
                            removeCustomTask(task.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete task"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
              
              

              {/* Timer and controls - only show if practice is active or in extended practice mode */}
              {(practiceMode || isExtendPracticeMode || sessionActive) && (
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  {/* Simple timer with task title */}
                  {simpleTimerRunning && (
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-700 mb-1">
                        {task?.title || 'Current Task'}
                      </div>
                      <div className="text-lg font-mono font-bold text-indigo-600">
                        {formatTime(simpleTaskTimes[taskId] || 0)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {taskDurations[taskId] ? `of ${taskDurations[taskId]}m` : 'No limit'}
                      </div>
                    </div>
                  )}
                  
                  {/* Progress bar - real-time calculation */}
                  {taskDurations[taskId] > 0 && (
                    <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-500 origin-left"
                        style={{ 
                          width: '100%',
                          transform: `scaleX(${Math.min(1, (simpleTaskTimes[taskId] || 0) / (taskDurations[taskId] * 60))})`
                        }}
                      ></div>
                    </div>
                  )}
                  
                  {/* Metronome */}
                  <CompactMetronome 
                    onStop={() => {
                      // Close the metronome when it stops
                      setExpandedMetronomes(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(taskId);
                        return newSet;
                      });
                    }}
                  />
                  
                  {/* Start/Stop button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTaskTimer(taskId);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm ${
                      taskTimers[taskId]?.isRunning
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {taskTimers[taskId]?.isRunning ? 'Stop' : 'Start'}
                  </button>
                  
                </div>
              )}
            </div>
            </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Planning flow functions

  const updateTaskDuration = (taskId: string, minutes: number) => {
    setTaskDurations(prev => {
      const newDurations = {
        ...prev,
        [taskId]: minutes
      };
      
      // Save to preferences if minutes > 0
      if (minutes > 0) {
        const newPreferences = {
          ...taskTimePreferences,
          [taskId]: minutes
        };
        saveTaskTimePreferences(newPreferences);
      }
      
      return newDurations;
    });
  };

  const getTaskId = (task: Task & { source: 'custom'; originalIndex: number }): string => {
    return `custom_${task.id}`;
  };

  const getAllTasks = (): Task[] => {
    return [...availableTasks, ...customTasks];
  };

  const getUnifiedTasks = (): (Task & { source: 'custom'; originalIndex: number })[] => {
    // Only show custom tasks now - no lesson tasks
    const customTasksWithSource = customTasks.map((task, index) => ({ ...task, source: 'custom' as const, originalIndex: index }));
    return customTasksWithSource;
  };

  const getGuidedTasks = (): Task[] => {
    // Only use custom tasks for guided practice, preserving visual order within sections
    const tasksWithDuration = customTasks.filter((task) => {
      const taskId = `custom_${task.id}`;
      return (taskDurations[taskId] || 0) > 0;
    });


    // Sort tasks by section order (Technique first, then Repertoire, then Ensemble)
    // but preserve the original order within each section
    const sortedTasks = tasksWithDuration.sort((a, b) => {
      const aSection = a.category === 'Scales' || a.category === 'Exercises' || a.category === 'Studies' ? 'Technique' : a.category;
      const bSection = b.category === 'Scales' || b.category === 'Exercises' || b.category === 'Studies' ? 'Technique' : b.category;
      
      const aIndex = sectionOrder.indexOf(aSection as 'Technique' | 'Repertoire' | 'Ensemble');
      const bIndex = sectionOrder.indexOf(bSection as 'Technique' | 'Repertoire' | 'Ensemble');
      
      // If tasks are in different sections, sort by section order
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
      
      // If tasks are in the same section, sort by subsection order for Technique section
      if (aSection === 'Technique' && bSection === 'Technique') {
        const aSubsectionIndex = techniqueSubsectionOrder.indexOf(a.category as 'Scales' | 'Exercises' | 'Studies');
        const bSubsectionIndex = techniqueSubsectionOrder.indexOf(b.category as 'Scales' | 'Exercises' | 'Studies');
        
        // If tasks are in different subsections, sort by subsection order
        if (aSubsectionIndex !== bSubsectionIndex) {
          return aSubsectionIndex - bSubsectionIndex;
        }
      }
      
      // If tasks are in the same subsection, preserve their original order in customTasks array
      const aOriginalIndex = customTasks.findIndex(task => task.id === a.id);
      const bOriginalIndex = customTasks.findIndex(task => task.id === b.id);
      return aOriginalIndex - bOriginalIndex;
    });
    
    return sortedTasks;
  };

  // Memoized task lookup for better performance
  const getTaskByTaskId = useCallback((taskId: string): Task | null => {
    if (taskId.startsWith('custom_')) {
      const customTaskId = taskId.replace('custom_', '');
      return customTasks.find(task => task.id === customTaskId) || null;
    }
    return null;
  }, [customTasks]);

  // Drag and drop handlers for task reordering
  const handleDragStart = (e: React.DragEvent, task: Task & { source: 'custom'; originalIndex: number }) => {
    // Find the actual index in the customTasks array
    const actualIndex = customTasks.findIndex(t => t.id === task.id);
    setDraggedTaskIndex(actualIndex);
    setDraggedTaskType('custom');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id || '');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback for drop zones
    const target = e.currentTarget as HTMLElement;
    target.classList.add('ring-2', 'ring-green-400', 'bg-green-50', 'scale-105');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Remove visual feedback when leaving drop zone
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('ring-2', 'ring-green-400', 'bg-green-50', 'scale-105');
  };

  const handleDrop = (e: React.DragEvent, dropTask: Task & { source: 'custom'; originalIndex: number }) => {
    e.preventDefault();
    
    if (draggedTaskIndex === null) return;
    
    // Find the actual index in the customTasks array
    const dropIndex = customTasks.findIndex(t => t.id === dropTask.id);
    
    // Don't allow dropping on the same position
    if (draggedTaskIndex === dropIndex) {
      setDraggedTaskIndex(null);
      setDraggedTaskType(null);
      return;
    }

    // Create a new array with the reordered tasks
    const newCustomTasks = [...customTasks];
    const draggedTask = newCustomTasks[draggedTaskIndex];
    
    // Remove the dragged task from its original position
    newCustomTasks.splice(draggedTaskIndex, 1);
    
    // Calculate the correct insertion index
    // When dragging down (dropIndex > draggedTaskIndex), we want to insert AFTER the target
    // When dragging up (dropIndex < draggedTaskIndex), we want to insert AT the target position
    let insertionIndex = dropIndex;
    if (dropIndex > draggedTaskIndex) {
      // When dragging down, we want to insert after the target
      // Since we removed an item above, we need to insert at dropIndex (not dropIndex - 1)
      insertionIndex = dropIndex;
    } else {
      // When dragging up, insert at the target position
      insertionIndex = dropIndex;
    }
    
    // Insert the dragged task at the calculated position
    newCustomTasks.splice(insertionIndex, 0, draggedTask);

    // Update custom tasks array
    setCustomTasks(newCustomTasks);
    setDraggedTaskIndex(null);
    setDraggedTaskType(null);
    
    // Remove visual feedback
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('ring-2', 'ring-green-400', 'bg-green-50', 'scale-105');
    
    // Play success sound
    playTickSound();
  };

  const handleDragEnd = () => {
    setDraggedTaskIndex(null);
    setDraggedTaskType(null);
  };

  // Section drag and drop handlers
  const handleSectionDragStart = (e: React.DragEvent, sectionName: string) => {
    setDraggedSection(sectionName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e: React.DragEvent, targetSection: string) => {
    e.preventDefault();
    
    if (!draggedSection || draggedSection === targetSection) {
      setDraggedSection(null);
      return;
    }

    const newOrder = [...sectionOrder];
    const draggedIndex = newOrder.indexOf(draggedSection as 'Technique' | 'Repertoire' | 'Ensemble');
    const targetIndex = newOrder.indexOf(targetSection as 'Technique' | 'Repertoire' | 'Ensemble');
    
    // Remove dragged section and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedSection as 'Technique' | 'Repertoire' | 'Ensemble');
    
    setSectionOrder(newOrder);
    setDraggedSection(null);
  };

  const handleSectionDragEnd = () => {
    setDraggedSection(null);
  };

  // Subsection drag and drop handlers
  const handleSubsectionDragStart = (e: React.DragEvent, subsectionName: string) => {
    setDraggedSubsection(subsectionName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSubsectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSubsectionDrop = (e: React.DragEvent, targetSubsection: string) => {
    e.preventDefault();
    
    if (!draggedSubsection || draggedSubsection === targetSubsection) {
      setDraggedSubsection(null);
      return;
    }

    const newOrder = [...techniqueSubsectionOrder];
    const draggedIndex = newOrder.indexOf(draggedSubsection as 'Scales' | 'Exercises' | 'Studies');
    const targetIndex = newOrder.indexOf(targetSubsection as 'Scales' | 'Exercises' | 'Studies');
    
    // Remove dragged subsection and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedSubsection as 'Scales' | 'Exercises' | 'Studies');
    
    setTechniqueSubsectionOrder(newOrder);
    setDraggedSubsection(null);
  };

  const handleSubsectionDragEnd = () => {
    setDraggedSubsection(null);
  };

  const getTotalPlannedMinutes = (): number => {
    return Object.values(taskDurations).reduce((sum, minutes) => sum + minutes, 0);
  };

  const getRemainingMinutes = (): number => {
    // In teacher mode, use the student's daily practice goal
    const targetTotalMinutes = isTeacherMode && viewingStudent?.practiceSessionDuration 
      ? viewingStudent.practiceSessionDuration 
      : plannedTotalMinutes;
    return targetTotalMinutes - getTotalPlannedMinutes();
  };

  const getTaskTooltip = (): string => {
    const remaining = getRemainingMinutes();
    const targetTotalMinutes = isTeacherMode && viewingStudent?.practiceSessionDuration 
      ? viewingStudent.practiceSessionDuration 
      : plannedTotalMinutes;
    
    let tooltip = `Remaining minutes to assign: ${remaining}`;
    
    if (isTeacherMode && viewingStudent?.practiceSessionDuration) {
      tooltip += ` (Student's goal: ${viewingStudent.practiceSessionDuration} min)`;
    } else if (currentUser?.practiceSessionDuration) {
      tooltip += ` (Your daily goal: ${currentUser.practiceSessionDuration} min)`;
    }
    
    if (remaining > 0) {
      tooltip += `\n\n💡 Tip: Allocate at least ${targetTotalMinutes} minutes to start your practice session!`;
    } else {
      tooltip += `\n\n🎉 Perfect! You're ready to start practicing.`;
    }
    
    return tooltip;
  };

  const canStartPractice = (): boolean => {
    // Use Today's Session Goal as the target
    const todaySessionGoal = todaySessionDetails?.goal || plannedTotalMinutes || currentUser?.practiceSessionDuration || 30;
    const targetTotalMinutes = isTeacherMode && viewingStudent?.practiceSessionDuration 
      ? (todaySessionDetails?.goal || viewingStudent.practiceSessionDuration || 30)
      : todaySessionGoal;
    return getTotalPlannedMinutes() >= targetTotalMinutes && targetTotalMinutes > 0;
  };

  // Practice mode functions
  const startGuidedPractice = async () => {
    if (!canStartPractice()) {
      return;
    }
    
    
    const guidedTasks = getGuidedTasks();
    
    // Get the first task to show tips for
    const firstTask = guidedTasks.find(task => {
      // Check both the task ID and the custom_ prefixed version
      const duration = task.id ? (taskDurations[task.id] || taskDurations[`custom_${task.id}`]) : 0;
      return task.id && duration > 0;
    });
    
    if (firstTask) {
      setCurrentTaskForTips(firstTask);
      setShowTipsPopup(true);
    } else {
      // No tasks with time allocated, start practice directly
      await actuallyStartPractice();
    }
  };

  const actuallyStartPractice = async () => {
    if (!canStartPractice()) {
      return;
    }
    
    try {
      const sessionData = {
        studentId: currentUser!.uid,
        lessonId: latestLesson?.lessonId || '', // Use empty string for custom practice sessions
        plannedTotalMinutes,
        plannedDurations: taskDurations,
        actualDurations: {},
        completed: false,
        startAt: serverTimestamp(),
        taskCounts: {},
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'practiceSessions'), sessionData);
      
      const session: PracticeSession = {
        id: docRef.id,
        studentId: sessionData.studentId,
        lessonId: sessionData.lessonId,
        plannedTotalMinutes: sessionData.plannedTotalMinutes,
        plannedDurations: sessionData.plannedDurations,
        actualDurations: sessionData.actualDurations,
        completed: sessionData.completed,
        taskCounts: sessionData.taskCounts,
        startAt: new Date(),
        createdAt: new Date()
      };
      
      setCurrentSession(session);
      setPracticeMode(true);
      setCurrentTaskIndex(0);
      setCurrentTaskStartTime(new Date());
      setCurrentTaskElapsed(0);
      setPlanningComplete(true);
      setCelebratedTasks(new Set()); // Reset celebrations for new session
      setCurrentCelebratedTaskId(null); // Reset current celebrated task
      celebratedTasksRef.current.clear(); // Reset ref immediately
      
      // Initialize practice state
      
      // Initialize task timers with proper structure for all tasks
      const unifiedTasks = getUnifiedTasks();
      const initialTaskTimers: Record<string, {
        isRunning: boolean;
        elapsed: number;
        startTime: Date | null;
        completed: boolean;
      }> = {};
      
      unifiedTasks.forEach(task => {
        // Use getTaskId to get the correct task ID format
        const taskId = getTaskId(task);
        console.log('Initializing task timer for taskId:', taskId, 'task:', task);
        
        initialTaskTimers[taskId] = {
          isRunning: false,
          elapsed: 0,
          startTime: null,
          completed: false
        };
      });
      
      console.log('Initial task timers:', initialTaskTimers);
      
      // Don't reset taskTimers - preserve existing state
      // setTaskTimers(initialTaskTimers);
      
      // Start the overall timer automatically
      setOverallStartTime(new Date());
      setOverallIsRunning(true);
      
      // Start the first task's timer automatically in guided practice mode
      if (unifiedTasks.length > 0) {
        const firstTask = unifiedTasks[0];
        const firstTaskId = getTaskId(firstTask);
        console.log('Starting first task timer automatically:', firstTaskId);
        
        setTaskTimers(prev => ({
          ...prev,
          [firstTaskId]: {
            ...prev[firstTaskId],
            isRunning: true,
            startTime: new Date()
          }
        }));
      }
      
      // Close tips popup if open
      setShowTipsPopup(false);
      setCurrentTaskForTips(null);
      setShowAddTipsInterface(false);
      setNewTipText('');
      
    } catch (err) {
      console.error('Error starting guided practice:', err);
      setError('Failed to start guided practice session');
    }
  };

  // Tips popup functions
  const handleTipsPopupNo = () => {
    setShowTipsPopup(false);
    setCurrentTaskForTips(null);
    setShowAddTipsInterface(false);
    setNewTipText('');
    
    // If we're already in practice mode, don't restart the session
    if (practiceMode) {
      return;
    }
    
    // Only start a new practice session if we're not already practicing
    actuallyStartPractice();
  };

  const handleTipsPopupYes = () => {
    setShowAddTipsInterface(true);
  };

  const addTipToCurrentTask = async () => {
    if (!currentTaskForTips || !newTipText.trim()) return;


    try {
      const updatedReminders = [
        ...(currentTaskForTips.practiceReminders || []),
        newTipText.trim()
      ];

      // Update the task in the local state
      const updatedTask = {
        ...currentTaskForTips,
        practiceReminders: updatedReminders
      };


      // Update the current task for tips to show the new tip immediately
      setCurrentTaskForTips(updatedTask);

      // Update the task in the customTasks list (this is where all tasks are stored)
      setCustomTasks(prev => {
        const updated = prev.map(task => task.id === currentTaskForTips.id ? updatedTask : task);
        return updated;
      });

      // Save to Firestore to persist the changes
      if (currentTaskForTips.id) {
        const taskRef = doc(db, 'customTasks', currentTaskForTips.id);
        await updateDoc(taskRef, {
          practiceReminders: updatedReminders
        });
      }

      setNewTipText('');
      
    } catch (err) {
      console.error('Error adding tip:', err);
      setError('Failed to add tip');
    }
  };

  const startPracticingCurrentTask = () => {
    setShowTipsPopup(false);
    setCurrentTaskForTips(null);
    setShowAddTipsInterface(false);
    setNewTipText('');
    
    // Start the main timer - only reset if not in repractice mode
    if (!isRepracticeMode) {
      setSimpleTimer(0);
      setSimpleTimerRunning(true);
      
      // Initialize task times for all tasks
      const guidedTasks = getGuidedTasks();
      const initialTaskTimes: Record<string, number> = {};
      guidedTasks.forEach(task => {
        const taskId = task.isCustom ? `custom_${task.id}` : `lesson_${getAllTasks().indexOf(task)}`;
        initialTaskTimes[taskId] = 0;
      });
      setSimpleTaskTimes(initialTaskTimes);
    } else {
      // In repractice mode, just ensure timer is running (don't reset)
      setSimpleTimerRunning(true);
    }
    
    // If we're in extend practice mode, preserve the timer state and start practice
    if (isExtendPracticeMode) {
      // Don't reset the timer - it's already set with the preserved elapsed time
      // Don't reset completed tips in extend practice mode - let user continue where they left off
      setSelectedTips(new Set()); // Reset tip selection
      setHiddenTips(new Set()); // Reset hidden tips
      setHasTipSelectionChanged(false); // Reset selection change flag
      
      // Initialize task timers with proper structure for all tasks
      const guidedTasks = getGuidedTasks();
      const initialTaskTimers: Record<string, {
        isRunning: boolean;
        elapsed: number;
        startTime: Date | null;
        completed: boolean;
      }> = {};
      
      guidedTasks.forEach(task => {
        const taskId = task.isCustom ? `custom_${task.id}` : `lesson_${getAllTasks().indexOf(task)}`;
        const savedElapsed = extendPracticeTaskTimers[taskId] || 0;
        
        initialTaskTimers[taskId] = {
          isRunning: false,
          elapsed: savedElapsed,
          startTime: null,
          completed: savedElapsed > 0 && !!taskDurations[taskId] && savedElapsed >= (taskDurations[taskId] * 60)
        };
      });
      
      // Don't reset taskTimers - preserve existing state
      // setTaskTimers(initialTaskTimers);
      
      // Start practice mode with preserved timer
      setPracticeMode(true);
      setSessionActive(true);
      setOverallIsRunning(true);
      // Don't reset overall start time - it's already set with the preserved elapsed time
      
      // Start the individual task timer for the current task
      const currentTask = getGuidedTasks()[currentTaskIndex];
      if (currentTask) {
        const currentTaskId = currentTask.isCustom ? `custom_${currentTask.id}` : `lesson_${getAllTasks().indexOf(currentTask)}`;
        setTaskTimers(prev => ({
          ...prev,
          [currentTaskId]: {
            ...prev[currentTaskId],
            isRunning: true,
            startTime: new Date()
          }
        }));
      }
      
      setExtendPracticeOverallTimer(0);
      setPracticeMode(practiceMode);
      setSessionActive(sessionActive);
      setSessionPaused(sessionPaused);
      return;
    }
    
    // If we're in re-practice mode, only reset completed tips and selection change flag
    // Don't reset the timer - let it continue from where it left off
    if (isRepracticeMode) {
      setCompletedReminders(new Set()); // Reset completed tips
      setHasTipSelectionChanged(false); // Reset selection change flag
      setIsRepracticeMode(false); // Reset re-practice mode
      return;
    }
    
    // If we're already in practice mode, don't restart the session
    if (practiceMode) {
      return;
    }
    
    // Only start a new practice session if we're not already practicing
    actuallyStartPractice();
  };

  // Tip completion functions
  const toggleTipCompletion = (taskId: string, tipIndex: number) => {
    const tipKey = `${taskId}-${tipIndex}`;
    setCompletedTips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tipKey)) {
        newSet.delete(tipKey);
      } else {
        newSet.add(tipKey);
      }
      return newSet;
    });
  };

  const getCompletedTipsForTask = (taskId: string): number => {
    const currentTask = getUnifiedTasks().find(task => task.id === taskId);
    if (!currentTask || !currentTask.practiceReminders) return 0;
    
    let completedCount = 0;
    for (let i = 0; i < currentTask.practiceReminders.length; i++) {
      const tipKey = `${taskId}-${i}`;
      if (completedTips.has(tipKey)) {
        completedCount++;
      }
    }
    return completedCount;
  };

  const areAllTipsCompleted = (taskId: string): boolean => {
    const currentTask = getUnifiedTasks().find(task => task.id === taskId);
    if (!currentTask || !currentTask.practiceReminders) return true;
    
    // Only check visible tips (not hidden ones)
    const visibleTips = getFilteredTipsForTask(currentTask);
    if (visibleTips.length === 0) return true; // No visible tips to complete
    
    let completedCount = 0;
    for (let i = 0; i < currentTask.practiceReminders.length; i++) {
      const tipKey = `${taskId}-${i}`;
      // Only count visible tips
      if (!hiddenTips.has(tipKey)) {
        const reminder = currentTask.practiceReminders[i];
        const reminderText = typeof reminder === 'string' ? reminder : reminder.content || '';
        if (completedReminders.has(reminderText)) {
          completedCount++;
        }
      }
    }
    return completedCount === visibleTips.length;
  };

  const resetTipsForNewTask = () => {
    setCompletedReminders(new Set());
    setSelectedTips(new Set());
    setHiddenTips(new Set());
    setHasTipSelectionChanged(false);
    setIsRepracticeMode(false);
  };

  // Tip selection functions
  const toggleTipSelection = (taskId: string, tipIndex: number) => {
    const tipKey = `${taskId}-${tipIndex}`;
    
    // Check if we're trying to select a tip with metronome
    const currentTask = customTasks.find(t => t.id === taskId);
    if (currentTask && currentTask.practiceReminders && currentTask.practiceReminders[tipIndex]) {
      const reminder = currentTask.practiceReminders[tipIndex];
      const hasMetronome = typeof reminder === 'object' && reminder.hasMetronome;
      
      if (hasMetronome) {
        // Check if there are already selected tips with metronome
        const hasExistingMetronomeTip = Array.from(selectedTips).some(existingTipKey => {
          const [existingTaskId, existingTipIndex] = existingTipKey.split('-');
          const existingTask = customTasks.find(t => t.id === existingTaskId);
          if (existingTask && existingTask.practiceReminders && existingTask.practiceReminders[parseInt(existingTipIndex)]) {
            const existingReminder = existingTask.practiceReminders[parseInt(existingTipIndex)];
            return typeof existingReminder === 'object' && existingReminder.hasMetronome;
          }
          return false;
        });
        
        if (hasExistingMetronomeTip && !selectedTips.has(tipKey)) {
          // Prevent selection - show error message
          setError('You can only select one goal with metronome tempo at a time! Please deselect the other tempo goal first.');
          return;
        }
      }
    }
    
    setSelectedTips(prev => {
      const newSet = new Set(prev);
      const wasSelected = newSet.has(tipKey);
      
      if (wasSelected) {
        // Deselecting - stop metronome if this was a tempo goal
        newSet.delete(tipKey);
        if (currentTempoGoal && currentTempoGoal.taskId === taskId && currentTempoGoal.tipIndex === tipIndex) {
          setCurrentTempoGoal(null);
          setMetronomeState({isRunning: false, tempo: metronomeState.tempo}); // Update metronome state
          if (metronomeRef.current) {
            metronomeRef.current.stopMetronome();
          }
        }
      } else {
        // Selecting - start metronome if this is a tempo goal
        newSet.add(tipKey);
        
        // Check if this is a tempo goal
        if (currentTask && currentTask.practiceReminders && currentTask.practiceReminders[tipIndex]) {
          const reminder = currentTask.practiceReminders[tipIndex];
          const hasMetronome = typeof reminder === 'object' && reminder.hasMetronome;
          const tempo = typeof reminder === 'object' && reminder.tempo;
          
          if (hasMetronome && tempo) {
            // Reset auto-start flag when new tempo goal is set
            metronomeAutoStarting.current = false;
            metronomeRefSet.current = false;
            // Set the tempo goal first
            setCurrentTempoGoal({ tempo, taskId, tipIndex });
            
            // Update metronome state
            setMetronomeState({isRunning: true, tempo});
            
            // Start metronome with retry mechanism
            const startMetronomeWithRetry = (retryCount = 0) => {
              if (metronomeRef.current) {
                metronomeRef.current.stopMetronome();
                metronomeRef.current.setTempo(tempo);
                setTimeout(() => {
                  if (metronomeRef.current) {
                    metronomeRef.current.startMetronome();
                  }
                }, 100);
              } else if (retryCount < 5) {
                // Retry up to 5 times with increasing delay
                setTimeout(() => startMetronomeWithRetry(retryCount + 1), 200 * (retryCount + 1));
              }
            };
            
            startMetronomeWithRetry();
          }
        }
        
        // Remove from hidden if it was hidden
        setHiddenTips(prevHidden => {
          const newHidden = new Set(prevHidden);
          newHidden.delete(tipKey);
          return newHidden;
        });
      }
      return newSet;
    });
    setHasTipSelectionChanged(true);
  };

  const toggleTipHidden = (taskId: string, tipIndex: number) => {
    const tipKey = `${taskId}-${tipIndex}`;
    
    // Check if this tip has metronome and is currently selected
    const currentTask = customTasks.find(t => t.id === taskId);
    const isCurrentlySelected = selectedTips.has(tipKey);
    const hasMetronome = currentTask && currentTask.practiceReminders && currentTask.practiceReminders[tipIndex] &&
      typeof currentTask.practiceReminders[tipIndex] === 'object' && 
      (currentTask.practiceReminders[tipIndex] as any).hasMetronome;
    
    setHiddenTips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tipKey)) {
        newSet.delete(tipKey);
      } else {
        newSet.add(tipKey);
        // Remove from selected if it was selected
        setSelectedTips(prevSelected => {
          const newSelected = new Set(prevSelected);
          newSelected.delete(tipKey);
          return newSelected;
        });
        
        // Stop metronome if this was a selected tip with metronome
        if (isCurrentlySelected && hasMetronome) {
          if (currentTempoGoal && currentTempoGoal.taskId === taskId && currentTempoGoal.tipIndex === tipIndex) {
            setCurrentTempoGoal(null);
            setMetronomeState({isRunning: false, tempo: metronomeState.tempo}); // Update metronome state
            if (metronomeRef.current) {
              metronomeRef.current.stopMetronome();
            }
          }
        }
      }
      return newSet;
    });
    setHasTipSelectionChanged(true);
  };

  const getTipState = (taskId: string, tipIndex: number) => {
    const tipKey = `${taskId}-${tipIndex}`;
    if (selectedTips.has(tipKey)) return 'selected';
    if (hiddenTips.has(tipKey)) return 'hidden';
    return 'neutral';
  };

  const getFilteredTipsForTask = (task: Task) => {
    if (!task.practiceReminders) return [];
    
    return task.practiceReminders.filter((_, index) => {
      const tipKey = `${task.id}-${index}`;
      return !hiddenTips.has(tipKey);
    });
  };

  const areAllTipsDecided = (task: Task): boolean => {
    if (!task.practiceReminders || task.practiceReminders.length === 0) return true;
    
    for (let i = 0; i < task.practiceReminders.length; i++) {
      const tipKey = `${task.id}-${i}`;
      // Tip is decided if it's either selected or hidden
      if (!selectedTips.has(tipKey) && !hiddenTips.has(tipKey)) {
        return false;
      }
    }
    return true;
  };

  const handleTipsCompletionDialogResume = () => {
    setShowTipsCompletionDialog(false);
    setIsFinishPracticeDialog(false);
    // Stay on current task
  };

  const handleTipsCompletionDialogNext = () => {
    setShowTipsCompletionDialog(false);
    setIsFinishPracticeDialog(false);
    
    if (isFinishPracticeDialog) {
      // If this is a finish practice dialog, finish the practice
      finishGuidedPractice();
      return;
    }
    
    // Force move to next task even with incomplete tips
    const tasks = getGuidedTasks();
    
    if (currentTaskIndex < tasks.length - 1) {
      // Save current task time
      const currentTask = tasks[currentTaskIndex];
      const taskId = currentTask.isCustom ? `custom_${currentTask.id}` : `lesson_${getAllTasks().indexOf(currentTask)}`;
      const taskElapsedSeconds = simpleTaskTimes[taskId] || 0;
      const actualMinutes = Math.floor(taskElapsedSeconds / 60);
      
      // Save task completion data
      
      setCurrentSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          actualDurations: {
            ...prev.actualDurations,
            [taskId]: actualMinutes
          }
        };
      });
      
      // Move to next task
      const nextTaskIndex = currentTaskIndex + 1;
      
      setCurrentTaskIndex(nextTaskIndex);
      
      // Restore elapsed time for the new task if it exists
      const nextTask = tasks[nextTaskIndex];
      const nextTaskId = nextTask.isCustom ? `custom_${nextTask.id}` : `lesson_${getAllTasks().indexOf(nextTask)}`;
      const savedElapsedTime = 0; // No saved time for regular practice
      // Adjust start time to account for already elapsed time
      const now = new Date();
      const adjustedStartTime = new Date(now.getTime() - savedElapsedTime * 1000);
      
      // Set the adjusted start time - the timer effect will calculate the correct elapsed time
      setCurrentTaskStartTime(adjustedStartTime);
      // Don't set currentTaskElapsed here - let the timer effect handle it
      
      // Reset tips for new task and show tips popup
      resetTipsForNewTask();
      if (nextTask) {
        setCurrentTaskForTips(nextTask);
        setShowTipsPopup(true);
      }
    } else {
      finishGuidedPractice();
    }
  };

  // Re-practice dialog handlers
  const handleRepracticeDialogYes = () => {
    setShowRepracticeDialog(false);
    // Stay on current task to practice again with different tips
    setCurrentTaskElapsed(0);
    setCurrentTaskStartTime(new Date());
    setCompletedReminders(new Set()); // Reset completed tips
    setHasTipSelectionChanged(false); // Reset selection change flag
  };

  const handleRepracticeDialogNo = () => {
    setShowRepracticeDialog(false);
    // Move to next task
    const tasks = getGuidedTasks();
    if (currentTaskIndex < tasks.length - 1) {
      // Save current task time
      const currentTask = tasks[currentTaskIndex];
      const taskId = currentTask.isCustom ? `custom_${currentTask.id}` : `lesson_${getAllTasks().indexOf(currentTask)}`;
      const taskElapsedSeconds = simpleTaskTimes[taskId] || 0;
      const actualMinutes = Math.floor(taskElapsedSeconds / 60);
      
      setCurrentSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          actualDurations: {
            ...prev.actualDurations,
            [taskId]: actualMinutes
          }
        };
      });
      
      // Move to next task
      const nextTaskIndex = currentTaskIndex + 1;
      setCurrentTaskIndex(nextTaskIndex);
      setCurrentTaskStartTime(new Date());
      setCurrentTaskElapsed(0);
      
      // Reset tips for new task and show tips popup
      resetTipsForNewTask();
      const nextTask = tasks[nextTaskIndex];
      if (nextTask) {
        setCurrentTaskForTips(nextTask);
        setShowTipsPopup(true);
      }
    } else {
      finishGuidedPractice();
    }
  };

  const nextTask = () => {
    // Stop all metronomes when moving to next task
    stopAllMetronomes();
    
    const tasks = getGuidedTasks();
    const currentTask = tasks[currentTaskIndex];
    
    
    // Check if all tips are completed for current task
    if (currentTask && currentTask.id && !areAllTipsCompleted(currentTask.id)) {
      setShowTipsCompletionDialog(true);
      return;
    }
    
    
    if (currentTaskIndex < tasks.length - 1) {
      // Save current task time
      const taskId = currentTask.isCustom ? `custom_${currentTask.id}` : `lesson_${getAllTasks().indexOf(currentTask)}`;
      const taskElapsedSeconds = simpleTaskTimes[taskId] || 0;
      const actualMinutes = Math.floor(taskElapsedSeconds / 60);
      
      // Save task completion data
      
      setCurrentSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          actualDurations: {
            ...prev.actualDurations,
            [taskId]: actualMinutes
          }
        };
      });
      
      // Move to next task
      const nextTaskIndex = currentTaskIndex + 1;
      
      setCurrentTaskIndex(nextTaskIndex);
      
      // Restore elapsed time for the new task if it exists
      const nextTask = tasks[nextTaskIndex];
      const nextTaskId = nextTask.isCustom ? `custom_${nextTask.id}` : `lesson_${getAllTasks().indexOf(nextTask)}`;
      const savedElapsedTime = 0; // No saved time for regular practice
      // Adjust start time to account for already elapsed time
      const now = new Date();
      const adjustedStartTime = new Date(now.getTime() - savedElapsedTime * 1000);
      
      // Set the adjusted start time - the timer effect will calculate the correct elapsed time
      setCurrentTaskStartTime(adjustedStartTime);
      // Don't set currentTaskElapsed here - let the timer effect handle it
      
      // Reset tips for new task and show tips popup
      resetTipsForNewTask();
      if (nextTask) {
        setCurrentTaskForTips(nextTask);
        setShowTipsPopup(true);
      }
    } else {
      finishGuidedPractice();
    }
  };

  const handleFinishPractice = () => {
    // Stop all metronomes when finishing practice
    stopAllMetronomes();
    
    // Store the current task index, elapsed time, and overall session state for potential return
    setReturnToTaskIndex(currentTaskIndex);
    // Get the current task's elapsed time from simple timer
    const currentTask = getGuidedTasks()[currentTaskIndex];
    const taskId = currentTask ? (currentTask.isCustom ? `custom_${currentTask.id}` : `lesson_${getAllTasks().indexOf(currentTask)}`) : '';
    const currentTaskElapsedTime = simpleTaskTimes[taskId] || 0;
    setReturnToTaskElapsed(currentTaskElapsedTime);
    setReturnToOverallStartTime(overallStartTime);
    setReturnToOverallRemainingTime(overallRemainingTime);
    
    // Show confirmation dialog
    setShowFinishPracticeConfirmation(true);
  };

  const handleReturnToTask = () => {
    setShowFinishPracticeConfirmation(false);
    // User stays on current task, no action needed
  };

  const handleConfirmFinishSession = () => {
    setShowFinishPracticeConfirmation(false);
    
    // Check if daily goal was reached
    const targetUser = isTeacherMode ? viewingStudent : currentUser;
    const dailyGoalMinutes = targetUser?.practiceSessionDuration || 30;
    const sessionDurationMinutes = (currentSession?.durationSec || 0) / 60;
    const goalReached = sessionDurationMinutes >= dailyGoalMinutes;
    
    // Set the daily goal message for the completion window
    setShowDailyGoalMessage(!goalReached);
    
    // Proceed with the normal finish flow
    const tasks = getGuidedTasks();
    const currentTask = tasks[currentTaskIndex];
    
    // Only check tips completion on the final task, and not in extend practice mode
    if (currentTaskIndex === tasks.length - 1 && !isExtendPracticeMode) {
      // Check if all tips are completed for current task (only on last task)
      if (currentTask && currentTask.id && !areAllTipsCompleted(currentTask.id)) {
        setIsFinishPracticeDialog(true);
        setShowTipsCompletionDialog(true);
        return;
      }
    }
    
    // If tips are completed or no tips, finish practice
    finishGuidedPractice();
  };


  const finishGuidedPractice = async () => {
    
    if (!currentSession || !currentTaskStartTime) return;
    
    try {
      // Save final task time
      const tasks = getGuidedTasks();
      const currentTask = tasks[currentTaskIndex];
      const taskId = currentTask.isCustom ? `custom_${currentTask.id}` : `lesson_${getAllTasks().indexOf(currentTask)}`;
      // Use simple timer data instead of currentTaskElapsed
      const taskElapsedSeconds = simpleTaskTimes[taskId] || 0;
      // For extend practice mode, use ceil instead of floor to preserve accuracy for short durations
      const actualMinutes = isExtendPracticeMode ? Math.ceil(taskElapsedSeconds / 60) : Math.floor(taskElapsedSeconds / 60);
      
      const finalActualDurations = {
        ...currentSession.actualDurations,
        [taskId]: actualMinutes
      };
      
      const endTime = new Date();
      const totalDurationSec = Math.floor((endTime.getTime() - currentSession.startAt.getTime()) / 1000);
      
      // Check if this is an extended session (doesn't exist in Firestore yet)
      // Check if this is an extended session for proper Firestore handling
      if (currentSession.id.startsWith('extended_')) {
        // Create a new document for extended sessions
        const sessionData = {
          studentId: currentSession.studentId,
          lessonId: currentSession.lessonId,
          plannedTotalMinutes: currentSession.plannedTotalMinutes,
          plannedDurations: currentSession.plannedDurations,
          actualDurations: finalActualDurations,
          completed: true,
          taskCounts: currentSession.taskCounts,
          startAt: currentSession.startAt,
          endAt: serverTimestamp(),
          durationSec: totalDurationSec,
          createdAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'practiceSessions'), sessionData);
        currentSession.id = docRef.id; // Update the session ID
      } else {
        // Update existing document for regular sessions
        await updateDoc(doc(db, 'practiceSessions', currentSession.id), {
          actualDurations: finalActualDurations,
          completed: true,
          endAt: serverTimestamp(),
          durationSec: totalDurationSec
        });
      }
      
      const completedSession: PracticeSession = {
        ...currentSession,
        actualDurations: finalActualDurations,
        completed: true,
        endAt: endTime,
        durationSec: totalDurationSec
      };
      
      // Stop the overall timer
      setOverallIsRunning(false);
      
      // If we're in extend practice mode, update the session summary with new data
      if (isExtendPracticeMode) {
        setSessionSummary(completedSession);
        // Update the extend practice task timers with the new elapsed time
        setExtendPracticeTaskTimers(prev => ({
          ...prev,
          [taskId]: taskElapsedSeconds
        }));
        // Update the overall timer
        setExtendPracticeOverallTimer(totalDurationSec);
        
        // Restart the overall timer for extended practice mode
        setOverallIsRunning(true);
        // Don't exit extend practice mode, just update the summary
      } else {
        setSessionSummary(completedSession);
        setPracticeMode(false);
        setCurrentSession(null);
        // Stop main timer when practice ends
        setSimpleTimerRunning(false);
      }
      
      // Refresh weekly progress and badges
      refreshWeeklyProgress();
      loadBadges();
      
      // Check if daily goal was reached
      const sessionDurationMinutes = totalDurationSec / 60;
      await checkDailyGoalReached(sessionDurationMinutes);
      
    } catch (err) {
      console.error('Error finishing guided practice:', err);
      setError('Failed to finish guided practice session');
    }
  };

  const resetPlanning = () => {
    setPlanningComplete(false);
    setPracticeMode(false);
    setCurrentTaskIndex(0);
    setCurrentTaskElapsed(0);
    setSessionSummary(null);
    setReturnToTaskIndex(null);
    setReturnToTaskElapsed(null);
    setReturnToOverallStartTime(null);
    setReturnToOverallRemainingTime(null);
    setTaskDurations({});
    setPlannedTotalMinutes(0);
    setTaskTimers({});
    setOverallIsRunning(false);
    setOverallRemainingTime(0);
    setCelebratedTasks(new Set());
    setCurrentCelebratedTaskId(null);
    celebratedTasksRef.current.clear();
  };

  const resetTimers = () => {
    // Reset all task durations to 0
    setTaskDurations({});
    
    // Reset daily practice goal to user's profile setting
    const targetUser = isTeacherMode ? viewingStudent : currentUser;
    const defaultGoal = targetUser?.practiceSessionDuration || 30;
    setPlannedTotalMinutes(defaultGoal);
    setOverallRemainingTime(defaultGoal * 60);
    
    // Don't reset planning state - keep Next Session card visible
    // setPlanningComplete(false); // Commented out to preserve Next Session card
  };

  // Individual task timer functions
  const startOverallTimer = () => {
    setOverallStartTime(new Date());
    setOverallIsRunning(true);
  };

  const stopOverallTimer = () => {
    setOverallIsRunning(false);
  };

  const toggleTaskTimer = (taskId: string) => {
    console.log('toggleTaskTimer called with taskId:', taskId);
    console.log('Current taskTimers state:', taskTimers);
    setTaskTimers(prev => {
      const current = prev[taskId];
      console.log('Current timer for taskId:', current);
      if (!current) {
        console.log('No timer found for taskId:', taskId);
        return prev;
      }

      if (current.isRunning) {
        // Stop the timer
        return {
          ...prev,
          [taskId]: {
            ...current,
            isRunning: false,
            startTime: null
          }
        };
      } else {
        // Start the timer
        const now = new Date();
        return {
          ...prev,
          [taskId]: {
            ...current,
            isRunning: true,
            startTime: now,
            completed: false // Reset completed status when starting
          }
        };
      }
    });

    // Start overall timer if this is the first task being started
    if (!overallIsRunning) {
      startOverallTimer();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading practice tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleBackToDashboard}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">🎵</div>
                <h1 className="text-xl font-bold text-white">
                  Practice Planner
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Notification System */}
              <div className="text-white">
                <NotificationSystem currentUser={currentUser} />
              </div>
              
              <button
                onClick={handleBackToDashboard}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Teacher Mode Header */}
          {isTeacherMode && viewingStudent && (
            <div className="mb-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {viewingStudent.name}'s Practice Tasks
                </h1>
                <p className="text-gray-600">
                  {viewingStudent.instrument} • Managing practice tasks for {viewingStudent.nickname}
                </p>
              </div>
            </div>
          )}
          <div className="mb-6">
          </div>

          {/* Session Summary Modal */}
          {sessionSummary && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header with Close Button */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900">
                        {isExtendPracticeMode ? "Extend Practice Mode" : "Practice Session Complete!"}
                      </h3>
                      {isExtendPracticeMode && (
                        <p className="text-sm text-gray-600 mt-1">
                          Click "Practice Again" on any task to continue from where you left off
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSessionSummary(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Your Goal Card */}
                <div className="text-center mb-8">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 text-white shadow-lg">
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold opacity-90 mb-2">Your Goal</h4>
                      <div className="text-4xl font-bold tracking-tight mb-2">
                        {formatTime(sessionSummary.durationSec || 0)}
                      </div>
                      <div className="text-lg opacity-90">
                        Target: {sessionSummary.plannedTotalMinutes} minutes
                      </div>
                    </div>
                    <div className="flex justify-center items-center">
                      {(() => {
                        const sessionDurationMinutes = (sessionSummary.durationSec || 0) / 60;
                        const goalMinutes = sessionSummary.plannedTotalMinutes || 30;
                        const goalMet = sessionDurationMinutes >= goalMinutes;
                        
                        if (goalMet) {
                          return (
                            <div className="flex items-center space-x-2">
                              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-lg font-semibold">Goal Met!</span>
                            </div>
                          );
                        } else {
                          const remaining = Math.ceil(goalMinutes - sessionDurationMinutes);
                          return (
                            <span className="text-lg font-semibold">
                              {remaining} min to go
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>

                {/* Teacher Goal Card */}
                <div className="text-center mb-8">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-white shadow-lg">
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold opacity-90 mb-2">Teacher Goal</h4>
                      <div className="text-4xl font-bold tracking-tight mb-2">
                        {(() => {
                          const targetUser = isTeacherMode ? viewingStudent : currentUser;
                          return targetUser?.practiceSessionDuration || 30;
                        })()} minutes
                      </div>
                    </div>
                    <div className="flex justify-center items-center">
                      {(() => {
                        const targetUser = isTeacherMode ? viewingStudent : currentUser;
                        const teacherGoalMinutes = targetUser?.practiceSessionDuration || 30;
                        const sessionDurationMinutes = (sessionSummary.durationSec || 0) / 60;
                        const teacherGoalMet = sessionDurationMinutes >= teacherGoalMinutes;
                        
                        if (teacherGoalMet) {
                          return (
                            <div className="flex items-center space-x-2">
                              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-lg font-semibold">Goal Met! +5 bonus points!</span>
                            </div>
                          );
                        } else {
                          const remaining = Math.ceil(teacherGoalMinutes - sessionDurationMinutes);
                          return (
                            <span className="text-lg font-semibold">
                              {remaining} min to go
                            </span>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center space-x-4 mb-8">
                  <button
                    onClick={() => {
                      // Finish the guided practice session and go directly to next session setup
                      setSessionSummary(null);
                      setReturnToTaskIndex(null);
                      setReturnToTaskElapsed(null);
                      setReturnToOverallStartTime(null);
                      setReturnToOverallRemainingTime(null);
                      // Reset session state
                      // Reset all practice state
                      setPracticeMode(false);
                      setCurrentSession(null);
                      setCurrentTaskIndex(0);
                      setCurrentTaskElapsed(0);
                      setCurrentTaskStartTime(null);
                      setSessionActive(false);
                      setOverallIsRunning(false);
                      setOverallStartTime(null);
                      setPlanningComplete(false);
                      setTaskDurations({});
                      setPlannedTotalMinutes(0);
                      setCelebratedTasks(new Set());
                      setCurrentCelebratedTaskId(null);
                      celebratedTasksRef.current.clear();
                      setIsExtendPracticeMode(false);
                      setExtendPracticeTaskTimers({});
                      
                      // Set up minimal state to show Next Session card
                      setPlannedTotalMinutes(30); // Default goal
                      setTaskDurations({}); // Empty but present object
                      setPlanningComplete(true); // Show the planning section
                      
                      // Go directly to next session setup
                      handleSetNextSession();
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Finish Practice and Setup my Next Session</span>
                    </div>
                  </button>
                </div>

                {/* Bonus Points and Next Session Benefits */}
                <div className="text-center mb-8">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-purple-800 mb-4">🎯 Next Session Benefits</h4>
                    
                    {/* Bonus Points Info */}
                    {(() => {
                      const targetUser = isTeacherMode ? viewingStudent : currentUser;
                      const teacherGoalMinutes = targetUser?.practiceSessionDuration || 30;
                      const sessionDurationMinutes = (sessionSummary.durationSec || 0) / 60;
                      const teacherGoalMet = sessionDurationMinutes >= teacherGoalMinutes;
                      
                      if (teacherGoalMet) {
                        return (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center justify-center space-x-2">
                              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                              <span className="text-lg font-medium text-blue-800">
                                +5 bonus points for meeting your teacher's goal! 🎯
                              </span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Next Session Benefits */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-purple-700">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Get practice reminders</span>
                      </div>
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                        </svg>
                        <span>Let buddies nudge you</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Task Breakdown
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(sessionSummary.plannedDurations)
                        .filter(([taskId, plannedMinutes]) => plannedMinutes > 0)
                        .map(([taskId, plannedMinutes]) => {
                        const actualMinutes = sessionSummary.actualDurations[taskId] || 0;
                        const task = getTaskByTaskId(taskId);
                        const isCompleted = actualMinutes >= plannedMinutes;
                        
                        
                        return (
                          <div key={taskId} className={`flex justify-between items-center p-4 rounded-xl border-2 transition-all ${
                            isCompleted 
                              ? 'bg-green-50 border-green-200 shadow-sm' 
                              : 'bg-yellow-50 border-yellow-200 shadow-sm'
                          }`}>
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isCompleted ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                              }`}>
                                {isCompleted ? (
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <span className="font-semibold text-gray-900">{task?.title || 'Unknown Task'}</span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-6 text-sm">
                              <div className="text-center">
                                <div className="text-blue-600 font-semibold">{plannedMinutes}m</div>
                                <div className="text-gray-500 text-xs">Planned</div>
                              </div>
                              <div className="w-px h-8 bg-gray-300"></div>
                              <div className="text-center">
                                <div className="text-green-600 font-semibold">{actualMinutes}m</div>
                                <div className="text-gray-500 text-xs">Actual</div>
                              </div>
                              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                isCompleted 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {isCompleted ? 'Complete' : 'In Progress'}
                              </div>
                              {isExtendPracticeMode && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Simple: just close the extended practice mode
                                    setIsExtendPracticeMode(false);
                                    setSessionSummary(null);
                                  }}
                                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-semibold transition-colors"
                                >
                                  Close Extended Practice
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          )}


          {/* Practice Mode */}
          {practiceMode && (
            <div className="fixed inset-0 bg-gradient-to-br from-purple-900 to-indigo-900 flex items-start justify-center z-50 overflow-y-auto">
              
              <div className="text-center text-white max-w-6xl mx-4 py-8">
                {/* Overall Timer - Top */}
                <div className="mb-6">
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold">Daily Practice Goal</h3>
                      <div className="text-sm opacity-90">
                        {overallRemainingTime === 0 ? 'Time\'s up! 🎉' : 
                         overallIsRunning ? 'Practice in progress...' : 'Ready to start'}
                      </div>
                      <div className="text-3xl font-mono font-bold">
                        {formatTime(overallRemainingTime)}
                      </div>
                    </div>
                    
                    {/* Progress bar with color change */}
                    <div className="w-full bg-white bg-opacity-20 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-2 rounded-full transition-all duration-1000 origin-left"
                        style={{ 
                          width: '100%',
                          transform: `scaleX(${Math.max(0, ((plannedTotalMinutes * 60 - overallRemainingTime) / (plannedTotalMinutes * 60)))})`,
                          backgroundColor: (() => {
                            const progress = ((plannedTotalMinutes * 60 - overallRemainingTime) / (plannedTotalMinutes * 60)) * 100;
                            if (progress < 25) return '#10B981'; // Green
                            if (progress < 50) return '#F59E0B'; // Yellow
                            if (progress < 75) return '#F97316'; // Orange
                            return '#EF4444'; // Red
                          })()
                        }}
                      ></div>
                    </div>
                  </div>
                  
                </div>

                {/* Practice Reminders - Middle - Only show current task tips */}
                {(() => {
                  const currentTask = getGuidedTasks()[currentTaskIndex];
                  const visibleTips = currentTask ? getFilteredTipsForTask(currentTask) : [];
                  return visibleTips.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-bold">Task Goals</h3>
                        <div className="text-sm text-white bg-blue-600 bg-opacity-50 px-3 py-1 rounded-full">
                          {(() => {
                            let completedCount = 0;
                            if (currentTask.practiceReminders) {
                              for (let i = 0; i < currentTask.practiceReminders.length; i++) {
                                const tipKey = `${currentTask.id}-${i}`;
                                if (!hiddenTips.has(tipKey)) {
                                  const reminder = currentTask.practiceReminders[i];
                                  const reminderText = typeof reminder === 'string' ? reminder : reminder.content || '';
                                  if (completedReminders.has(reminderText)) {
                                    completedCount++;
                                  }
                                }
                              }
                            }
                            return `${completedCount} / ${visibleTips.length} completed`;
                          })()}
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-800 mb-3 font-medium">
                          💡 Try to tick off each goal when completed, fixed, or thought about during this task practice:
                        </p>
                      </div>
                      <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {visibleTips.map((reminder, index) => {
                            // Find the original index in the full practiceReminders array
                            const originalIndex = currentTask.practiceReminders?.findIndex((r, i) => {
                              const tipKey = `${currentTask.id}-${i}`;
                              return !hiddenTips.has(tipKey) && r === reminder;
                            }) ?? 0;
                            
                            // Handle both string and structured object formats
                            const reminderText = typeof reminder === 'string' ? reminder : reminder.content || '';
                            return (
                              <div
                                key={originalIndex}
                                onClick={() => toggleReminderCompletion(reminderText)}
                                className={`flex items-center p-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                  completedReminders.has(reminderText)
                                    ? 'bg-green-500 bg-opacity-30 border-2 border-green-400'
                                    : 'bg-white bg-opacity-10 hover:bg-opacity-20 border-2 border-transparent'
                                }`}
                              >
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-2 ${
                                  completedReminders.has(reminderText)
                                    ? 'bg-green-500 border-green-500'
                                    : 'border-white'
                                }`}>
                                  {completedReminders.has(reminderText) && (
                                    <span className="text-white text-xs font-bold">✓</span>
                                  )}
                                </div>
                                <span className={`text-xs ${
                                  completedReminders.has(reminderText)
                                    ? 'line-through opacity-75'
                                    : ''
                                }`}>
                                  {reminderText}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Current Task - Bottom */}
                <div className="mb-6">
                  <div className="flex items-center justify-center mb-3">
                    <div className="flex items-center space-x-4">
                      <h3 className="text-lg font-bold">Current Task</h3>
                      <div className="text-sm opacity-75">
                        Planned: {taskDurations[getGuidedTasks()[currentTaskIndex]?.isCustom ? `custom_${getGuidedTasks()[currentTaskIndex]?.id}` : `lesson_${getAllTasks().indexOf(getGuidedTasks()[currentTaskIndex])}`] || 0} minutes
                      </div>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h2 className="text-2xl font-bold">
                        {getGuidedTasks()[currentTaskIndex]?.title}
                      </h2>
                      
                  {/* Metronome */}
                  <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-2 border border-white border-opacity-20">
                    <div className="text-xs text-white opacity-75 text-center mb-1">Metronome</div>
                    <CompactMetronome 
                      ref={(ref) => {
                        // Set the ref when available
                        if (ref) {
                          metronomeRef.current = ref;
                        }
                      }}
                      onStop={() => {
                        // Handle stop if needed
                      }}
                    />
                  </div>
                    </div>
                    
                    <div className="text-4xl font-bold mb-3 text-center">
                      {formatTime(simpleTaskTimes[getGuidedTasks()[currentTaskIndex] ? (getGuidedTasks()[currentTaskIndex].isCustom ? `custom_${getGuidedTasks()[currentTaskIndex].id}` : `lesson_${getAllTasks().indexOf(getGuidedTasks()[currentTaskIndex])}`) : ''] || 0)}
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setSessionPaused(!sessionPaused)}
                    className="w-32 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium"
                  >
                    {sessionPaused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={() => {
                      // Stop all metronomes when practicing again
                      stopAllMetronomes();
                      
                      // Show tips popup for current task in re-practice mode
                      const currentTask = getGuidedTasks()[currentTaskIndex];
                      if (currentTask) {
                        setIsRepracticeMode(true);
                        setCurrentTaskForTips(currentTask);
                        setShowTipsPopup(true);
                      }
                    }}
                    className="w-32 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium"
                  >
                    Practice Again
                  </button>
                  {currentTaskIndex < getGuidedTasks().length - 1 && (
                  <button
                    onClick={nextTask}
                      className="w-32 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                    >
                      Next Task
                    </button>
                  )}
                  <button
                    onClick={handleFinishPractice}
                    className="w-32 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
                  >
                    Finish Practice
                  </button>
                </div>
                
                <div className="mt-4 text-sm opacity-75">
                  Task {currentTaskIndex + 1} of {getGuidedTasks().length}
                </div>
              </div>
            </div>
          )}

          {/* Free Practice Modal */}
          {showFreePractice && (
            <div className={`fixed inset-0 bg-gradient-to-br ${themes[selectedTheme as keyof typeof themes].background} flex items-start justify-center z-50 overflow-y-auto`}>
              <div className="text-center text-white max-w-6xl mx-4 py-8">
                {/* Theme Selector and Practice Controls */}
                <div className="mb-6 flex justify-center">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium">Change Theme:</span>
                      <select
                        value={selectedTheme}
                        onChange={(e) => handleThemeChange(e.target.value)}
                        className="bg-white bg-opacity-20 backdrop-blur-sm text-white border border-white border-opacity-30 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                      >
                        {Object.entries(themes).map(([key, theme]) => (
                          <option key={key} value={key} className="bg-gray-800 text-white">
                            {theme.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={finishFreePractice}
                        disabled={!currentUser}
                        className="px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm text-white border border-white border-opacity-30 rounded-md font-medium text-sm hover:bg-opacity-30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Finish Practice
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Fixed width container for cards */}
                <div className="w-2xl mx-auto" style={{ width: '672px' }}>
                {/* Timer - Top */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4">Free Practice Session</h3>
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center">
                      {/* Timer Section - Left Side */}
                      <div className="flex items-center space-x-4">
                        {/* Progress Ring */}
                        <div className="relative w-48 h-48">
                          <svg className="w-48 h-48 transform -rotate-90" viewBox="0 0 100 100">
                            {/* Background circle */}
                            <circle
                              cx="50"
                              cy="50"
                              r="45"
                              stroke="rgba(255, 255, 255, 0.2)"
                              strokeWidth="8"
                              fill="none"
                            />
                            {/* Progress circle */}
                            <circle
                              cx="50"
                              cy="50"
                              r="45"
                              stroke="url(#progressGradient)"
                              strokeWidth="8"
                              fill="none"
                              strokeLinecap="round"
                              strokeDasharray={`${2 * Math.PI * 45}`}
                              strokeDashoffset={`${2 * Math.PI * 45 * (1 - Math.min(freePracticeElapsed / ((freePracticeGoal || (isTeacherMode ? viewingStudent?.practiceSessionDuration : currentUser?.practiceSessionDuration) || 30) * 60), 1))}`}
                              className="transition-all duration-1000 ease-out"
                            />
                            {/* Gradient definition */}
                            <defs>
                              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor={themes[selectedTheme as keyof typeof themes].progressColors[0]} />
                                <stop offset="50%" stopColor={themes[selectedTheme as keyof typeof themes].progressColors[1]} />
                                <stop offset="100%" stopColor={themes[selectedTheme as keyof typeof themes].progressColors[2]} />
                              </linearGradient>
                            </defs>
                          </svg>
                          
                          {/* Timer text in center */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-3xl font-mono font-bold animate-pulse">
                              {formatTime(freePracticeElapsed)}
                            </div>
                            <div className="text-sm opacity-75 mt-1">
                              Goal: {freePracticeGoal || (isTeacherMode ? viewingStudent?.practiceSessionDuration : currentUser?.practiceSessionDuration) || 30} min
                            </div>
                          </div>
                        </div>

                        {/* Timer Controls */}
                        <div className="flex flex-col items-center space-y-2">
                          <div className="text-sm opacity-75">
                            {(() => {
                              const goalMinutes = freePracticeGoal || (isTeacherMode ? viewingStudent?.practiceSessionDuration : currentUser?.practiceSessionDuration) || 30;
                              const progressPercent = Math.min((freePracticeElapsed / 60) / goalMinutes * 100, 100);
                              return `${progressPercent.toFixed(0)}% of goal`;
                            })()}
                          </div>
                          
                          <button
                            onClick={freePracticeRunning ? pauseFreePractice : resumeFreePractice}
                            className="px-4 py-1.5 text-sm bg-white bg-opacity-20 backdrop-blur-sm text-white border border-white border-opacity-30 rounded-md font-medium hover:bg-opacity-30 transition-all duration-200"
                            disabled={!Object.values(categoryTimers).some(timer => timer.isActive) && !freePracticeRunning}
                          >
                            {freePracticeRunning ? 'Pause' : 'Resume'}
                          </button>
                        </div>
                      </div>

                      {/* Metronome - Right Side */}
                      <div className="flex-shrink-0">
                        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20 w-80">
                          <div className="text-sm text-white opacity-90 text-center mb-3 font-medium">Metronome</div>
                          
                          {/* Full Metronome Interface - Always Visible */}
                          <div className="space-y-3">
                            {/* Tempo Display and Play/Stop */}
                            <div className="flex items-center justify-between">
                              <div className="text-center">
                                <div className="text-2xl font-mono font-bold text-white">
                                  {metronomeState.tempo}
                                </div>
                                <div className="text-xs text-white opacity-75">BPM</div>
                              </div>
                              <button
                                onClick={() => {
                                  if (metronomeRef.current) {
                                    if (metronomeState.isRunning) {
                                      metronomeRef.current.stopMetronome();
                                      setMetronomeState({isRunning: false, tempo: metronomeState.tempo});
                                    } else {
                                      metronomeRef.current.startMetronome();
                                      setMetronomeState({isRunning: true, tempo: metronomeState.tempo});
                                    }
                                  }
                                }}
                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
                                  metronomeState.isRunning
                                    ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg'
                                    : 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                                }`}
                              >
                                {metronomeState.isRunning ? (
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                            </div>

                            {/* Tempo Controls */}
                            <div className="space-y-2">
                              {/* Tempo Slider */}
                              <div className="px-1">
                                <input
                                  type="range"
                                  min="30"
                                  max="240"
                                  value={metronomeState.tempo}
                                  onChange={(e) => {
                                    const newTempo = parseInt(e.target.value, 10);
                                    setMetronomeState({isRunning: metronomeState.isRunning, tempo: newTempo});
                                    if (metronomeRef.current) {
                                      metronomeRef.current.setTempo(newTempo);
                                      // If metronome is running, restart it with new tempo
                                      if (metronomeState.isRunning) {
                                        metronomeRef.current.stopMetronome();
                                        setTimeout(() => {
                                          if (metronomeRef.current) {
                                            metronomeRef.current.startMetronome();
                                          }
                                        }, 50);
                                      }
                                    }
                                  }}
                                  className="w-full h-2 bg-white bg-opacity-20 rounded-lg appearance-none cursor-pointer"
                                  style={{
                                    background: `linear-gradient(to right, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)`
                                  }}
                                />
                                <div className="flex justify-between text-xs text-white opacity-75 mt-1">
                                  <span>30</span>
                                  <span>240</span>
                                </div>
                              </div>

                              {/* Numeric Input */}
                              <div className="flex items-center justify-center space-x-2">
                                <label className="text-white text-xs opacity-90">Tempo:</label>
                                <input
                                  type="number"
                                  min="30"
                                  max="240"
                                  defaultValue={metronomeState.tempo}
                                  onBlur={(e) => {
                                    const newTempo = Math.max(30, Math.min(240, parseInt(e.target.value, 10) || 120));
                                    setMetronomeState({isRunning: metronomeState.isRunning, tempo: newTempo});
                                    if (metronomeRef.current) {
                                      metronomeRef.current.setTempo(newTempo);
                                      // If metronome is running, restart it with new tempo
                                      if (metronomeState.isRunning) {
                                        metronomeRef.current.stopMetronome();
                                        setTimeout(() => {
                                          if (metronomeRef.current) {
                                            metronomeRef.current.startMetronome();
                                          }
                                        }, 50);
                                      }
                                    }
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  className="w-12 px-1 py-0.5 text-center bg-white bg-opacity-20 text-white rounded border border-white border-opacity-30 focus:outline-none focus:ring-1 focus:ring-white focus:ring-opacity-50 text-xs"
                                />
                                <span className="text-white text-xs opacity-90">BPM</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Hidden CompactMetronome for Web Audio API functionality */}
                        <div className="hidden">
                          <CompactMetronome 
                            ref={(ref) => {
                              // Set the ref when available
                              if (ref) {
                                metronomeRef.current = ref;
                              }
                            }}
                            onStop={() => {
                              // Handle stop if needed
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Practice Category Buttons */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4">Practice Categories</h3>
                  <div className="grid grid-cols-5 gap-4">
                    {[
                      { key: 'scales', label: 'Scales' },
                      { key: 'exercises', label: 'Exercises' },
                      { key: 'studies', label: 'Studies' },
                      { key: 'repertoire', label: 'Repertoire' },
                      { key: 'ensemble', label: 'Ensemble' }
                    ].map(({ key, label }) => (
                      <div key={key} className="text-center">
                        <button
                          onClick={() => toggleCategoryTimer(key as keyof typeof categoryTimers)}
                          className={`w-full py-2 px-4 rounded font-medium transition-all duration-200 ${
                            categoryTimers[key as keyof typeof categoryTimers].isActive
                              ? 'bg-white text-gray-800 shadow-lg'
                              : 'bg-black bg-opacity-40 text-white hover:bg-opacity-60 shadow-md'
                          }`}
                        >
                          {label}
                        </button>
                        <div className="mt-2 text-lg font-mono font-bold text-white">
                          {formatCategoryTime(categoryTimers[key as keyof typeof categoryTimers].elapsed)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quotes and Mindset Messages Carousel */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4">Inspiration & Guidance</h3>
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-6 mb-6">
                    <div className="relative">
                      {carouselItems.length > 0 ? (
                        <div className="text-center">
                          <div className="text-2xl mb-2">💬</div>
                          <blockquote className="text-lg italic leading-relaxed mb-4">
                            "{carouselItems[currentCarouselIndex]?.content}"
                          </blockquote>
                          <div className="text-sm opacity-75">
                            Inspirational Quote
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="text-2xl mb-2">📚</div>
                          <p className="text-lg italic leading-relaxed mb-4">
                            "Loading inspirational quotes..."
                          </p>
                          <div className="text-sm opacity-75">
                            Motivation for your practice
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* End fixed width container */}


                
              </div>
            </div>
          )}

          {/* Task Complete Celebration Modal */}
          {showTaskComplete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-gradient-to-br from-green-400 to-blue-500 rounded-2xl p-8 max-w-md w-full mx-4 text-center text-white">
                <div className="text-6xl mb-4 animate-pulse">🎉</div>
                <h3 className="text-2xl font-bold mb-2">Task Complete!</h3>
                <p className="text-lg mb-4">Great job on:</p>
                <p className="text-xl font-semibold bg-white bg-opacity-20 rounded-lg p-3 mb-6">
                  {completedTaskTitle}
                </p>
                <div className="text-sm opacity-90 mb-6">
                  You've reached your assigned time for this task!
                </div>
                <button
                  onClick={closeTaskCompleteCelebration}
                  className="bg-white text-green-600 hover:bg-green-50 font-bold py-3 px-6 rounded-lg text-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  I'm a Practice Champion! 🏆
                </button>
              </div>
            </div>
          )}




          {sessionActive && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="flex space-x-2">
                    {!sessionPaused ? (
                      <button
                        onClick={pausePractice}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md font-medium"
                      >
                        Pause
                      </button>
                    ) : (
                      <button
                        onClick={resumePractice}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
                      >
                        Resume
                      </button>
                    )}
                    <button
                      onClick={finishPractice}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium"
                    >
                      Finish Practice
                    </button>
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatTime(elapsedTime)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {sessionPaused ? 'Paused' : 'Active'}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Daily Practice Goal Timer - Always Visible */}
        <DailyPracticeGoalCard
          currentUser={isTeacherMode ? (viewingStudent as User) : currentUser}
          badgesLoaded={badgesLoaded}
          earnedBadges={earnedBadges}
          isPracticeSessionActive={false}
          currentSessionProgress={0}
          showResetButton={true}
          onResetTimers={resetTimers}
          viewingStudent={viewingStudent}
          isTeacherMode={isTeacherMode}
          plannedTotalMinutes={plannedTotalMinutes}
          overallRemainingTime={overallRemainingTime}
          formatTime={formatTime}
        />

          {/* Planning Flow Controls */}
          {!planningComplete ? (
            <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="text-center">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
                  {!isTeacherMode && (
                    <>
                  <button
                    onClick={startGuidedPractice}
                    disabled={!canStartPractice()}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm w-full"
                  >
                    <span className="hidden sm:inline">Start Guided Practice</span>
                    <span className="sm:hidden">Guided</span>
                  </button>
                  <button
                    onClick={startFreePractice}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm w-full"
                  >
                    <span className="hidden sm:inline">Start Free Practice</span>
                    <span className="sm:hidden">Free</span>
                  </button>
                    </>
                  )}
                  <div className="relative group w-full">
                    <button
                      onClick={() => setShowAddTask(!showAddTask)}
                      className={`px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm text-white transition-all duration-300 w-full ${
                        getUnifiedTasks().length === 0 
                          ? 'bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/50 animate-pulse ring-2 ring-indigo-300 ring-opacity-75' 
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      <span className="hidden sm:inline">{showAddTask ? 'Cancel' : 'Add Custom Task'}</span>
                      <span className="sm:hidden">{showAddTask ? 'Cancel' : 'Add Task'}</span>
                    </button>
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      Add a Practice Task to session planner below.
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div className="relative group w-full">
                    <button
                      onClick={() => setShowAddReminder(!showAddReminder)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm w-full h-full flex items-center justify-center"
                    >
                      <span className="hidden sm:inline">{showAddReminder ? 'Cancel' : 'Add Task Goal'}</span>
                      <span className="sm:hidden">{showAddReminder ? 'Cancel' : 'Goal'}</span>
                    </button>
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      Add a specific focus goal to a task.
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  {isTeacherMode ? (
                    <button
                      onClick={() => setShowTeacherSessionModal(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm w-full h-full flex items-center justify-center"
                    >
                      <span className="hidden sm:inline">Save for Student</span>
                      <span className="sm:hidden">Save</span>
                    </button>
                  ) : (
                  <button
                    onClick={() => setShowSaveSessionModal(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm w-full h-full flex items-center justify-center"
                  >
                    <span className="hidden sm:inline">Save Session</span>
                    <span className="sm:hidden">Save</span>
                  </button>
                  )}
                  <button
                    onClick={() => setShowLoadSessionModal(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-2 py-2 sm:px-4 sm:py-3 rounded-md font-medium text-xs sm:text-sm w-full h-full flex items-center justify-center"
                  >
                    <span className="hidden sm:inline">Load Session</span>
                    <span className="sm:hidden">Load</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  {!isTeacherMode && (
                  <button
                    onClick={startGuidedPractice}
                    disabled={!canStartPractice()}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-md font-medium"
                    title={canStartPractice() ? 
                      `Start your ${(() => {
                        const targetTotalMinutes = isTeacherMode && viewingStudent?.practiceSessionDuration 
                          ? viewingStudent.practiceSessionDuration 
                          : plannedTotalMinutes;
                        return targetTotalMinutes;
                      })()}-minute practice session!` : 
                      'Complete your practice planning first - assign at least your daily goal time to begin.'}
                  >
                    Start Guided Practice
                  </button>
                  )}
                  <button
                    onClick={resetPlanning}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium"
                    title="Clear all time allocations and start over with your practice planning"
                  >
                    Reset Planning
                  </button>
                </div>
                
                <div 
                  className="text-center cursor-help"
                  title={getTaskTooltip()}
                >
                  <div className="text-2xl font-bold text-gray-900">
                    {getTotalPlannedMinutes()} / {(() => {
                      const targetTotalMinutes = isTeacherMode && viewingStudent?.practiceSessionDuration 
                        ? viewingStudent.practiceSessionDuration 
                        : plannedTotalMinutes;
                      return targetTotalMinutes;
                    })()} minutes
                  </div>
                  <div className="text-sm text-gray-500">
                    {getRemainingMinutes() > 0 ? `${getRemainingMinutes()} minutes left to assign` : 'Daily goal reached!'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add Task and Reminder Forms */}
          {showAddTask && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Custom Task</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder="Enter practice task (max 35 characters)"
                    maxLength={35}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addCustomTask()}
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {newTaskTitle.length}/35 characters
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={newTaskCategory}
                    onChange={(e) => setNewTaskCategory(e.target.value as 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Scales">Scales</option>
                    <option value="Exercises">Exercises</option>
                    <option value="Studies">Studies</option>
                    <option value="Repertoire">Repertoire</option>
                    <option value="Ensemble">Ensemble</option>
                  </select>
                </div>
                <button
                  onClick={addCustomTask}
                  disabled={!newTaskTitle.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md font-medium"
                >
                  Add Task
                </button>
              </div>
            </div>
          )}

          {showAddReminder && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Task Goal</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Task to Add Goal
                </label>
                <select
                  value={selectedTaskForReminder || ''}
                  onChange={(e) => setSelectedTaskForReminder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a task...</option>
                  {customTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.title} ({task.category})
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedTaskForReminder && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newReminderText}
                        onChange={(e) => setNewReminderText(e.target.value)}
                        placeholder="Enter task goal..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <button
                      onClick={() => addPracticeReminder(selectedTaskForReminder)}
                      disabled={!newReminderText.trim()}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md font-medium"
                    >
                      Add Goal
                    </button>
                  </div>
                  
                  {/* Metronome Options */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="checkbox"
                        id="addMetronome"
                        checked={newReminderHasMetronome}
                        onChange={(e) => setNewReminderHasMetronome(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="addMetronome" className="text-sm font-medium text-gray-700">
                        Add Metronome
                      </label>
                    </div>
                    
                    {newReminderHasMetronome && (
                      <div className="flex items-center space-x-3">
                        <label className="text-sm text-gray-600">Tempo:</label>
                        <input
                          type="number"
                          min="30"
                          max="240"
                          value={newReminderTempo}
                          onChange={(e) => setNewReminderTempo(parseInt(e.target.value) || 120)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                        />
                        <span className="text-sm text-gray-600">BPM</span>
                        <div className="text-xs text-gray-500">
                          Metronome will auto-start when this goal is selected
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Practice Session Planning */}
          {plannedTotalMinutes > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-1 gap-4">
                {/* Next Session */}
                <div className="bg-white bg-opacity-60 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-white flex items-center">
                        <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                        Next Session
                    </h4>
                      <button
                    onClick={() => {
                      // Go directly to next session setup
                      handleSetNextSession();
                    }}
                        className="text-white hover:text-green-100 text-sm font-medium flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-1 rounded-md transition-all duration-200"
                      >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      {isEditingTodayDate ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="date"
                            value={editingTodayDate}
                            onChange={(e) => setEditingTodayDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      ) : (
                        <span className="font-medium">{todaySessionDetails?.date ? new Date(todaySessionDetails.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : new Date().toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      {isEditingTodayTime ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="time"
                            value={editingTodayTime}
                            onChange={(e) => setEditingTodayTime(e.target.value)}
                            min={new Date().toTimeString().slice(0, 5)}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      ) : (
                        <span className="font-medium">{todaySessionDetails?.time || 'Not scheduled'}</span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Goal:</span>
                      {isEditingTodayGoal ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={editingTodayGoal}
                            onChange={(e) => setEditingTodayGoal(parseInt(e.target.value) || 30)}
                            min="1"
                            max="180"
                            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-16"
                          />
                          <span className="text-xs text-gray-500">min</span>
                        </div>
                      ) : (
                        <span className="font-medium">{todaySessionDetails?.goal || plannedTotalMinutes} minutes</span>
                      )}
                    </div>
                    
                    {/* Save/Cancel buttons when editing */}
                    {(isEditingTodayTime || isEditingTodayGoal || isEditingTodayDate) && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex justify-center space-x-4">
                          <button
                            onClick={() => {
                              handleSaveTodayTime();
                              if (isEditingTodayGoal) {
                                handleSaveTodayGoal();
                              }
                              if (isEditingTodayDate) {
                                handleSaveTodayDate();
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Save Changes</span>
                          </button>
                          <button
                            onClick={() => {
                              handleCancelEditTodayTime();
                              handleCancelEditTodayGoal();
                              handleCancelEditTodayDate();
                            }}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Cancel</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Empty Next Session Prompt */}
          {(!todaySessionDetails || (!todaySessionDetails.time && !todaySessionDetails.goal)) && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 mb-6">
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-amber-800 mb-2">
                  Set Up Your Next Practice Session
                </h3>
                <p className="text-amber-700 mb-4">
                  Plan when you'll practice next and set your goal to get the most out of your practice time.
                </p>
                <div className="bg-white bg-opacity-60 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-amber-800 mb-2">What you can set:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-amber-700">
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Practice Time:</strong> When you'll start</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Practice Goal:</strong> How long to practice</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-amber-600">
                  💡 <strong>Tip:</strong> Once you set your session, you'll get helpful reminders and your buddies can nudge you if you miss practice!
                </p>
              </div>
            </div>
          )}


          {/* Planning Instructions */}
          {planningComplete && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                    {isTeacherMode ? 'Plan Practice Session for Student' : 'Plan Your Practice Session'}
                  </h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    {isTeacherMode ? (
                      <>
                        <p>• <strong>Assign time</strong> to each task based on the student's daily practice goal ({viewingStudent?.practiceSessionDuration || 30} minutes)</p>
                        <p>• <strong>Reorder tasks</strong> by dragging them up or down using the drag handle (≡)</p>
                        <p>• <strong>Save for Student</strong> when planning is complete to create a reusable practice session</p>
                        <p>• The student can then load and use this session for their practice</p>
                      </>
                    ) : (
                      <>
                        <p>• <strong>Assign time</strong> to each task using the duration inputs (0-60 minutes)</p>
                        <p>• <strong>Reorder tasks</strong> by dragging them up or down using the drag handle (≡)</p>
                        <p>• When all time is allocated, click <strong>"Start Guided Practice"</strong></p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Save Session Modal */}
          {showSaveSessionModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Save Practice Session</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Name
                  </label>
                  <input
                    type="text"
                    value={newSessionName}
                    onChange={(e) => setNewSessionName(e.target.value)}
                    placeholder="e.g., Technical Focus, Repertoire Practice"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newSessionDescription}
                    onChange={(e) => setNewSessionDescription(e.target.value)}
                    placeholder="e.g., Focus on scales and technical exercises"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 h-20"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowSaveSessionModal(false);
                      setNewSessionName('');
                      setNewSessionDescription('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCurrentSession}
                    disabled={!newSessionName.trim()}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-md font-medium"
                  >
                    Save Session
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Teacher Save Session Modal */}
          {showTeacherSessionModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Save Session for {viewingStudent?.name}</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Name
                  </label>
                  <input
                    type="text"
                    value={teacherSessionName}
                    onChange={(e) => setTeacherSessionName(e.target.value)}
                    placeholder="e.g., Technical Focus, Repertoire Practice"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={teacherSessionDescription}
                    onChange={(e) => setTeacherSessionDescription(e.target.value)}
                    placeholder="e.g., Focus on scales and technical exercises"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 h-20"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowTeacherSessionModal(false);
                      setTeacherSessionName('');
                      setTeacherSessionDescription('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveTeacherSession}
                    disabled={!teacherSessionName.trim()}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-md font-medium"
                  >
                    Save for Student
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Session Modal */}
          {showEditSessionModal && editingSession && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Session: {editingSession.name}</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Name
                  </label>
                  <input
                    type="text"
                    value={teacherSessionName}
                    onChange={(e) => setTeacherSessionName(e.target.value)}
                    placeholder="e.g., Technical Focus, Repertoire Practice"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={teacherSessionDescription}
                    onChange={(e) => setTeacherSessionDescription(e.target.value)}
                    placeholder="e.g., Focus on scales and technical exercises"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 h-20"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEditSessionModal(false);
                      setEditingSession(null);
                      setTeacherSessionName('');
                      setTeacherSessionDescription('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (editingSession) {
                        updateSavedSession(editingSession.id, {
                          name: teacherSessionName.trim(),
                          description: teacherSessionDescription.trim(),
                          plannedTotalMinutes: plannedTotalMinutes,
                          taskDurations: { ...taskDurations }
                        });
                      }
                    }}
                    disabled={!teacherSessionName.trim()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md font-medium"
                  >
                    Update Session
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Practice Tips Popup Modal */}
          {showTipsPopup && currentTaskForTips && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {isRepracticeMode ? `Practice Goals for "${currentTaskForTips.title}" - Round 2!` : `Practice Goals for "${currentTaskForTips.title}"`}
                </h3>
                
                {/* Current Tips Display */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {isRepracticeMode 
                      ? "Great job completing all goals! Now select different ones to focus on for another round of practice. You can also ADD more goals yourself!"
                      : "Here are your current goals to think about. Select which ones you would like to focus on or hide for this session. You can also ADD more goals yourself!"
                    }
                  </h4>
                  
                  {currentTaskForTips.practiceReminders && currentTaskForTips.practiceReminders.length > 0 ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="space-y-2">
                        {currentTaskForTips.practiceReminders.map((reminder, index) => {
                          const tipKey = `${currentTaskForTips.id}-${index}`;
                          const tipState = getTipState(currentTaskForTips.id || '', index);
                          const reminderText = typeof reminder === 'string' ? reminder : reminder.content;
                          
                          return (
                            <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
                              tipState === 'selected' ? 'bg-green-100 border border-green-300' :
                              tipState === 'hidden' ? 'bg-red-100 border border-red-300' :
                              'bg-white border border-gray-200'
                            }`}>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => toggleTipSelection(currentTaskForTips.id || '', index)}
                                  className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-colors ${
                                    tipState === 'selected'
                                      ? 'bg-green-500 border-green-500 text-white'
                                      : 'bg-white border-gray-300 hover:border-green-400 hover:bg-green-50'
                                  }`}
                                  title="I'll focus on this one!"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => toggleTipHidden(currentTaskForTips.id || '', index)}
                                  className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-colors ${
                                    tipState === 'hidden'
                                      ? 'bg-red-500 border-red-500 text-white'
                                      : 'bg-white border-gray-300 hover:border-red-400 hover:bg-red-50'
                                  }`}
                                  title="Not today!"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                              <div className="flex items-center space-x-2 flex-1">
                                {typeof reminder === 'object' && reminder.hasMetronome && (
                                  <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.792L5.617 15H3a1 1 0 01-1-1V6a1 1 0 011-1h2.617l2.766-1.792a1 1 0 011-.332zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <span className={`text-sm ${
                                  tipState === 'selected' ? 'text-green-800' :
                                  tipState === 'hidden' ? 'text-red-800' :
                                  'text-blue-800'
                                }`}>
                                  {reminderText}
                                </span>
                                {typeof reminder === 'object' && reminder.hasMetronome && reminder.tempo && (
                                  <span className="text-xs text-blue-600 font-medium">
                                    ({reminder.tempo} BPM)
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600 italic">
                        No goals added yet for this task.
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {!showAddTipsInterface ? (
                  <div className="mb-6">
                    {(!areAllTipsDecided(currentTaskForTips) || (isRepracticeMode && !hasTipSelectionChanged)) && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          {isRepracticeMode && !hasTipSelectionChanged
                            ? "💡 Please make changes to goals before practicing again"
                            : "💡 Please select (✓) or hide (✗) all goals before starting practice"
                          }
                        </p>
                      </div>
                    )}
                    <div className="flex space-x-3">
                      <button
                        onClick={handleTipsPopupYes}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                      >
                        Add Goals
                      </button>
                      <button
                        onClick={startPracticingCurrentTask}
                        disabled={!areAllTipsDecided(currentTaskForTips) || (isRepracticeMode && !hasTipSelectionChanged)}
                        className={`px-4 py-2 rounded-md font-medium ${
                          areAllTipsDecided(currentTaskForTips) && (!isRepracticeMode || hasTipSelectionChanged)
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        }`}
                        title={
                          !areAllTipsDecided(currentTaskForTips) 
                            ? "Please select or hide all goals before starting practice" 
                            : (isRepracticeMode && !hasTipSelectionChanged)
                            ? "Please make changes to goals before practicing again"
                            : ""
                        }
                      >
                        {isRepracticeMode ? "Practice Again" : "Now I'm Ready to Practice"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Add Tips Interface */
                  <div className="mb-6">
                    {(!areAllTipsDecided(currentTaskForTips) || (isRepracticeMode && !hasTipSelectionChanged)) && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          {isRepracticeMode && !hasTipSelectionChanged
                            ? "💡 Please make changes to goals before practicing again"
                            : "💡 Please select (✓) or hide (✗) all goals before starting practice"
                          }
                        </p>
                      </div>
                    )}
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Add more goals for "{currentTaskForTips.title}":
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Goal
                        </label>
                        <textarea
                          value={newTipText}
                          onChange={(e) => setNewTipText(e.target.value)}
                          placeholder="Enter a helpful goal for practicing this task..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                        <div className="flex space-x-3">
                          <button
                            onClick={addTipToCurrentTask}
                            disabled={!newTipText.trim()}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md font-medium"
                          >
                            Add Goal
                          </button>
                          <button
                            onClick={startPracticingCurrentTask}
                            disabled={!areAllTipsDecided(currentTaskForTips) || (isRepracticeMode && !hasTipSelectionChanged)}
                            className={`px-4 py-2 rounded-md font-medium ${
                              areAllTipsDecided(currentTaskForTips) && (!isRepracticeMode || hasTipSelectionChanged)
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            }`}
                            title={
                              !areAllTipsDecided(currentTaskForTips) 
                                ? "Please select or hide all tips before starting practice" 
                                : (isRepracticeMode && !hasTipSelectionChanged)
                                ? "Please make changes to tips before practicing again"
                                : ""
                            }
                          >
                            {isRepracticeMode ? "Practice Again" : "Now I'm Ready to Practice"}
                          </button>
                        </div>
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowTipsPopup(false);
                      setCurrentTaskForTips(null);
                      setShowAddTipsInterface(false);
                      setNewTipText('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tips Completion Dialog */}
          {showTipsCompletionDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Incomplete Goals
                </h3>
                <p className="text-sm text-gray-700 mb-6">
                  {isFinishPracticeDialog 
                    ? "Are you sure you would like to finish your practice session? You still have uncompleted goals to think about!"
                    : "Are you sure you would like to move on to your next task? You still have uncompleted goals to think about!"
                  }
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleTipsCompletionDialogResume}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                  >
                    Resume Task
                  </button>
                  <button
                    onClick={handleTipsCompletionDialogNext}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium"
                  >
                    {isFinishPracticeDialog ? "Finish Practice" : "Next Task"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Re-practice Dialog */}
          {showRepracticeDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Practice Again?
                </h3>
                <p className="text-sm text-gray-700 mb-6">
                  Would you like to practice this task again while focusing on different tips?
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleRepracticeDialogYes}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium"
                  >
                    Yes, Practice Again
                  </button>
                  <button
                    onClick={handleRepracticeDialogNo}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium"
                  >
                    No, Next Task
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Load Session Modal */}
          {showLoadSessionModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Load Saved Session</h3>
                {savedSessions.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-600">No saved sessions yet. Create your first session!</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {savedSessions.map((session) => (
                      <div key={session.id} className={`border rounded-lg p-4 hover:bg-gray-50 ${
                        session.createdBy === 'teacher' 
                          ? 'border-purple-200 bg-purple-50' 
                          : 'border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-gray-900">{session.name}</h4>
                              {session.createdBy === 'teacher' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Teacher Suggested
                                </span>
                              )}
                            </div>
                            {session.description && (
                              <p className="text-sm text-gray-600 mt-1">{session.description}</p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                              <span>{session.plannedTotalMinutes} min total</span>
                              <span>{Object.keys(session.taskDurations).length} tasks</span>
                              <span>
                                Last used: {session.lastUsed 
                                  ? (session.lastUsed instanceof Date 
                                      ? session.lastUsed.toLocaleDateString()
                                      : new Date(session.lastUsed.seconds * 1000).toLocaleDateString())
                                  : (session.createdAt instanceof Date 
                                      ? session.createdAt.toLocaleDateString()
                                      : new Date(session.createdAt.seconds * 1000).toLocaleDateString())
                                }
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => loadSavedSession(session)}
                              className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-md"
                            >
                              Load
                            </button>
                            {session.createdBy === 'teacher' && isTeacherMode && (
                              <button
                                onClick={() => startEditingSession(session)}
                                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                              >
                                Edit
                              </button>
                            )}
                            {session.createdBy !== 'teacher' && (
                            <button
                              onClick={() => deleteSavedSession(session.id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md"
                            >
                              Delete
                            </button>
                            )}
                            {session.createdBy === 'teacher' && (
                              <span className="text-xs text-purple-600 font-medium">
                                Teacher Created
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowLoadSessionModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Practice Task List - Draggable 3-Section Structure - Hidden during guided practice */}
          {!sessionActive && (
            <div className="space-y-6">
            {/* Planning Description Window - Collapsible */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg mb-4 sm:mb-6">
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="w-full p-4 sm:p-6 text-left hover:bg-blue-100 transition-colors duration-200 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        {isTeacherMode ? 'Create Practice Sessions for Students' : 'Plan Your Practice Session'}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {isTeacherMode 
                          ? 'Learn how to create structured practice sessions for your students'
                          : 'Learn how to allocate time, organise tasks, and create custom practice sessions'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    <svg 
                      className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                        isDescriptionExpanded ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>
              
              {isDescriptionExpanded && (
                <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                  {isTeacherMode ? (
                    <>
                      <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                        Create structured practice sessions for your student ({viewingStudent?.name}). Allocate time to each task based on their daily practice goal ({viewingStudent?.practiceSessionDuration || 30} minutes), organise the session flow, and save it for them to use.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 text-xs sm:text-sm">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Session Planning
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Assign time to each task based on student's daily goal</li>
                              <li>• Watch the progress bar show allocation against their target</li>
                              <li>• Ensure total time matches their practice session duration</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                              Session Organisation
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Drag tasks between Technique, Repertoire, and Ensemble sections</li>
                              <li>• Reorder tasks within each section by dragging up/down</li>
                              <li>• Create an optimal practice flow for the student</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Teacher Tasks
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Click "Add Custom Task" to create teacher-specific practice items</li>
                              <li>• Choose from Scales, Exercises, Studies, Repertoire, or Ensemble</li>
                              <li>• Teacher-created tasks are marked with a "T" badge</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-orange-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              Practice Guidance
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Click "Add Task Goal" to add helpful reminders for tasks</li>
                              <li>• Tips appear during student's guided practice</li>
                              <li>• Provide specific focus points and technique reminders</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-purple-100 rounded-md">
                        <p className="text-sm text-purple-800">
                          <strong>Teacher Tip:</strong> Create balanced sessions that start with technique work, progress to repertoire pieces, and include ensemble work. Use "Save for Student" to create reusable practice sessions that students can load and follow independently. Students will see these sessions marked as "Teacher Suggested"!
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                        Allocate time to each task below until your daily goal is reached. You can organise your practice by dragging tasks between sections, create custom tasks, and add helpful goals to guide your practice.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 text-xs sm:text-sm">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Time Allocation
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Enter minutes for each task using the number inputs</li>
                              <li>• Watch the daily goal progress bar update automatically</li>
                              <li>• Allocate time until you reach your daily practice goal</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                              </svg>
                              Drag & Drop
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Drag tasks between Technique, Repertoire, and Ensemble sections</li>
                              <li>• Reorder tasks within each section by dragging up/down</li>
                              <li>• Organise your practice flow to match your preferences</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Custom Tasks
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Click "Add Custom Task" to create your own practice items</li>
                              <li>• Choose from Scales, Exercises, Studies, Repertoire, or Ensemble</li>
                              <li>• Custom tasks appear in your practice list and can be organised</li>
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 flex items-center">
                              <svg className="w-4 h-4 text-orange-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              Task Tips
                            </h4>
                            <ul className="text-gray-600 space-y-1 ml-6">
                              <li>• Click "Add Task Goal" to add helpful reminders for tasks</li>
                              <li>• Tips appear during guided practice to guide your focus</li>
                              <li>• Check off tips as you complete them during practice</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 p-4 bg-blue-100 rounded-md">
                        <p className="text-sm text-blue-800">
                          <strong>Pro Tip:</strong> Start with technique work (scales, exercises), then move to repertoire pieces, and finish with ensemble pieces for a well-rounded practice session. Use the "Save Session" feature to remember your favorite practice configurations!
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Render sections in the current order */}
            {sectionOrder.map((sectionName) => renderSection(sectionName))}

          </div>
          )}

          {/* Task Tally Display */}
          {Object.keys(taskCounts).length > 0 && (
            <div className="mt-6 bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Practice Tally</h3>
              <div className="space-y-2">
                {Object.entries(taskCounts).map(([taskId, count]) => {
                  const task = getTaskByTaskId(taskId);
                  return (
                    <div key={taskId} className="flex justify-between items-center">
                      <span className="text-gray-700">{task?.title || 'Unknown Task'}</span>
                      <span className="text-sm text-gray-500">
                        Practiced {count} time{count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Profile Setup Modal */}
          {showSetupModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto bg-indigo-100 rounded-full">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                  </div>
                  <div className="mt-2 text-center">
                    <h3 className="text-lg font-medium text-gray-900">Set Up Your Practice Goals</h3>
                    <div className="mt-2 px-7 py-3">
                      <p className="text-sm text-gray-500">
                        Let's set up your practice preferences to help you stay on track!
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      How long should each practice session be? (minutes)
                    </label>
                    <input
                      type="number"
                      value={sessionDuration}
                      onChange={(e) => setSessionDuration(parseInt(e.target.value) || 30)}
                      min="1"
                      max="180"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Tip: Use 1 for quick testing, 30+ for normal practice sessions.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      How many times per week do you want to practice?
                    </label>
                    <input
                      type="number"
                      value={sessionsPerWeek}
                      onChange={(e) => setSessionsPerWeek(parseInt(e.target.value) || 5)}
                      min="1"
                      max="7"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={handleSetupSave}
                    disabled={savingSetup}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  >
                    {savingSetup ? 'Saving...' : 'Save & Continue'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Tip Response Popup */}
      {showTipResponsePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg mx-4 text-center">
            <div className="text-4xl mb-4">💡</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Great job practicing!
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              I have practiced focusing on "{currentTipText}"
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleTipResponse('consistent')}
                className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all duration-200"
              >
                ✓ I can now consistently apply this tip
              </button>
              <button
                onClick={() => handleTipResponse('improving')}
                className="w-full px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-all duration-200"
              >
                📈 I am getting better at this tip but still need to practice it some more
              </button>
              <button
                onClick={() => handleTipResponse('no_improvement')}
                className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all duration-200"
              >
                ❌ I didn't get any better at this tip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Practice Goal Celebration Modal */}
      {showGoalCelebration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center animate-pulse">
            <div className="text-6xl mb-4 animate-pulse">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Daily Goal Reached!
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Congratulations! You've completed your daily practice goal of {freePracticeGoal || currentUser?.practiceSessionDuration || 30} minutes.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowGoalCelebration(false)}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all duration-200"
              >
                Continue Practicing
              </button>
              <button
                onClick={() => {
                  setShowGoalCelebration(false);
                  finishFreePractice();
                }}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200"
              >
                Finish Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finish Practice Confirmation Dialog */}
      {showFinishPracticeConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Are you sure?
              </h3>
              <p className="text-gray-600">
                Do you want to finish your practice session?
              </p>
            </div>

            {/* Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={handleReturnToTask}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>No, Return to Task</span>
                </div>
              </button>
              <button
                onClick={handleConfirmFinishSession}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Yes, Finish Session</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Next Practice Session Modal */}
      {showNextSessionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Schedule Next Practice</h2>
              <p className="text-gray-600">Set your next practice session date, time, and daily goal</p>
            </div>

            <div className="space-y-6">
              {/* Date Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Practice Date
                </label>
                <input
                  type="date"
                  value={nextSessionDate || (() => {
                    const today = new Date();
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    return tomorrow.getFullYear() + '-' + 
                      String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(tomorrow.getDate()).padStart(2, '0');
                  })()}
                  onChange={(e) => setNextSessionDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Time Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Practice Time
                </label>
                <input
                  type="time"
                  value={nextSessionTime}
                  onChange={(e) => setNextSessionTime(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Daily Goal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Goal (minutes)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={nextSessionGoal}
                    onChange={(e) => setNextSessionGoal(parseInt(e.target.value) || 30)}
                    min="10"
                    max="180"
                    step="5"
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 text-sm">min</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Recommended: 15-60 minutes for effective practice
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-8">
              <button
                onClick={() => setShowNextSessionModal(false)}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNextSession}
                disabled={!nextSessionDate || !nextSessionTime}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Session Next Session Prompt Modal */}
      {showNextSessionPrompt && (() => {
        // Calculate total daily practice time (including all sessions today)
        const today = new Date().toDateString();
        const todaySessions = allWeeklySessions.filter(session => {
          const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
          return sessionDate.toDateString() === today;
        });
        
        const totalDailyMinutes = todaySessions.reduce((total, session) => {
          return total + (session.durationSec || 0) / 60;
        }, 0);
        
        const todaySessionGoal = todaySessionDetails?.goal || plannedTotalMinutes || currentUser?.practiceSessionDuration || 30;
        const teacherGoal = currentUser?.practiceSessionDuration || 30;
        
        const todayGoalMet = totalDailyMinutes >= todaySessionGoal;
        const teacherGoalMet = totalDailyMinutes >= teacherGoal;
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="mb-4">
                    <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Great Job! 🎉
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {todaySessions.length > 1 ? (
                      <>
                        You just practiced for <span className="font-semibold text-blue-600">{Math.floor((todaySessions[todaySessions.length - 1]?.durationSec || 0) / 60)} minutes</span><br/>
                        Total today: <span className="font-semibold text-green-600">{Math.floor(totalDailyMinutes)} minutes</span>
                      </>
                    ) : (
                      <>
                        You practiced for <span className="font-semibold text-blue-600">{Math.floor(totalDailyMinutes)} minutes</span>
                      </>
                    )}
                  </p>
                </div>

                {/* Session Performance Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3 text-center">Session Summary</h4>
                  
                  {/* Today's Session Goal */}
                  <div className="flex items-center justify-between mb-3 p-2 bg-white rounded-md">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Your Goal:</span>
                      <span className="text-sm text-gray-600">{todaySessionGoal} minutes</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {todayGoalMet ? (
                        <>
                          <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-green-600 font-medium">Met!</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-orange-600">{Math.ceil(todaySessionGoal - totalDailyMinutes)} min to go</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Teacher's Goal */}
                  <div className="flex items-center justify-between mb-3 p-2 bg-white rounded-md">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Teacher Goal:</span>
                      <span className="text-sm text-gray-600">{teacherGoal} minutes</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {teacherGoalMet ? (
                        <>
                          <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-green-600 font-medium">Met!</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-orange-600">{Math.ceil(teacherGoal - totalDailyMinutes)} min to go</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Points Explanation */}
                  {teacherGoalMet && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-3">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        <span className="text-sm font-medium text-blue-800">
                          +5 bonus points for meeting your teacher's goal! 🎯
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Next Session Setup */}
                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-green-800 mb-3 text-center">Set up your next session to:</h4>
                  <div className="space-y-2 text-sm text-green-700">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Get practice reminders</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                      </svg>
                      <span>Let buddies nudge you</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      setShowNextSessionPrompt(false);
                      handleEditTodayTime();
                      handleEditTodayGoal();
                      handleEditTodayDate();
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Set Next Session</span>
                  </button>
                  <button
                    onClick={() => setShowNextSessionPrompt(false)}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Streak Celebration Modal */}
      {showStreakCelebration && streakCelebrationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="relative">
              {/* Background gradient based on streak type */}
              <div className={`absolute inset-0 ${streakCelebrationData.isFirstDay 
                ? 'bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500' 
                : 'bg-gradient-to-br from-orange-500 via-red-500 to-pink-500'
              }`}></div>
              
              {/* Content */}
              <div className="relative z-10 p-8 text-center">
                {/* Icon */}
                <div className="mb-6">
                  <div className="text-6xl mb-2 animate-bounce">
                    {streakCelebrationData.isFirstDay ? '🎉' : '🔥'}
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-4">
                  {streakCelebrationData.isFirstDay 
                    ? 'Streak Created!' 
                    : 'Streak Alive!'
                  }
                </h3>
                
                {/* Message */}
                <div className="text-white text-lg mb-6 leading-relaxed">
                  {streakCelebrationData.message.split('\n').map((line, index) => (
                    <div key={index} className={index > 0 ? 'mt-2' : ''}>
                      {line}
                    </div>
                  ))}
                </div>
                
                {/* Streak counter */}
                <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-6">
                  <div className="text-white text-sm opacity-90 mb-1">Current Streak</div>
                  <div className="text-3xl font-bold text-white">
                    {streakCelebrationData.currentStreak} Day{streakCelebrationData.currentStreak !== 1 ? 's' : ''}
                  </div>
                </div>
                
                {/* Close button */}
                <button
                  onClick={() => setShowStreakCelebration(false)}
                  className="bg-white text-gray-800 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-200 shadow-lg"
                >
                  Awesome! 🎵
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Metronome - Hidden but functional for goal card interactions */}
      {!practiceMode && (
        <div className="fixed bottom-4 right-4 z-40 opacity-0 pointer-events-none">
          <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-3 border border-gray-200 shadow-lg">
            <div className="text-xs text-gray-600 text-center mb-2 font-medium">Metronome</div>
            <CompactMetronome 
              ref={(ref) => {
                // Set the ref when available
                if (ref) {
                  metronomeRef.current = ref;
                }
              }}
              onStop={() => {
                // Handle stop if needed
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default PracticeTasks;
